import { completionComponentSourceSchema, type CompletionComponentSource } from "@/src/features/jobs/completionComponentEffects";
import type { CompletionCloseResponse } from "@/src/features/jobs/completionCloseDelivery";
import { completionDeliveryIdSchema, completionUndoRecoveryGuardSchema, type CompletionUndoRecoveryGuard, type CompletionDeliveryResponse } from "@/src/features/jobs/completionDelivery";
import { applyCompletionUndoAndFinalize } from "@/src/features/jobs/client/completionUndoApplication";
import { parseCompletionUndoAction, type CompletionUndoAction } from "@/src/features/jobs/completionUndoAction";
import { preparedUndoIdentity } from "@/src/features/jobs/completionUndoRecovery";
import { reconcileCompletionConversation, restoreCompletionConversation } from "@/src/features/jobs/completionConversationReconciliation";
import type { UndoRecoveryBinding } from "@/src/features/jobs/client/undoRecoveryRegistry";
import { create } from "zustand";
import { collapsedSingletonCleanup } from "@/src/features/workspace/client/restoreWorkspace";
import { WorkspaceSaveQueue, WorkspaceSaveConflictError, reconcileWorkspaceEffectDraft, type WorkspaceSaveDraft, type WorkspaceSnapshot } from "@/src/features/workspace/client/workspaceSaveQueue";
import { WorkspaceOwnerChangedError } from "@/src/features/workspace/client/workspacePersistenceErrors";
import { MAX_RECORDS_BODY_BYTES } from "@/src/shared/contracts/records";
import { persist, createJSONStorage } from "zustand/middleware";
import { emptyUserInfo, emptyChatSession } from "@/src/shared/defaultData";
import { PassageUserType, ChatSession, MicState, AppState, AiActivityState, Rectangle, QuadTree, FreeSpaceRegion, PlacementStrategy, componentsMapType, PendingChatMessage, WindowDimensions, ComponentEditRecord, WindowEditHistoryState, ActiveCall, JoinedNativeCall, ComponentMapReferenceType, DismissedNotificationEntry, GeometryChangeOptions } from "@/src/shared/contracts/app";
import { ChainArtifact, reviseArtifact } from "@smartmanager/shell-ui/shared/artifacts";
import { chatSlotActions } from "@smartmanager/shell-ui/shared/chatSlot";
import { planComponentPlacement } from "@/src/state/appStore/layoutPlanner";
import {
  computeFreeRegions,
  findNearestRingPosition,
  movementCost,
  occupancyRect,
  rectsOverlap,
  type LayoutPoint,
  type LayoutRect,
  type LayoutSize,
} from "@/src/state/appStore/layoutGeometry";
import { getSession, hasBrowserSession } from "@/src/shared/browser/sessionStorage";
import { apiUrl, isElectron } from '@/src/shared/config/api';
import { getUserLogSessionHeaders } from "@/src/shared/browser/userSessionLog";
import {
  createVerifiedCheckoutSyncMessage,
  isVerifiedCheckoutSyncForSession,
  mergeReverifiedCheckoutSession,
  VERIFIED_CHECKOUT_SYNC_MESSAGE_TYPE,
} from "@/src/features/billing/client/verifiedCheckoutSync";
import {
  createProviderConnectionSyncBridge,
  PROVIDER_CONNECTION_SYNC_MESSAGE_TYPE,
  type AuthoritativeUserDetails,
} from "@/src/features/integrations/client/providerConnectionSync";
import {
  EPHEMERAL_COMPONENT_NAMES,
  mergeDurableComponentsWithLocalEphemeral,
} from "@/src/features/windows/ephemeralComponents";
import { planSingletonWindowAdds, propsWouldChange } from "@/src/features/windows/singletonWindows";
import {
  ATTENTION_PROMPT_OVERLAP_WEIGHT,
  placeInSaturatedWorkspace,
} from "@/src/state/appStore/saturatedPlacement";
import {
  getStackZIndex,
  isAttentionPrompt,
  keepAttentionPromptsOnTop,
  keepAttentionPromptsOnTopByWindow,
} from "@/src/state/appStore/windowStacking";
import {
  purgeLegacyBrowserSecrets,
  sanitizePersistedAppState,
} from "@/src/state/appStore/persistencePolicy";
import {
  isDetachedWorkspaceSurfaceActive,
  isLandingDemoStoreIsolationActive,
  isolatedBrowserStorage,
  setLandingDemoStoreIsolation,
} from "@/src/state/appStore/landingDemoIsolation";
import {
  fetchWorkspacePersistence,
  trackWorkspacePersistence,
} from "@/src/state/appStore/workspacePersistence";
import { foldDocumentEditPatchProps } from "@smartmanager/shell-ui/features/documents/editSource";
import { reconcileJobStatusComponents } from "@/src/features/jobs/jobStatusComponent";
import { planWorkspaceClose, PROTECTED_WORKSPACE_COMPONENT_NAMES } from "@/src/features/workspace/workspaceClosePlan";
import { NOTIFICATION_FEED_PENDING, restoreDoneEntries, takeDoneEntries } from "@/src/features/notifications/feedSync";
import {
  dockComponentPair,
  dockGroupIsAdjacent,
  getDockGroupMembers,
  moveDockGroup,
  resizeDockGroup,
  undockComponentGroup,
  updateDockGroupGeometry,
} from "@smartmanager/shell-ui/features/windows/componentDockGroup";
import {
  clampFootprintIntoWorkspace,
  growsOnItsOwn,
  growthReservations,
  placementFootprint,
  withGrowthReservations,
  type PlacementOccupancy,
} from "@/src/state/appStore/windowFootprint";
import {
  geometryKey,
  growthHolds,
  positionKey,
  keepHeldLiveHeights,
  rebaseHeldHeights,
  type HeldPlacementRebase,
  savedComponents,
  savedComponentsByWindow,
  withHeldHeight,
  withLiveHeightIn,
  withoutHolds,
  type HeldComponentHeights,
  type HeldGrowth,
} from "@/src/state/appStore/heldHeights";
import { chromeClearanceSeeds, fitWindowsIntoWorkspace, maxWindowSize, workspaceChromeZones } from "@/src/state/appStore/workspaceChrome";
import { getElectronAPI } from "@/src/shared/platform";

export {
  isDetachedWorkspaceSurfaceActive,
  isLandingDemoStoreIsolationActive,
  setDetachedWorkspaceSurface,
  setLandingDemoStoreIsolation,
} from "@/src/state/appStore/landingDemoIsolation";
export { waitForWorkspacePersistence } from "@/src/state/appStore/workspacePersistence";

const windowStoreDebug = (...args: unknown[]) => {
  if (process.env.NEXT_PUBLIC_DEBUG_WINDOW_STORE === "true") {
    console.debug(...args);
  }
};

const OCCUPANCY_MARGIN = 3;
const PROTECTED_COMPONENTS = new Set<string>(PROTECTED_WORKSPACE_COMPONENT_NAMES);
const EPHEMERAL_COMPONENTS = EPHEMERAL_COMPONENT_NAMES;

const hasValidComponentSize = (size: any): size is { width: number; height: number } => (
  size &&
  typeof size.width === "number" &&
  typeof size.height === "number" &&
  Number.isFinite(size.width) &&
  Number.isFinite(size.height) &&
  size.width > 0 &&
  size.height > 0
);

// BroadcastChannel for cross-window state synchronization
const BROADCAST_CHANNEL_NAME = 'nv-app-store-sync';
let broadcastChannel: BroadcastChannel | null = null;

function isWorkspacePersistenceSuspended(): boolean {
  return useAppStore.getState().isSigningOut;
}

type AddComponentToGridOptions = {
  targetWindowId?: string;
  allowCrossWindowRelocation?: boolean;
  sourceWindowId?: string;
};

type RemoveComponentOptions = {
  skipPersistence?: boolean;
  skipSync?: boolean;
  trackDeleted?: boolean;
  /** Internal recursion target used to remove duplicate copies across windows. */
  targetWindowId?: string;
  /** Internal repair path only: remove redundant copies while retaining one protected instance. */
  allowProtectedRemoval?: boolean;
};

// Initialize broadcast channel if available
if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
}

// Default window ID for web mode (single window)
const DEFAULT_WINDOW_ID = 'main';

// Helper: Get the current window ID or fallback to default
const getCurrentWindowIdOrDefault = (state: any): string => {
  return state.currentWindowId || DEFAULT_WINDOW_ID;
};

const flattenComponentsByWindow = (
  componentsByWindow: Record<string, componentsMapType[]>
): componentsMapType[] => Object.values(componentsByWindow).flat();

const updateSessionComponentsMap = (state: AppState, componentsMap: componentsMapType[]): Partial<AppState> => ({
  userSession: {
    ...state.userSession,
    preferences: {
      ...state.userSession.preferences,
      componentsMap,
    },
  },
});

function workspaceSyncIdentity(state: AppState) {
  const ownerId = state.userSession.userId;
  if (state.isSigningOut || state.userSession.isAuthorized !== true || !ownerId || ownerId === "demo") return null;
  return { ownerId, companyId: state.userSession.companyId || null };
}

function isWorkspaceSyncComponent(component: any): boolean {
  const validPosition = (position: any) => position && Number.isFinite(position.x) && Number.isFinite(position.y);
  return Boolean(component && typeof component === "object" && !Array.isArray(component)
    && typeof component.id === "string" && component.id.trim()
    && typeof component.name === "string" && component.name.trim()
    && component.props && typeof component.props === "object" && !Array.isArray(component.props)
    && validPosition(component.position) && hasValidComponentSize(component.size)
    && (component.mobilePosition === undefined || validPosition(component.mobilePosition))
    && (component.mobileSize === undefined || hasValidComponentSize(component.mobileSize)));
}

const broadcastComponentsByWindowSync = (
  componentsByWindow: Record<string, componentsMapType[]>
) => {
  if (!broadcastChannel || isLandingDemoStoreIsolationActive()) return;
  if (isDetachedWorkspaceSurfaceActive()) return;
  const identity = workspaceSyncIdentity(useAppStore.getState());
  if (!identity) return;
  const durableComponentsByWindow = Object.fromEntries(
    Object.entries(savedComponentsByWindow(componentsByWindow, useAppStore.getState().heldComponentHeights)).map(([windowId, components]) => [
      windowId,
      components.filter((component) => !EPHEMERAL_COMPONENTS.has(component.name)),
    ]),
  );
  broadcastChannel.postMessage({
    type: 'COMPONENTS_BY_WINDOW_SYNC',
    schemaVersion: 1,
    ...identity,
    componentsByWindow: durableComponentsByWindow,
    timestamp: Date.now(),
  });
};

const checkoutSyncRequests = new Set<string>();

export function broadcastVerifiedCheckoutSession(sessionId: string): boolean {
  if (!broadcastChannel || isLandingDemoStoreIsolationActive()) return false;
  const message = createVerifiedCheckoutSyncMessage(
    useAppStore.getState().userSession,
    sessionId,
  );
  if (!message) return false;
  broadcastChannel.postMessage(message);
  return true;
}

async function reverifyBroadcastCheckoutSession(value: unknown): Promise<void> {
  const initialSession = useAppStore.getState().userSession;
  if (!isVerifiedCheckoutSyncForSession(value, initialSession)) return;
  const requestKey = `${value.userId}:${value.companyId}:${value.sessionId}`;
  if (checkoutSyncRequests.has(requestKey)) return;
  checkoutSyncRequests.add(requestKey);

  try {
    // Keep the state store free of the browser analytics/auth request graph at
    // module initialization; node-based store tests import this module too.
    const { authenticatedJsonRequest } = await import(
      "@/src/client/http/authenticatedJsonRequest"
    );
    const response = await authenticatedJsonRequest<{
      status?: unknown;
      payments?: PassageUserType["payments"];
    }>({
      endpoint: "checkout/session",
      method: "POST",
      body: { sessionId: value.sessionId },
      timeoutMs: 30_000,
      fallbackErrorMessage: "Failed to verify payment in this window.",
    });
    const latestSession = useAppStore.getState().userSession;
    if (!isVerifiedCheckoutSyncForSession(value, latestSession)) return;
    const nextSession = mergeReverifiedCheckoutSession(latestSession, response);
    if (nextSession) useAppStore.setState({ userSession: nextSession });
  } catch (error) {
    console.warn("[Billing] Cross-window checkout verification failed", error);
  } finally {
    checkoutSyncRequests.delete(requestKey);
  }
}

// Cross-window connect hints: a provider connected in one window refreshes the
// others from the server, so a wait registered in another renderer resumes.
const providerConnectionSync = createProviderConnectionSyncBridge({
  postMessage: (message) => {
    if (broadcastChannel && !isLandingDemoStoreIsolationActive()) broadcastChannel.postMessage(message);
  },
  getSession: () => {
    const state = useAppStore.getState();
    return state.isSigningOut ? null : state.userSession;
  },
  setSession: (userSession) => useAppStore.setState({ userSession }),
  fetchUserDetails: async () => {
    // Same lazy import as the checkout reverify: keep the request graph out of
    // store module initialization.
    const { authenticatedJsonRequest } = await import(
      "@/src/client/http/authenticatedJsonRequest"
    );
    const rows = await authenticatedJsonRequest<Array<{ _source?: AuthoritativeUserDetails }>>({
      endpoint: "records",
      method: "GET",
      query: { query: "getUserInfo" },
      timeoutMs: 30_000,
      fallbackErrorMessage: "Failed to refresh connected accounts in this window.",
    });
    return Array.isArray(rows) ? rows[0]?._source : null;
  },
});

// Helper: Get components for a specific window
const getWindowComponents = (state: any, windowId: string): componentsMapType[] => {
  return state.componentsByWindow[windowId] || [];
};

// Helper: Get quadtree for a specific window
const getWindowQuadtree = (state: any, windowId: string): QuadTree | null => {
  return state.quadtreesByWindow[windowId] || null;
};

// Helper: Get dimensions for a specific window
const getWindowDimensions = (state: any, windowId: string): WindowDimensions | null => {
  return state.screenDimensionsByWindow[windowId] || null;
};

/**
 * App chrome that windows must keep clear of (workspaceChrome.ts). The desktop
 * drag rail exists exactly when the app runs in Electron, which is known
 * synchronously, so even the first restore after launch avoids it. The mobile
 * layout stacks windows under its own chrome and reserves nothing here.
 */
const desktopChromeOptions = () => ({ desktopRail: getElectronAPI() !== null });

const getChromeZones = (
  state: Pick<AppState, "isMobileLayout">,
  dimensions: LayoutSize,
): LayoutRect[] => (state.isMobileLayout ? [] : workspaceChromeZones(dimensions, desktopChromeOptions()));

/**
 * `size` capped so the window fits between the desktop drag rail and the
 * screen edges with its margin. Taller windows would always put their title
 * bar under the rail. Without the rail an oversized window still shows its
 * title bar at the top edge, so its size is left alone; the mobile layout
 * sizes windows on its own.
 */
const fitSizeToWorkspace = (
  state: Pick<AppState, "isMobileLayout">,
  size: LayoutSize,
  dimensions: LayoutSize | null,
): LayoutSize => {
  const chrome = desktopChromeOptions();
  if (state.isMobileLayout || !dimensions || !chrome.desktopRail) return size;
  const max = maxWindowSize(dimensions, chrome, OCCUPANCY_MARGIN);
  if (size.width <= max.width && size.height <= max.height) return size;
  return { width: Math.min(size.width, max.width), height: Math.min(size.height, max.height) };
};

/**
 * `components` with their open desktop windows fitted inside the workspace:
 * below the drag rail and within the screen edges (fitWindowsIntoWorkspace).
 * Placement already puts new and restored windows there. This keeps every
 * other way geometry reaches the store there too: a layout saved in another
 * session and loaded from storage, a viewport that shrank, a window that
 * resizes or moves itself. A docked group moves as one block. `onlyIds`
 * limits the fit to the dock groups of those windows. The web and mobile
 * layouts reserve no chrome and keep their own rules. Returns `components`
 * itself when nothing moves.
 */
const fitComponentsToWorkspace = (
  state: Pick<AppState, "isMobileLayout" | "screenDimensionsByWindow">,
  windowId: string,
  components: componentsMapType[],
  onlyIds?: ReadonlySet<string>,
): componentsMapType[] => {
  const chrome = desktopChromeOptions();
  const dimensions = getWindowDimensions(state, windowId);
  if (state.isMobileLayout || !dimensions || !chrome.desktopRail) return components;
  const fitted = new Map<string, LayoutRect>();
  const seen = new Set<string>();
  for (const component of components) {
    if (seen.has(component.id)) continue;
    const group = getDockGroupMembers(components, component.id);
    group.forEach((member) => seen.add(member.id));
    if (onlyIds && !group.some((member) => onlyIds.has(member.id))) continue;
    const open = group.filter((member) => (
      !member.isMinimized && hasValidPosition(member.position) && hasValidComponentSize(member.size)
    ));
    if (open.length === 0) continue;
    const rects = open.map((member) => ({
      id: member.id,
      x: member.position.x,
      y: member.position.y,
      width: member.size.width,
      height: member.size.height,
    }));
    const result = fitWindowsIntoWorkspace(rects, dimensions, chrome, OCCUPANCY_MARGIN);
    if (result === rects) continue;
    for (const rect of result) fitted.set(rect.id, rect);
  }
  if (fitted.size === 0) return components;
  return components.map((component) => {
    const rect = fitted.get(component.id);
    return rect
      ? {
          ...component,
          position: { x: rect.x, y: rect.y },
          size: { width: rect.width, height: rect.height },
        }
      : component;
  });
};

const toChromeRectangle = (zone: LayoutRect) => new Rectangle(zone.x, zone.y, zone.width, zone.height);

/**
 * What a window being placed must avoid: the quadtree of every visible window,
 * the reserved growth of windows that grow on their own (see
 * windowFootprint.ts), and app chrome (workspaceChrome.ts). `components`
 * defaults to the stored window list; pass the in-progress batch when placing
 * during addComponentToGrid.
 */
const getPlacementOccupancy = (
  state: Pick<AppState, "componentsByWindow" | "quadtreesByWindow" | "screenDimensionsByWindow" | "isMobileLayout" | "heldComponentHeights">,
  windowId: string,
  excludeId?: string,
  components: componentsMapType[] = getWindowComponents(state, windowId),
): PlacementOccupancy | null => {
  const quadtree = getWindowQuadtree(state, windowId);
  const dimensions = getWindowDimensions(state, windowId);
  if (!quadtree || !dimensions) return quadtree;
  return withGrowthReservations(quadtree, [
    ...growthReservations(components, dimensions, Boolean(state.isMobileLayout), OCCUPANCY_MARGIN, excludeId, state.heldComponentHeights),
    ...getChromeZones(state, dimensions).map(toChromeRectangle),
  ]);
};

// Helper: Get deleted components for a specific window
const getWindowDeletedComponents = (state: any, windowId: string): componentsMapType[] => {
  return state.deletedComponentsByWindow[windowId] || [];
};

// Helper: Get edit history for a specific window
const getWindowEditHistory = (state: any, windowId: string): WindowEditHistoryState => {
  return state.editHistoryByWindow[windowId] || { history: [], currentIndex: -1 };
};

/**
 * Clamp a position to ensure the component fits within screen bounds.
 * Returns a validated position that guarantees the component won't overflow.
 */
const clampPositionToScreen = (
  x: number,
  y: number,
  componentWidth: number,
  componentHeight: number,
  screenWidth: number,
  screenHeight: number,
  margin: number = OCCUPANCY_MARGIN
): { x: number; y: number } => {
  const clampedX = Math.max(margin, Math.min(screenWidth - componentWidth - margin, x));
  const clampedY = Math.max(margin, Math.min(screenHeight - componentHeight - margin, y));
  return { x: Math.floor(clampedX), y: Math.floor(clampedY) };
};

const hasValidPosition = (position: any): position is { x: number; y: number } => (
  position &&
  typeof position.x === "number" &&
  typeof position.y === "number" &&
  Number.isFinite(position.x) &&
  Number.isFinite(position.y)
);

const isPositionAvailableInQuadtree = (
  position: { x: number; y: number },
  componentWidth: number,
  componentHeight: number,
  screenWidth: number,
  screenHeight: number,
  quadtree: PlacementOccupancy,
  margin: number = OCCUPANCY_MARGIN
): boolean => {
  if (
    position.x < margin ||
    position.y < margin ||
    position.x + componentWidth + margin > screenWidth ||
    position.y + componentHeight + margin > screenHeight
  ) {
    return false;
  }

  return !quadtree.overlapsAny(toOccupancyRect(position.x, position.y, componentWidth, componentHeight));
};

const findFirstAvailablePositionInQuadtree = (
  componentWidth: number,
  componentHeight: number,
  screenWidth: number,
  screenHeight: number,
  quadtree: PlacementOccupancy,
  preferredPosition?: { x: number; y: number },
  step: number = 5,
  margin: number = OCCUPANCY_MARGIN
): { x: number; y: number } | null => {
  const maxX = screenWidth - componentWidth - margin;
  const maxY = screenHeight - componentHeight - margin;
  if (maxX < margin || maxY < margin) return null;

  // Expanding-ring search: candidates ordered by distance from the preferred
  // position, so fallbacks land near where the component was wanted instead
  // of piling up in the top-left corner.
  const preferred = preferredPosition ?? { x: margin, y: margin };
  return findNearestRingPosition(
    preferred,
    { width: componentWidth, height: componentHeight },
    { width: screenWidth, height: screenHeight },
    margin,
    step,
    (candidate) =>
      isPositionAvailableInQuadtree(
        candidate,
        componentWidth,
        componentHeight,
        screenWidth,
        screenHeight,
        quadtree,
        margin
      )
  );
};

type PlacementTarget = Pick<componentsMapType, "name" | "size">;

/**
 * Whether `target` fits at `position` with its whole placement footprint
 * (windowFootprint.ts), clear of `occupancy` and inside the workspace.
 */
const footprintFitsAt = (
  target: PlacementTarget,
  position: { x: number; y: number },
  screen: WindowDimensions,
  occupancy: PlacementOccupancy,
  isMobileLayout: boolean,
): boolean => {
  const footprint = placementFootprint({ ...target, position }, screen, isMobileLayout);
  return isPositionAvailableInQuadtree(
    position,
    footprint.width,
    footprint.height,
    screen.width,
    screen.height,
    occupancy,
  );
};

/**
 * The free spot nearest to `preferredPosition` where `target` fits with its
 * whole footprint. A window whose footprint depends on where it sits (the
 * conversation's transcript opens beside or under the sphere) is checked with
 * the footprint it has at each candidate. The scan bounds come from the
 * window's own size, the smallest footprint it can have, and the position
 * clamped for its footprint there is tried as well, so a window saved near an
 * edge moves only as far as its footprint needs.
 */
const findFirstFootprintPosition = (
  target: PlacementTarget,
  screen: WindowDimensions,
  occupancy: PlacementOccupancy,
  isMobileLayout: boolean,
  preferredPosition: LayoutPoint = { x: OCCUPANCY_MARGIN, y: OCCUPANCY_MARGIN },
): LayoutPoint | null => {
  const clamped = clampFootprintIntoWorkspace(target, preferredPosition, screen, isMobileLayout, OCCUPANCY_MARGIN);
  const chromeSeeds = chromeClearanceSeeds(
    clamped,
    target.size,
    getChromeZones({ isMobileLayout }, screen),
    OCCUPANCY_MARGIN,
  );
  return findNearestRingPosition(
    preferredPosition,
    target.size,
    screen,
    OCCUPANCY_MARGIN,
    5,
    (candidate) => footprintFitsAt(target, candidate, screen, occupancy, isMobileLayout),
    [clamped, ...chromeSeeds],
  );
};

/**
 * Placement order for a batch: restored windows that grow on their own go
 * first, so they keep their saved spot and any window saved inside their
 * footprint moves instead, whatever order the layout was saved in. The rest
 * keep their order.
 */
const growingRestoresFirst = <T,>(components: T[], isMobileLayout: boolean): T[] => {
  const claimsSavedSpot = (component: T) => {
    if (typeof component !== "object" || component === null) return false;
    const { name, position } = component as { name?: unknown; position?: unknown };
    return typeof name === "string" && growsOnItsOwn({ name }, isMobileLayout) && hasValidPosition(position);
  };
  const first = components.filter(claimsSavedSpot);
  return first.length === 0 ? components : [...first, ...components.filter((component) => !claimsSavedSpot(component))];
};

/**
 * Planner callback: the footprint a window has at a position (windowFootprint.ts).
 * The planner asks once per candidate, so it passes only the fields the
 * footprint reads instead of copying the whole component.
 */
const footprintAtFor = (screen: WindowDimensions, isMobileLayout: boolean) => (
  component: componentsMapType,
  position: LayoutPoint,
) => placementFootprint({ name: component.name, size: component.size, position }, screen, isMobileLayout);

/**
 * Whether tidy may use a window as an anchor and rearrange its neighbours
 * around it. Only ordinary visible windows qualify: prompts and other
 * ephemeral windows stay where they are, dependency windows follow their
 * parent, and docked windows hold no workspace area. This is not an
 * occupancy test; every visible window blocks placement (see blocksPlacement).
 */
const isTidyAnchor = (
  comp: Pick<componentsMapType, "name" | "isMinimized" | "parentComponentId">,
): boolean =>
  !EPHEMERAL_COMPONENTS.has(comp.name) && !comp.isMinimized && !comp.parentComponentId;

/**
 * Whether a window occupies workspace area that new or restored windows must
 * avoid. Every visible window does, including transient prompts and
 * dependency windows; only minimized (docked) windows leave their area free.
 */
const blocksPlacement = (comp: Pick<componentsMapType, "isMinimized">): boolean =>
  !comp.isMinimized;

type MinimizeState = Pick<componentsMapType, "isMinimized" | "minimizedScale" | "isSystemTuckedAway">;

/**
 * Placement used to minimize open windows to relieve viewport pressure and
 * marked them `isSystemTuckedAway`. Placement never minimizes now, but those
 * tucks live on in saved workspaces and would come back minimized on every
 * load. A system tuck was never the person's choice, so it reopens; a window
 * the person minimized keeps its minimized state.
 */
const reopenRetiredSystemTuck = <T extends MinimizeState>(component: T): T =>
  component.isMinimized && component.isSystemTuckedAway
    ? { ...component, isMinimized: false, minimizedScale: undefined, isSystemTuckedAway: undefined }
    : component;

/**
 * Replaces the durable windows with a synced copy and keeps this renderer's
 * local ephemeral windows. The synced windows carry z values raised in
 * another renderer, so local prompts are re-stacked above them; otherwise a
 * window focused elsewhere would cover this renderer's DeviceApproval.
 * Windows folded here keep their local slim height, and grow back to the
 * incoming (saved) height (see heldHeights.ts). Every caller writes both maps.
 */
const mergeSyncedComponentsByWindow = (
  incomingByWindow: Record<string, componentsMapType[]>,
  localByWindow: Record<string, componentsMapType[]>,
  held: HeldComponentHeights,
): { componentsByWindow: Record<string, componentsMapType[]>; heldComponentHeights: HeldComponentHeights } => {
  const componentsByWindow = keepHeldLiveHeights(
    keepAttentionPromptsOnTopByWindow(
      mergeDurableComponentsWithLocalEphemeral(incomingByWindow, localByWindow),
    ),
    localByWindow,
    held,
  );
  return {
    componentsByWindow,
    heldComponentHeights: rebaseHeldHeights(incomingByWindow, held, placementRebase(localByWindow, componentsByWindow, held)),
  };
};

/**
 * How a hold starts over when a merge puts its window somewhere new
 * (rebaseHeldHeights): its growth is recorded the way a fold records it,
 * against the merged window at saved heights, with the incoming row unfolded.
 */
const placementRebase = (
  localByWindow: Record<string, componentsMapType[]>,
  mergedByWindow: Record<string, componentsMapType[]>,
  held: HeldComponentHeights,
): HeldPlacementRebase<componentsMapType> => ({
  localByWindow,
  regrow: (windowId, incoming) => ({
    overlapping: overlappingAtFold(
      savedComponents(mergedByWindow[windowId] ?? [], held).map((component) => component.id === incoming.id ? incoming : component),
      incoming.id,
    ),
  }),
});

const getEffectiveComponentZIndex = (comp: Partial<componentsMapType>): number => {
  return getStackZIndex(comp);
};

/**
 * Docked windows form one interaction surface. Promoting only the member that
 * received the click can leave its peer and controls underneath another
 * workspace window, so the complete group shares one front layer. Attention
 * prompts stay above it (see keepAttentionPromptsOnTop).
 */
const bringComponentGroupToFront = (
  components: componentsMapType[],
  componentId: string,
) => {
  const members = getDockGroupMembers(components, componentId);
  if (members.length === 0) return components;
  return keepAttentionPromptsOnTop(raiseDockGroup(components, members));
};

const raiseDockGroup = (
  components: componentsMapType[],
  members: componentsMapType[],
) => {
  const memberIds = new Set(members.map((member) => member.id));
  const highestOutsideZIndex = components.reduce(
    (highest, component) =>
      memberIds.has(component.id)
        ? highest
        : Math.max(highest, getEffectiveComponentZIndex(component)),
    0,
  );
  if (
    members.every(
      (member) =>
        getEffectiveComponentZIndex(member) > highestOutsideZIndex,
    )
  ) {
    return components;
  }
  const highestMemberZIndex = members.reduce(
    (highest, member) =>
      Math.max(highest, getEffectiveComponentZIndex(member)),
    0,
  );
  const frontZIndex = Math.max(
    highestMemberZIndex,
    highestOutsideZIndex + 1,
  );
  return components.map((component) =>
    memberIds.has(component.id)
      ? { ...component, zIndex: frontZIndex }
      : component,
  );
};

const toOccupancyRect = (x: number, y: number, w: number, h: number) => {
  return new Rectangle(x - OCCUPANCY_MARGIN, y - OCCUPANCY_MARGIN, w + OCCUPANCY_MARGIN * 2, h + OCCUPANCY_MARGIN * 2);
};

/** Occupancy of a window at its placement footprint (windowFootprint.ts), not just its current size. */
const toFootprintOccupancyRect = (
  component: Pick<componentsMapType, "name" | "size" | "position">,
  screen: WindowDimensions,
  isMobileLayout: boolean,
) => {
  const footprint = placementFootprint(component, screen, isMobileLayout);
  return toOccupancyRect(component.position.x, component.position.y, footprint.width, footprint.height);
};

/**
 * The position for `target` that puts its dock group (`members`, with any
 * windows that move along with it) at the free spot nearest where it is now,
 * clear of `blocked` and app chrome. The window is in the quadtree itself, so
 * occupancy comes from `blocked` alone. Null when the workspace has no free
 * spot for the group.
 */
const nearestClearGroupPosition = (
  state: Pick<AppState, "isMobileLayout">,
  dimensions: WindowDimensions,
  members: componentsMapType[],
  target: componentsMapType,
  blocked: Rectangle[],
): LayoutPoint | null => {
  const groupLeft = Math.min(...members.map((member) => member.position.x));
  const groupTop = Math.min(...members.map((member) => member.position.y));
  const groupSize = {
    width: Math.max(...members.map((member) => member.position.x + member.size.width)) - groupLeft,
    height: Math.max(...members.map((member) => member.position.y + member.size.height)) - groupTop,
  };
  const chromeRects = getChromeZones(state, dimensions).map(toChromeRectangle);
  const occupancy: PlacementOccupancy = {
    overlapsAny: (rect) =>
      blocked.some((other) => other.intersects(rect)) || chromeRects.some((zone) => zone.intersects(rect)),
  };
  const found = findFirstFootprintPosition(
    { name: members.length === 1 ? target.name : "", size: groupSize },
    dimensions,
    occupancy,
    Boolean(state.isMobileLayout),
    { x: groupLeft, y: groupTop },
  );
  return found ? { x: found.x + target.position.x - groupLeft, y: found.y + target.position.y - groupTop } : null;
};

/**
 * A window that grew back from one line (releaseComponentHeight) may now
 * cover a window opened or dragged into the space the line freed. Placement
 * keeps that space reserved (growthReservations), so this is the window a
 * person put there. The grown window moves to the nearest free spot, the way
 * a restored window does (restoreComponent), and the window the person
 * placed stays put. With no free spot, or on the mobile layout, it keeps its
 * spot. Returns `components` itself when nothing moves.
 *
 * Only the part that grew counts (below the line `folded` was), and only a
 * window that was not already overlapping the unfolded window where both are
 * now (`at` and `growth`): an overlap the person had before the fold is their layout,
 * and the window grows back into it.
 */
const moveGrownWindowClear = (
  state: Pick<AppState, "isMobileLayout" | "screenDimensionsByWindow">,
  windowId: string,
  components: componentsMapType[],
  folded: componentsMapType,
  at: string,
  growth: HeldGrowth,
  held: HeldComponentHeights,
): componentsMapType[] => {
  const dimensions = getWindowDimensions(state, windowId);
  const isMobileLayout = Boolean(state.isMobileLayout);
  const id = folded.id;
  const slimHeight = folded.size.height;
  const target = components.find((component) => component.id === id);
  if (!target || target.isMinimized || !dimensions || isMobileLayout) return components;
  if (target.size.height <= slimHeight) return components;
  const grown = new Rectangle(target.position.x, target.position.y + slimHeight, target.size.width, target.size.height - slimHeight);
  const members = getDockGroupMembers(components, id).filter((member) => !member.isMinimized);
  const memberIds = new Set(members.map((member) => member.id));
  const visible = components.filter((candidate) => !memberIds.has(candidate.id) && blocksPlacement(candidate));
  // Another window that is still folded for a moment counts at the height it grows back to.
  const others = savedComponents(visible, growthHolds(held));
  const rectOf = (component: componentsMapType) =>
    new Rectangle(component.position.x, component.position.y, component.size.width, component.size.height);
  // Moved while folded, the window grows back somewhere new: every overlap there is new.
  const unmoved = positionKey(folded) === at;
  const placedSince = visible.filter((candidate) => !unmoved || growth.overlapping[candidate.id] !== geometryKey(candidate));
  const covers = savedComponents(placedSince, growthHolds(held)).some((other) => grown.intersects(rectOf(other)));
  if (!covers) return components;
  const position = nearestClearGroupPosition(
    state,
    dimensions,
    members,
    target,
    others.map((other) => toFootprintOccupancyRect(other, dimensions, isMobileLayout)),
  );
  if (!position) {
    windowStoreDebug(`No free spot for ${target.name} after it grew back; keeping its spot`);
    return components;
  }
  windowStoreDebug(`Moved ${target.name} clear of the window placed where it grew back`, position);
  return moveDockGroup(components, id, position, isMobileLayout);
};

/**
 * The area each visible conversation keeps for its open transcript and
 * composer: the placement footprint (windowFootprint.ts) of every window that
 * grows on its own, at that window's spot. A window over it hides the
 * composer, or sits under the transcript, so the app never opens, restores or
 * moves a window into it while another spot exists. A person's own drag is
 * the one way in. The mobile layout stacks windows under its own chrome and
 * reserves nothing here.
 */
const conversationAreas = (
  state: Pick<AppState, "isMobileLayout">,
  dimensions: WindowDimensions,
  components: componentsMapType[],
  excludeIds: ReadonlySet<string> = new Set(),
): LayoutRect[] => {
  const isMobileLayout = Boolean(state.isMobileLayout);
  if (isMobileLayout) return [];
  return components
    .filter((component) => (
      !excludeIds.has(component.id) &&
      blocksPlacement(component) &&
      growsOnItsOwn(component, isMobileLayout) &&
      hasValidPosition(component.position)
    ))
    .map((component) => ({
      ...component.position,
      ...placementFootprint(component, dimensions, isMobileLayout),
    }));
};

/** conversationAreas with the placement margin, as reserved zones for the saturated fallback. */
const conversationKeepOuts = (
  state: Pick<AppState, "isMobileLayout">,
  dimensions: WindowDimensions,
  components: componentsMapType[],
  excludeIds?: ReadonlySet<string>,
): LayoutRect[] =>
  conversationAreas(state, dimensions, components, excludeIds).map((area) =>
    occupancyRect({ x: area.x, y: area.y }, { width: area.width, height: area.height }, OCCUPANCY_MARGIN),
  );

/** How much of `areas` a window's own rect covers, in px². */
const coveredConversationArea = (
  component: Pick<componentsMapType, "position" | "size">,
  areas: LayoutRect[],
): number =>
  areas.reduce((sum, area) => {
    const width =
      Math.min(component.position.x + component.size.width, area.x + area.width) -
      Math.max(component.position.x, area.x);
    const height =
      Math.min(component.position.y + component.size.height, area.y + area.height) -
      Math.max(component.position.y, area.y);
    return width > 0 && height > 0 ? sum + width * height : sum;
  }, 0);

/**
 * The dependency windows stacked on `ownerIds` (and on their dependencies)
 * while collapsed: they move with their owner, the way a drag moves them
 * (updateComponentPosition). Spread dependencies keep their own spots.
 */
const collapsedDependencyIdsOf = (components: componentsMapType[], ownerIds: Iterable<string>): Set<string> => {
  const byId = new Map(components.map((component) => [component.id, component]));
  const found = new Set<string>();
  const visit = (ownerId: string, visited: Set<string>) => {
    if (visited.has(ownerId)) return;
    visited.add(ownerId);
    for (const dependencyId of byId.get(ownerId)?.dependencyIds ?? []) {
      found.add(dependencyId);
      visit(dependencyId, visited);
    }
  };
  for (const ownerId of ownerIds) {
    if (byId.get(ownerId)?.isDependenciesSpread !== true) visit(ownerId, new Set());
  }
  return found;
};

/**
 * Moves the dock group of `targetId` out of every conversation's area
 * (conversationAreas): to the nearest free spot, the way a restored window
 * moves. In a full workspace it takes the saturated fallback with the
 * conversation reserved: it may cover other windows, and a window alone opens
 * smaller when that is what keeps the conversation clear. Returns
 * `components` itself when the group is clear already, is the conversation,
 * or no spot keeps the conversation clear.
 */
const moveClearOfConversation = (
  state: Pick<AppState, "isMobileLayout" | "screenDimensionsByWindow" | "heldComponentHeights">,
  windowId: string,
  components: componentsMapType[],
  targetId: string,
): componentsMapType[] => {
  const dimensions = getWindowDimensions(state, windowId);
  const isMobileLayout = Boolean(state.isMobileLayout);
  const target = components.find((component) => component.id === targetId);
  if (!dimensions || isMobileLayout || !target || target.isMinimized) return components;
  const members = getDockGroupMembers(components, target.id).filter((member) => !member.isMinimized);
  if (members.some((member) => growsOnItsOwn(member, isMobileLayout))) return components;
  const memberIds = new Set(members.map((member) => member.id));
  const areas = conversationAreas(state, dimensions, components, memberIds);
  if (!members.some((member) => coveredConversationArea(member, areas) > 0)) return components;

  // Collapsed dependencies move with the group. They sit beside their owner
  // (addComponentToGrid), not only stacked on it, so the spot is found for
  // the box around both: the group alone could land a dependency in the
  // conversation or off the workspace.
  const followers = collapsedDependencyIdsOf(components, memberIds);
  memberIds.forEach((id) => followers.delete(id));
  const movingBox = [
    ...members,
    ...components.filter((component) => (
      followers.has(component.id) && !component.isMinimized && hasValidPosition(component.position)
    )),
  ];
  // Another window that is folded for a moment counts at the height it grows back to.
  const others = savedComponents(
    components.filter((candidate) => !memberIds.has(candidate.id) && !followers.has(candidate.id) && blocksPlacement(candidate)),
    growthHolds(state.heldComponentHeights ?? {}),
  );
  const clear = nearestClearGroupPosition(
    state,
    dimensions,
    movingBox,
    target,
    others.map((other) => toFootprintOccupancyRect(other, dimensions, isMobileLayout)),
  );
  const withFollowers = (moved: componentsMapType[]): componentsMapType[] => {
    const movedTarget = moved.find((component) => component.id === target.id);
    if (!movedTarget || followers.size === 0) return moved;
    const deltaX = movedTarget.position.x - target.position.x;
    const deltaY = movedTarget.position.y - target.position.y;
    if (deltaX === 0 && deltaY === 0) return moved;
    const shift = (point: LayoutPoint) => ({ x: Math.floor(point.x + deltaX), y: Math.floor(point.y + deltaY) });
    return moved.map((component) => followers.has(component.id)
      ? {
          ...component,
          position: shift(component.position),
          originalDependencyPosition: component.originalDependencyPosition
            ? shift(component.originalDependencyPosition)
            : undefined,
        }
      : component);
  };
  if (clear) {
    windowStoreDebug(`Moved ${target.name} out of the conversation area`, clear);
    return withFollowers(moveDockGroup(components, target.id, clear, isMobileLayout));
  }

  const groupLeft = Math.min(...movingBox.map((member) => member.position.x));
  const groupTop = Math.min(...movingBox.map((member) => member.position.y));
  const groupSize = {
    width: Math.max(...movingBox.map((member) => member.position.x + member.size.width)) - groupLeft,
    height: Math.max(...movingBox.map((member) => member.position.y + member.size.height)) - groupTop,
  };
  const keepOuts = conversationKeepOuts(state, dimensions, components, memberIds);
  const saturated = placeInSaturatedWorkspace({
    requestedSize: groupSize,
    screen: dimensions,
    obstacles: others.map((other) => ({
      ...other.position,
      ...placementFootprint(other, dimensions, isMobileLayout),
      overlapWeight: isAttentionPrompt(other) ? ATTENTION_PROMPT_OVERLAP_WEIGHT : 1,
    })),
    reserved: [...getChromeZones(state, dimensions), ...keepOuts],
    preferred: { x: groupLeft, y: groupTop },
    margin: OCCUPANCY_MARGIN,
    allowShrink: false,
    // Only a window moving alone opens smaller: a box with followers keeps its size.
    fitAroundReserved: movingBox.length === 1,
  });
  const placed = occupancyRect(saturated.position, saturated.size, OCCUPANCY_MARGIN);
  if (keepOuts.some((zone) => rectsOverlap(placed, zone))) {
    windowStoreDebug(`No spot keeps the conversation clear of ${target.name}; keeping its spot`);
    return components;
  }
  const moved = moveDockGroup(
    components,
    target.id,
    { x: saturated.position.x + target.position.x - groupLeft, y: saturated.position.y + target.position.y - groupTop },
    isMobileLayout,
  );
  windowStoreDebug(`Moved ${target.name} out of the conversation area in a full workspace`, saturated);
  const resized = saturated.size.width === groupSize.width && saturated.size.height === groupSize.height
    ? moved
    : moved.map((component) => (component.id === target.id ? { ...component, size: { ...saturated.size } } : component));
  return withFollowers(resized);
};

/**
 * After the app changes window geometry (`before` -> `after`), moves every
 * window that now covers more of a conversation's area than it did out of it
 * (moveClearOfConversation). The app's changes include repairs after the
 * viewport shrinks, a window fitting its content and planned layouts, and a
 * conversation whose area turns from beside the sphere to under it. An overlap
 * the person made by dragging stays as long as the app does not deepen it.
 * `beforeDimensions` is the workspace `before` was laid out in. Returns
 * `after` itself when nothing moves.
 */
const keepConversationsClear = (
  state: Pick<AppState, "isMobileLayout" | "screenDimensionsByWindow" | "heldComponentHeights">,
  windowId: string,
  before: componentsMapType[],
  beforeDimensions: WindowDimensions | null,
  after: componentsMapType[],
): componentsMapType[] => {
  const dimensions = getWindowDimensions(state, windowId);
  const isMobileLayout = Boolean(state.isMobileLayout);
  if (!dimensions || isMobileLayout) return after;
  const areasAfter = conversationAreas(state, dimensions, after);
  if (areasAfter.length === 0) return after;
  const areasBefore = beforeDimensions ? conversationAreas(state, beforeDimensions, before) : [];
  const beforeById = new Map(before.map((component) => [component.id, component]));
  const pushedIn = after.filter((component) => {
    if (component.isMinimized || growsOnItsOwn(component, isMobileLayout)) return false;
    if (!hasValidPosition(component.position)) return false;
    const covered = coveredConversationArea(component, areasAfter);
    if (covered === 0) return false;
    const previous = beforeById.get(component.id);
    const coveredBefore = previous && !previous.isMinimized && hasValidPosition(previous.position)
      ? coveredConversationArea(previous, areasBefore)
      : 0;
    return covered > coveredBefore;
  });
  let result = after;
  // A collapsed dependency moves with its owner (moveClearOfConversation), so
  // one pushed in alongside its owner is left to the owner: moved alone first,
  // the owner's move would shift it again, off its stack.
  const handled = collapsedDependencyIdsOf(after, pushedIn.map((component) => component.id));
  for (const component of pushedIn) {
    if (handled.has(component.id)) continue;
    const groupIds = getDockGroupMembers(result, component.id).map((member) => member.id);
    groupIds.forEach((id) => handled.add(id));
    collapsedDependencyIdsOf(result, groupIds).forEach((id) => handled.add(id));
    result = moveClearOfConversation(state, windowId, result, component.id);
  }
  return result;
};

/** Ids whose position or size differs between two lists of the same windows. */
const geometryChangedIds = (before: componentsMapType[], after: componentsMapType[]): Set<string> => {
  if (before === after) return new Set();
  const beforeById = new Map(before.map((component) => [component.id, component]));
  return new Set(
    after
      .filter((component) => {
        const previous = beforeById.get(component.id);
        return !previous || geometryKey(previous) !== geometryKey(component);
      })
      .map((component) => component.id),
  );
};

/**
 * keepConversationsClear for a change made in one workspace, whose size did
 * not change with it. Adds the ids of the windows it moves to `changedIds`,
 * so the caller reindexes them.
 */
const keepConversationsClearOf = (
  state: Pick<AppState, "isMobileLayout" | "screenDimensionsByWindow" | "heldComponentHeights">,
  windowId: string,
  before: componentsMapType[],
  after: componentsMapType[],
  changedIds: Set<string>,
): componentsMapType[] => {
  const cleared = keepConversationsClear(state, windowId, before, getWindowDimensions(state, windowId), after);
  geometryChangedIds(after, cleared).forEach((id) => changedIds.add(id));
  return cleared;
};

/**
 * The windows that overlap `id`'s window right now, with their rectangles:
 * what a fold records as the layout the person had (HeldGrowth.overlapping).
 */
const overlappingAtFold = (components: componentsMapType[], id: string): Record<string, string> => {
  const target = components.find((component) => component.id === id);
  if (!target) return {};
  const memberIds = new Set(getDockGroupMembers(components, id).map((member) => member.id));
  const rectOf = (component: componentsMapType) =>
    new Rectangle(component.position.x, component.position.y, component.size.width, component.size.height);
  const unfolded = rectOf(target);
  return Object.fromEntries(components
    .filter((candidate) => !memberIds.has(candidate.id) && blocksPlacement(candidate) && unfolded.intersects(rectOf(candidate)))
    .map((candidate) => [candidate.id, geometryKey(candidate)]));
};

const getLayoutGeometry = (
  component: componentsMapType,
  isMobileLayout: boolean,
) => ({
  position:
    isMobileLayout && component.mobilePosition
      ? component.mobilePosition
      : component.position,
  size:
    isMobileLayout && component.mobileSize
      ? component.mobileSize
      : component.size,
});

const reindexComponentChanges = (
  quadtree: QuadTree | null,
  before: componentsMapType[],
  after: componentsMapType[],
  changedIds: Set<string>,
  isMobileLayout: boolean,
) => {
  if (!quadtree || changedIds.size === 0) return;
  for (const component of before) {
    if (!changedIds.has(component.id) || component.isMinimized) continue;
    const geometry = getLayoutGeometry(component, isMobileLayout);
    quadtree.remove(
      toOccupancyRect(
        geometry.position.x,
        geometry.position.y,
        geometry.size.width,
        geometry.size.height,
      ),
    );
  }
  for (const component of after) {
    if (!changedIds.has(component.id) || component.isMinimized) continue;
    const geometry = getLayoutGeometry(component, isMobileLayout);
    quadtree.insert(
      toOccupancyRect(
        geometry.position.x,
        geometry.position.y,
        geometry.size.width,
        geometry.size.height,
      ),
    );
  }
};

async function updateUser(updates: Partial<PassageUserType>) {
  // Only update non-preferences user data
  const { preferences, ...userUpdates } = updates;
  if (Object.keys(userUpdates).length === 0) return;

  if (isWorkspacePersistenceSuspended() || !hasBrowserSession()) return;
  const sessionId = getSession();
  windowStoreDebug("updateUser - fields:", Object.keys(userUpdates))
  try {
    await trackWorkspacePersistence(fetchWorkspacePersistence(apiUrl('/api/records'), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(sessionId ? { "x-session-id": sessionId } : {}),
        ...getUserLogSessionHeaders(),
      },
      credentials: isElectron ? 'include' : 'same-origin',
      body: JSON.stringify({
        index: "search-user-details",
        payload: userUpdates,
      }),
    }));
  } catch (error) {
    windowStoreDebug("Fail to Update user data, Error:", error);
  }
}

function toComponentReference(component: any): ComponentMapReferenceType {
  return {
    id: String(component?.id || ""),
    name: String(component?.name || ""),
    reason: typeof component?.reason === "string" ? component.reason : "",
    description: typeof component?.description === "string" ? component.description : "",
    score: typeof component?.score === "number" ? component.score : undefined,
    createdDate: component?.createdDate ?? Date.now(),
    title: typeof component?.props?.title === "string" ? component.props.title : undefined,
    customizesPrimitive:
      typeof component?.props?.customizesPrimitive === "string"
        ? component.props.customizesPrimitive
        : null,
    version: Number.isInteger(component?.props?.version) ? component.props.version : undefined,
    status: typeof component?.props?.status === "string" ? component.props.status : undefined,
  };
}

function normalizeComponentList(componentsMap: any): any[] {
  if (!Array.isArray(componentsMap)) return [];
  return componentsMap.filter((component: any) =>
    component && component.id && !EPHEMERAL_COMPONENTS.has(component.name)
  );
}

function captureWorkspacePersistenceIdentity() {
  const identity = workspaceSyncIdentity(useAppStore.getState());
  if (!identity || isWorkspacePersistenceSuspended() || !hasBrowserSession() || getSession() === "demo") return null;
  return {
    expectedOwnerId: identity.ownerId,
    expectedCompanyId: identity.companyId,
    isCurrent: () => {
      const current = workspaceSyncIdentity(useAppStore.getState());
      return current?.ownerId === identity.ownerId && current.companyId === identity.companyId
        && !isWorkspacePersistenceSuspended() && hasBrowserSession() && getSession() !== "demo";
    },
  };
}

let workspaceSaveQueue: WorkspaceSaveQueue | null = null;
let workspaceQueueGeneration = 0;
export function captureWorkspaceUndoRecoveryBinding(windowId: string, targetSceneId?: string | null): UndoRecoveryBinding | null {
  const identity = captureWorkspacePersistenceIdentity(), queue = workspaceSaveQueue;
  const state = useAppStore.getState(), selection = state.workspaceScenesByWindow[windowId], sessionId = getSession();
  if (!identity || !identity.expectedCompanyId || !queue || !state.componentsByWindow[windowId]
    || selection && selection.ownerId !== identity.expectedOwnerId) return null;
  return { queueGeneration: workspaceQueueGeneration, selection,
    scope: { ownerId: identity.expectedOwnerId, companyId: identity.expectedCompanyId, sessionId, windowId,
      sceneId: targetSceneId === undefined ? selection?.sceneId ?? null : targetSceneId },
    isCurrent: () => identity.isCurrent() && workspaceSaveQueue === queue && getSession() === sessionId
      && Boolean(useAppStore.getState().componentsByWindow[windowId]) && useAppStore.getState().workspaceScenesByWindow[windowId] === selection,
  };
}
let workspaceSaveSequence = 0;
const pendingCompletionApplications = new Map<string, { promise: Promise<CompletionDeliveryResponse> | null; result?: CompletionDeliveryResponse;
  recovery?: CompletionUndoRecoveryGuard; reconciled: boolean; actionIdentity?: string;
  execute?: Parameters<WorkspaceSaveQueue["applyEffect"]>[2]; recoveryCurrent?: () => boolean }>();
const pendingCompletionCloses = new Map<string, { promise: Promise<CompletionCloseResponse> | null; result?: CompletionCloseResponse;
  reconciled: boolean; execute: Parameters<WorkspaceSaveQueue["applyWorkspaceEffect"]>[1] }>();
type UndoEffectContext = { action: CompletionUndoAction; alreadyUndone?: boolean };
const completionEffectKey = (mode: string, owner: string, company: string, source: CompletionComponentSource,
  scene: string | null, component: string, window: string, request?: { reactId: string; previewId: string }) =>
  JSON.stringify([mode, owner, company, source, scene, component, request ? null : window, request?.reactId, request?.previewId],
    (_key, value) => value && typeof value === "object" && !Array.isArray(value)
      ? Object.fromEntries(Object.keys(value).sort().map(key => [key, value[key]])) : value);
function invalidateWorkspaceSaveQueue() {
  workspaceQueueGeneration += 1;
  workspaceSaveQueue?.invalidate();
  workspaceSaveQueue = null;
  pendingCompletionApplications.clear();
  pendingCompletionCloses.clear();
  workspaceSaveSequence += 1;
}

export async function retryWorkspaceSave(): Promise<void> {
  const state = useAppStore.getState();
  await updatePreferences({ ...state.userSession.preferences, componentsMap: state.getAllComponents() });
}

/** Review the owning window's conflict without releasing its original save queue. */
export async function reviewWorkspaceChanges(windowId: string) {
  const identity = captureWorkspacePersistenceIdentity(), queue = workspaceSaveQueue, sessionId = getSession();
  if (!identity?.expectedCompanyId || !queue) throw new WorkspaceOwnerChangedError();
  const owner = { expectedOwnerId: identity.expectedOwnerId, expectedCompanyId: identity.expectedCompanyId };
  const ownerCurrent = () => identity.isCurrent() && getSession() === sessionId;
  const current = () => ownerCurrent()
    && getCurrentWindowIdOrDefault(useAppStore.getState()) === windowId
    && Boolean(useAppStore.getState().componentsByWindow[windowId]);
  if (!current()) throw new WorkspaceOwnerChangedError();
  const same = (value: unknown) => JSON.stringify(value, (_key, item) => item && typeof item === "object" && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
  const capture = () => {
    const state = useAppStore.getState(), draft = captureCompletionWorkspaceDraft(state);
    // Older rows may omit createdDate. Do not invent a new timestamp on each
    // read of the same preview and make it immediately stale.
    draft.layout!.componentsMap = draft.layout!.componentsMap.map(ref => {
      if (draft.components.find(row => row.id === ref.id)?.createdDate != null) return ref;
      const { createdDate: omitted, ...stable } = ref; void omitted; return stable;
    });
    const visible = new Set(draft.components.map(row => row.id));
    const closed = new Map<string, componentsMapType>();
    const required = new Set(queue.recoveryComponentIds().filter(id => !visible.has(id)));
    for (const history of Object.values(state.deletedComponentsByWindow)) {
      const inWindow = new Map(history.map(row => [row.id, row]));
      for (const [id, row] of Array.from(inWindow)) if (required.has(id)) {
        if (closed.has(id) && same(closed.get(id)) !== same(row)) throw new Error("Reopen the conflicting closed view before reviewing this workspace");
        closed.set(id, row);
      }
    }
    if (Array.from(required).some(id => !closed.has(id))) throw new Error("An unsaved view is not available in this window's history");
    return { draft: { ...draft, components: [...Array.from(closed.values()), ...draft.components] },
      sceneId: state.workspaceScenesByWindow[windowId]?.sceneId ?? null,
      context: { windowId, activeWindowId: state.currentWindowId, windows: Object.entries(state.componentsByWindow).map(([id, rows]) => [id,
        rows.filter(row => !EPHEMERAL_COMPONENTS.has(row.name)).map(row => row.id)]), scenes: state.workspaceScenesByWindow },
    };
  };
  const place = (draft: WorkspaceSaveDraft, saved: import("@/src/features/workspace/workspaceRecovery").WorkspaceRecoverySnapshot,
    choice: import("@/src/features/workspace/client/workspaceRecoverySession").WorkspaceRecoveryChoice) => {
    if (!current() || !draft.layout) throw new WorkspaceOwnerChangedError();
    const state = useAppStore.getState(), layout = draft.layout;
    if (layout.lastLayoutMode !== undefined && layout.lastLayoutMode !== "desktop" && layout.lastLayoutMode !== "mobile") throw new Error("This saved layout cannot be displayed here yet");
    const ids = new Set(layout.componentsMap.map(ref => ref.id));
    const rows = new Map(draft.components.filter(row => ids.has(row.id)).map(row => [row.id, row]));
    if (rows.size !== ids.size || Array.from(rows.values()).some(row => !isWorkspaceSyncComponent(row))) throw new Error("Some views cannot be displayed safely in this workspace");
    const local = flattenComponentsByWindow(state.componentsByWindow);
    if (local.some(row => EPHEMERAL_COMPONENTS.has(row.name) && rows.has(row.id))) throw new Error("A saved view conflicts with a live call or task");
    const rawSavedIds = saved.layout.payload.componentsMap;
    const savedIds = new Set((typeof rawSavedIds === "string" ? JSON.parse(rawSavedIds) : rawSavedIds).map((ref: { id: string }) => ref.id));
    const protectedRows = [...local.filter(row => PROTECTED_COMPONENTS.has(row.name)),
      ...saved.components.flatMap(row => row.payload && savedIds.has(row.componentId) && PROTECTED_COMPONENTS.has(String(row.payload.name)) ? [row.payload] : [])];
    if (protectedRows.some(row => !ids.has(row.id))) throw new Error("Keep the protected workspace controls open before applying this choice");
    for (const row of Array.from(rows.values())) {
      const dependencies = row.dependencyIds;
      if (dependencies !== undefined && (!Array.isArray(dependencies) || dependencies.some(id => typeof id !== "string" || !ids.has(id)))) throw new Error("This choice would leave a view without its related content");
      const dock = row.dockGroup as { peerId?: unknown } | undefined;
      if (dock?.peerId !== undefined && (typeof dock.peerId !== "string" || !ids.has(dock.peerId))) throw new Error("This choice would separate linked views");
    }
    const selectedScene = choice === "saved" ? saved.catalog.activeSceneId ?? null : state.workspaceScenesByWindow[windowId]?.sceneId ?? null;
    const changingScene = selectedScene !== (state.workspaceScenesByWindow[windowId]?.sceneId ?? null);
    if (changingScene && Object.entries(state.componentsByWindow).some(([id, values]) => id !== windowId && values.some(row => !EPHEMERAL_COMPONENTS.has(row.name)))) {
      throw new Error("Recover the other open windows before switching to a different saved scene");
    }
    const durable: Record<string, componentsMapType[]> = Object.fromEntries(Object.entries(state.componentsByWindow).map(([id, values]) => [id,
      values.flatMap(row => !EPHEMERAL_COMPONENTS.has(row.name) && rows.has(row.id) ? [rows.get(row.id)! as unknown as componentsMapType] : [])]));
    const placed = new Set(flattenComponentsByWindow(durable).map(row => row.id));
    durable[windowId].push(...Array.from(rows.values()).filter(row => !placed.has(row.id)) as unknown as componentsMapType[]);
    const order = new Map(layout.componentsMap.map((ref, index) => [ref.id, index]));
    for (const values of Object.values(durable)) values.sort((a, b) => order.get(a.id)! - order.get(b.id)!);
    if (same(flattenComponentsByWindow(durable).map(row => row.id)) !== same(layout.componentsMap.map(ref => ref.id))) {
      throw new Error("This saved order requires moving views between windows. Review their placement first.");
    }
    return { ...mergeSyncedComponentsByWindow(durable, state.componentsByWindow, state.heldComponentHeights), selectedScene, changingScene };
  };
  const { prepareWorkspaceRecovery } = await import("@/src/features/workspace/client/workspaceRecoverySession");
  if (!current() || workspaceSaveQueue !== queue) throw new WorkspaceOwnerChangedError();
  return prepareWorkspaceRecovery({ identity: owner, queue, capture, isOwnerCurrent: ownerCurrent,
    isQueueCurrent: candidate => workspaceSaveQueue === candidate,
    request: async input => {
      const { requestWorkspaceRecovery } = await import("@/src/features/workspace/client/workspaceRecovery");
      return requestWorkspaceRecovery(input, { isCurrent: () => current() && workspaceSaveQueue === queue });
    },
    transport: async input => {
      const { requestWorkspacePersistence } = await import("@/src/features/workspace/client/workspacePersistence");
      return requestWorkspacePersistence(input, { isCurrent: ownerCurrent });
    },
    validateSelection: (draft, saved, choice) => { place(draft, saved, choice); },
    adopt: (draft, saved, replacement, choice) => {
      if (!current() || workspaceSaveQueue !== queue) return false;
      const placement = place(draft, saved, choice), state = useAppStore.getState();
      const active = new Set(draft.layout!.componentsMap.map(ref => ref.id));
      const closedRows = new Map(draft.components.filter(row => !active.has(row.id)).map(row => [row.id, row]));
      const deletedComponentsByWindow = Object.fromEntries(Object.entries(state.deletedComponentsByWindow).map(([id, history]) => [id,
        history.filter(row => !active.has(row.id)).map(row => (closedRows.get(row.id) ?? row) as componentsMapType)]));
      for (const [id, values] of Object.entries(state.componentsByWindow)) {
        const newlyClosed = values.filter(row => !EPHEMERAL_COMPONENTS.has(row.name) && !active.has(row.id));
        const prior = deletedComponentsByWindow[id] ?? [];
        deletedComponentsByWindow[id] = Array.from(new Map([...prior, ...newlyClosed].map(row => [row.id, row])).values());
      }
      const allComponents = flattenComponentsByWindow(placement.componentsByWindow);
      const emptyViewer = { files: [], currentDocumentIndex: 0 };
      workspaceSaveQueue = replacement;
      workspaceQueueGeneration++;
      workspaceSaveSequence++;
      pendingCompletionApplications.clear(); pendingCompletionCloses.clear();
      useAppStore.setState({ componentsByWindow: placement.componentsByWindow, heldComponentHeights: placement.heldComponentHeights,
        deletedComponentsByWindow, workspaceSaveIssue: null,
        workspaceScenesByWindow: { ...state.workspaceScenesByWindow, [windowId]: { ownerId: owner.expectedOwnerId, sceneId: placement.selectedScene } },
        ...(placement.changingScene ? { documentViewerByWindow: { ...state.documentViewerByWindow, [windowId]: emptyViewer }, documentViewer: emptyViewer } : {}),
        userSession: { ...state.userSession, preferences: { ...state.userSession.preferences, componentsMap: allComponents,
          notificationWindowDismissed: draft.layout!.notificationWindowDismissed,
          lastLayoutMode: draft.layout!.lastLayoutMode as "desktop" | "mobile" | undefined } },
      });
      useAppStore.getState().rebuildAllQuadtrees();
      broadcastComponentsByWindowSync(placement.componentsByWindow);
      return true;
    },
  });
}

/** Commit the source-authored edit before reconciling its current stored payload. */
export async function applyWorkspaceCompletionEdit(
  ownerId: string, source: CompletionComponentSource, sceneId: string | null,
  componentId: string, windowId: string, shouldApply: () => boolean,
): Promise<CompletionDeliveryResponse> {
  return commitWorkspaceCompletionEffect("apply", ownerId, source, sceneId, componentId, windowId, shouldApply);
}

function captureCompletionWorkspaceDraft(state: AppState): WorkspaceSaveDraft {
  const components = savedComponents(normalizeComponentList(state.getAllComponents()), state.heldComponentHeights);
  const preferences = state.userSession.preferences;
  return { components, layout: { componentsMap: components.map(toComponentReference),
    ...(typeof preferences?.notificationWindowDismissed === "boolean" ? { notificationWindowDismissed: preferences.notificationWindowDismissed } : {}),
    ...(typeof preferences?.lastLayoutMode === "string" ? { lastLayoutMode: preferences.lastLayoutMode } : {}),
  } };
}

/** Save an authored close once, then reconcile its current workspace receipt. */
export async function applyWorkspaceCompletionClose(ownerId: string, source: CompletionComponentSource, sceneId: string | null,
  targetId: string, windowId: string, shouldApply: () => boolean): Promise<CompletionCloseResponse> {
  const identity = captureWorkspacePersistenceIdentity(), queue = workspaceSaveQueue, session = getSession();
  const selected = useAppStore.getState().workspaceScenesByWindow[windowId];
  const ownsQueue = () => Boolean(identity && identity.expectedOwnerId === ownerId && identity.isCurrent()
    && getSession() === session && queue && workspaceSaveQueue === queue);
  const current = () => ownsQueue() && Boolean(useAppStore.getState().componentsByWindow[windowId])
    && useAppStore.getState().workspaceScenesByWindow[windowId] === selected;
  if (!identity?.expectedCompanyId || !queue || !current() || !shouldApply()
    || (selected?.sceneId ?? null) !== sceneId || selected && selected.ownerId !== ownerId) throw new WorkspaceOwnerChangedError();
  const companyId = identity.expectedCompanyId, capturedSource = completionComponentSourceSchema.parse(source);
  completionDeliveryIdSchema.parse(targetId);
  const key = completionEffectKey("close", ownerId, companyId, capturedSource, sceneId, targetId, windowId);
  let entry = pendingCompletionCloses.get(key);
  if (entry?.promise) return entry.promise;
  const sequence = ++workspaceSaveSequence;
  const reconcile = (record: NonNullable<typeof entry>, saved: WorkspaceSnapshot, before: WorkspaceSaveDraft) => {
    if (!current()) return false;
    const state = useAppStore.getState(), local = captureCompletionWorkspaceDraft(state);
    const merged = reconcileWorkspaceEffectDraft(before, saved, local);
    const lastLayoutMode = merged.layout!.lastLayoutMode;
    if (lastLayoutMode !== undefined && lastLayoutMode !== "desktop" && lastLayoutMode !== "mobile") return false;
    const activeIds = new Set(merged.layout!.componentsMap.map(ref => ref.id));
    const rows = new Map(merged.components.filter(row => activeIds.has(row.id)).map(row => [row.id, row]));
    if (Array.from(activeIds).some(id => !rows.has(id)) || Array.from(rows.values()).some(row => !isWorkspaceSyncComponent(row))) return false;
    if (flattenComponentsByWindow(state.componentsByWindow).some(component => EPHEMERAL_COMPONENTS.has(component.name) && rows.has(component.id))) return false;
    const durableByWindow = Object.fromEntries(Object.entries(state.componentsByWindow).map(([id, components]) => [id,
      components.flatMap(component => !EPHEMERAL_COMPONENTS.has(component.name) && rows.has(component.id)
        ? [rows.get(component.id)! as unknown as componentsMapType] : [])]));
    const placed = new Set(flattenComponentsByWindow(durableByWindow).map(component => component.id));
    durableByWindow[windowId].push(...Array.from(rows.values()).filter(row => !placed.has(row.id)) as unknown as componentsMapType[]);
    const order = new Map(merged.layout!.componentsMap.map((ref, index) => [ref.id, index]));
    for (const components of Object.values(durableByWindow)) components.sort((left, right) => order.get(left.id)! - order.get(right.id)!);
    // A global order that would require moving views between live windows needs
    // placement recovery. Never acknowledge one order and autosave another.
    if (JSON.stringify(flattenComponentsByWindow(durableByWindow).map(component => component.id)) !== JSON.stringify(merged.layout!.componentsMap.map(ref => ref.id))) return false;
    const { componentsByWindow, heldComponentHeights } = mergeSyncedComponentsByWindow(durableByWindow, state.componentsByWindow, state.heldComponentHeights);
    const closedRows = new Map(saved.components.filter(row => record.result?.receipt?.closedComponentIds.includes(row.componentId)
      && row.payload && !activeIds.has(row.componentId)).map(row => [row.componentId, row.payload!]));
    const deletedComponentsByWindow = { ...state.deletedComponentsByWindow };
    for (const [id, components] of Object.entries(state.componentsByWindow)) {
      const newlyClosed = components.filter(component => closedRows.has(component.id)).map(component => closedRows.get(component.id)! as unknown as componentsMapType);
      if (newlyClosed.length) deletedComponentsByWindow[id] = [...(deletedComponentsByWindow[id] ?? []).filter(component => !closedRows.has(component.id)), ...newlyClosed];
    }
    const allComponents = flattenComponentsByWindow(componentsByWindow);
    useAppStore.setState({ componentsByWindow, heldComponentHeights, deletedComponentsByWindow,
      userSession: { ...state.userSession, preferences: { ...state.userSession.preferences, componentsMap: allComponents,
        notificationWindowDismissed: merged.layout!.notificationWindowDismissed, lastLayoutMode } } });
    useAppStore.getState().rebuildAllQuadtrees();
    broadcastComponentsByWindowSync(componentsByWindow);
    record.reconciled = true;
    if (sequence === workspaceSaveSequence) useAppStore.setState({ workspaceSaveIssue: null });
    if (!record.promise && pendingCompletionCloses.get(key) === record) pendingCompletionCloses.delete(key);
    return true;
  };
  let operation: Promise<void>;
  if (entry) {
    const record = entry;
    operation = entry.result ? queue.resumeWorkspaceEffect(entry.execute, (saved, before) => reconcile(record, saved, before)) : queue.retryPending();
  } else {
    let execute: ReturnType<typeof import("@/src/features/jobs/client/completionCloseDelivery")["createWorkspaceCompletionCloseExecutor"]> | undefined;
    const record = { promise: null as Promise<CompletionCloseResponse> | null, reconciled: false,
      execute: async (expected: WorkspaceSnapshot) => {
        const { createWorkspaceCompletionCloseExecutor } = await import("@/src/features/jobs/client/completionCloseDelivery");
        execute ??= createWorkspaceCompletionCloseExecutor({ expectedOwnerId: ownerId, expectedCompanyId: companyId,
          jobId: capturedSource.jobId, sourceFingerprint: capturedSource.sourceFingerprint, workspaceSceneId: sceneId,
          effectIndex: Number(capturedSource.effectId.slice("component:".length)), mode: "inspect" }, targetId, { isCurrent: current });
        const result = await execute(expected); record.result = result; return result.snapshot;
      }, result: undefined as CompletionCloseResponse | undefined };
    entry = record; pendingCompletionCloses.set(key, record);
    const before = captureCompletionWorkspaceDraft(useAppStore.getState());
    // Flush the displayed draft through its existing row tokens before closing.
    // Register the close immediately so later edits retain this transaction.
    operation = Promise.all([queue.save(before), queue.applyWorkspaceEffect(before, record.execute,
      (saved, captured) => reconcile(record, saved, captured))]).then(() => undefined);
  }
  const record = entry;
  const pending = trackWorkspacePersistence(operation.then(() => {
    if (!current()) throw new WorkspaceOwnerChangedError();
    if (!record.reconciled || !record.result) throw new Error("Saved close reconciliation is incomplete");
    return record.result;
  }).catch(error => {
    if (ownsQueue() && sequence === workspaceSaveSequence) useAppStore.setState({ workspaceSaveIssue: error instanceof WorkspaceSaveConflictError ? "conflict" : "unavailable" });
    throw error;
  }).finally(() => {
    record.promise = null;
    if (record.reconciled && pendingCompletionCloses.get(key) === record) pendingCompletionCloses.delete(key);
  }));
  record.promise = pending; return pending;
}

/** Restore a durable source snapshot through the same queue that owns local saves. */
export async function undoWorkspaceCompletionEdit(
  ownerId: string, source: CompletionComponentSource, sceneId: string | null,
  componentId: string, windowId: string, shouldApply: () => boolean, recovery?: CompletionUndoRecoveryGuard,
  context?: UndoEffectContext,
): Promise<CompletionDeliveryResponse> {
  return commitWorkspaceCompletionEffect("undo", ownerId, source, sceneId, componentId, windowId, shouldApply, recovery, context);
}

/** A closed target needs no new effect, but its original pending barrier must settle. */
export async function reconcileRecoveredWorkspaceUndo(ownerId: string, value: unknown, windowId: string,
  shouldApply: () => boolean, recovery: CompletionUndoRecoveryGuard) {
  const identity = captureWorkspacePersistenceIdentity();
  if (!identity || !identity.expectedCompanyId || identity.expectedOwnerId !== ownerId || !shouldApply()) throw new WorkspaceOwnerChangedError();
  const action = parseCompletionUndoAction(value, ownerId, identity.expectedCompanyId), target = action.undoTarget.undo!;
  const key = completionEffectKey("undo", ownerId, identity.expectedCompanyId, target.source, target.workspaceSceneId, action.componentId, windowId, action.undoRequest);
  const visible = useAppStore.getState().getAllComponents().some(component => component.id === action.componentId);
  if (!visible && !pendingCompletionApplications.has(key)) return;
  await undoWorkspaceCompletionEdit(ownerId, target.source, target.workspaceSceneId, action.componentId, windowId, shouldApply, recovery, { action, alreadyUndone: true });
}

export async function applyWorkspaceUndoAction(ownerId: string, value: unknown, windowId: string, shouldApply: () => boolean, recovery?: CompletionUndoRecoveryGuard, alreadyUndone = false) {
  const identity = captureWorkspacePersistenceIdentity();
  if (!identity || !identity.expectedCompanyId || identity.expectedOwnerId !== ownerId || !identity.isCurrent() || !shouldApply()) throw new WorkspaceOwnerChangedError();
  const session = getSession();
  const guard = recovery ? completionUndoRecoveryGuardSchema.parse(recovery) : undefined;
  const queue = workspaceSaveQueue;
  const selected = useAppStore.getState().workspaceScenesByWindow[windowId];
  const originalSelection = selected;
  const current = () => {
    const state = useAppStore.getState(), selection = state.workspaceScenesByWindow[windowId];
    return identity.isCurrent() && getSession() === session && Boolean(queue) && workspaceSaveQueue === queue && shouldApply()
      && Boolean(state.componentsByWindow[windowId]) && selection === originalSelection;
  };
  const { effect, finalization } = await applyCompletionUndoAndFinalize(value, { ownerId, companyId: identity.expectedCompanyId, isCurrent: current, recovery: guard, alreadyUndone,
    apply: action => {
      const target = action.undoTarget.undo!;
      // The request stays bound to its originating window, while the saved
      // effect is reconciled in the window that actually owns the view.
      const state = useAppStore.getState();
      const destinations = Object.entries(state.componentsByWindow).flatMap(([id, rows]) =>
        rows.filter(row => row.id === action.componentId).map(() => id));
      if (destinations.length !== 1) throw new Error("The saved view needs one unambiguous window before undo");
      const destinationId = destinations[0];
      const destinationSelection = state.workspaceScenesByWindow[destinationId];
      if (destinationSelection && destinationSelection.ownerId !== ownerId) throw new WorkspaceOwnerChangedError();
      return undoWorkspaceCompletionEdit(ownerId, target.source, target.workspaceSceneId, action.componentId, destinationId, current, guard, { action });
    } });
  if (!current()) throw new WorkspaceOwnerChangedError();
  const state = useAppStore.getState();
  const reconciled = (guard ? restoreCompletionConversation : reconcileCompletionConversation)(state.chatSession.chatHistory, finalization);
  if (reconciled.applied) state.setChatSession({ ...state.chatSession, chatHistory: reconciled.chatHistory });
  return { component: effect?.component ?? null, conversation: reconciled.conversation };
}

async function commitWorkspaceCompletionEffect(
  mode: "apply" | "undo",
  ownerId: string, source: CompletionComponentSource, sceneId: string | null,
  componentId: string, windowId: string, shouldApply: () => boolean, recovery?: CompletionUndoRecoveryGuard,
  context?: UndoEffectContext,
): Promise<CompletionDeliveryResponse> {
  const guard = recovery ? completionUndoRecoveryGuardSchema.parse(recovery) : undefined;
  if (guard && mode !== "undo") throw new Error("Recovery requires an undo request");
  const session = getSession();
  const identity = captureWorkspacePersistenceIdentity();
  const queue = workspaceSaveQueue;
  const companyId = identity?.expectedCompanyId;
  const acknowledgementOnly = context?.alreadyUndone === true && Boolean(guard);
  if (context?.alreadyUndone && !guard) throw new Error("Recovered acknowledgement requires its original registration");
  const ownsQueue = () => Boolean(identity && identity.expectedOwnerId === ownerId && identity.isCurrent() && getSession() === session && queue && workspaceSaveQueue === queue);
  const selected = useAppStore.getState().workspaceScenesByWindow[windowId];
  const originalSelection = selected;
  const targetWindows = () => Object.entries(useAppStore.getState().componentsByWindow).flatMap(([id, rows]) =>
    rows.filter(row => row.id === componentId).map(() => id));
  const initialTargets = mode === "undo" ? targetWindows() : [windowId];
  if (mode === "undo" && (initialTargets.length > 1 || !acknowledgementOnly && initialTargets[0] !== windowId)) {
    throw new Error("The saved view needs one unambiguous window before undo");
  }
  const targetWindowId = initialTargets[0];
  const targetSelection = targetWindowId ? useAppStore.getState().workspaceScenesByWindow[targetWindowId] : undefined;
  const targetSceneId = targetSelection?.sceneId;
  if (mode === "undo" && targetSelection && targetSelection.ownerId !== ownerId) throw new WorkspaceOwnerChangedError();
  const current = () => {
    const state = useAppStore.getState();
    const selection = state.workspaceScenesByWindow[windowId];
    const targets = mode === "undo" ? targetWindows() : initialTargets;
    const destination = targetWindowId ? state.workspaceScenesByWindow[targetWindowId] : undefined;
    return ownsQueue() && Boolean(state.componentsByWindow[windowId])
      && selection === originalSelection
      && (mode !== "undo" || targets.length === initialTargets.length && targets[0] === targetWindowId
        && destination === targetSelection && destination?.sceneId === targetSceneId
        && (!destination || destination.ownerId === ownerId));
  };
  if (!identity || !companyId || !queue || !current() || !shouldApply()
    || !acknowledgementOnly && sceneId !== null && (originalSelection?.sceneId !== sceneId || originalSelection.ownerId !== ownerId)) throw new WorkspaceOwnerChangedError();
  const capturedSource = { ...source };
  const action = context ? parseCompletionUndoAction(context.action, ownerId, companyId) : null;
  const request = action?.undoRequest ?? (guard ? { reactId: guard.requestReactId, previewId: guard.previewId } : undefined);
  const key = completionEffectKey(mode, ownerId, companyId, capturedSource, sceneId, componentId, windowId, request);
  if (action && completionEffectKey(mode, ownerId, companyId, action.undoTarget.undo!.source, action.undoTarget.undo!.workspaceSceneId,
    action.componentId, windowId, action.undoRequest) !== key || guard && (request?.reactId !== guard.requestReactId || request?.previewId !== guard.previewId)) {
    throw new Error("The original undo binding changed");
  }
  const actionIdentity = action ? JSON.stringify(preparedUndoIdentity(action, ownerId, companyId),
    (_key, value) => value && typeof value === "object" && !Array.isArray(value)
      ? Object.fromEntries(Object.keys(value).sort().map(key => [key, value[key]])) : value) : undefined;
  let record = pendingCompletionApplications.get(key);
  if (record && (record.actionIdentity && record.actionIdentity !== actionIdentity
    || guard && record.recovery && guard.registrationDigest !== record.recovery.registrationDigest)) throw new Error("The original undo registration changed");
  if (record?.promise) return record.promise;
  const sequence = ++workspaceSaveSequence;
  const reconcile = (entry: NonNullable<typeof record>, row: WorkspaceSnapshot["components"][number], beforeApplication: Record<string, unknown>) => {
    if (!current() || (acknowledgementOnly || action) && !shouldApply()) return false;
    const state = useAppStore.getState();
    const visibleWindow = (targetWindowId ? [targetWindowId] : [])
      .find(id => state.componentsByWindow[id]?.some(value => value.id === componentId));
    const visible = visibleWindow ? normalizeComponentList(state.componentsByWindow[visibleWindow]).find(value => value.id === componentId) : undefined;
    // `beforeApplication` is the saved view: a folded window compares at its unfolded height.
    const savedVisible = visible ? savedComponents([visible], state.heldComponentHeights)[0] : undefined;
    if (!visible && !acknowledgementOnly || savedVisible && JSON.stringify(savedVisible) !== JSON.stringify(beforeApplication)
      || row.payload && !isWorkspaceSyncComponent(row.payload) || !row.payload && !acknowledgementOnly) return false;
    if (visibleWindow) {
      const payload = row.payload as unknown as componentsMapType | null;
      // A folded window stays one line here; the saved height it grows back to is the delivered one.
      const componentsByWindow = keepHeldLiveHeights({ ...state.componentsByWindow, [visibleWindow]: payload
        ? state.componentsByWindow[visibleWindow].map(value => value.id === componentId ? payload : value)
        : state.componentsByWindow[visibleWindow].filter(value => value.id !== componentId) }, state.componentsByWindow, state.heldComponentHeights);
      const heldComponentHeights = payload
        ? rebaseHeldHeights({ [visibleWindow]: [payload] }, state.heldComponentHeights, placementRebase(state.componentsByWindow, componentsByWindow, state.heldComponentHeights))
        : state.heldComponentHeights;
      useAppStore.setState({ componentsByWindow, heldComponentHeights, ...updateSessionComponentsMap(state, flattenComponentsByWindow(componentsByWindow)) });
      broadcastComponentsByWindowSync(componentsByWindow);
    }
    entry.reconciled = true;
    if (sequence === workspaceSaveSequence) useAppStore.setState({ workspaceSaveIssue: null });
    if (!entry.promise && pendingCompletionApplications.get(key) === entry) pendingCompletionApplications.delete(key);
    return true;
  };
  let operation: Promise<void>;
  if (record) {
    // Explicit recovery can refresh catalog/receipt metadata for this immutable
    // registration. The queued source and expected component fingerprint stay fixed.
    if (guard) record.recovery = guard;
    if (acknowledgementOnly && record.execute) {
      record.recoveryCurrent = () => current() && shouldApply();
      const entry = record;
      operation = queue.resumeEffect(record.execute, (row, before) => reconcile(entry, row, before));
    } else operation = queue.retryPending();
  } else {
    const state = useAppStore.getState();
    const before = savedComponents(normalizeComponentList(acknowledgementOnly ? state.getAllComponents() : state.componentsByWindow[windowId]), state.heldComponentHeights).find(value => value.id === componentId);
    if (!before || !isWorkspaceSyncComponent(before)) throw new Error("Restore the original view to receive this result");
    record = { promise: null, reconciled: false, ...(guard ? { recovery: guard } : {}), ...(actionIdentity ? { actionIdentity } : {}),
      ...(acknowledgementOnly ? { recoveryCurrent: () => current() && shouldApply() } : {}) };
    pendingCompletionApplications.set(key, record);
    const entry = record;
    entry.execute = async expectedFingerprint => {
      const valid = () => ownsQueue() && (!entry.recoveryCurrent || entry.recoveryCurrent());
      if (!valid()) throw new WorkspaceOwnerChangedError();
      const { requestCompletionDelivery } = await import("@/src/features/jobs/client/completionDelivery");
      try {
        const response = await requestCompletionDelivery({ expectedOwnerId: ownerId, expectedCompanyId: companyId,
          jobId: capturedSource.jobId, sourceFingerprint: capturedSource.sourceFingerprint, workspaceSceneId: sceneId,
          effectIndex: Number(capturedSource.effectId.slice("component:".length)), mode, expectedFingerprint, ...(mode === "undo" && entry.recovery ? { recovery: entry.recovery } : {}) }, componentId,
        { isCurrent: valid });
        if (entry.recoveryCurrent && response.outcome !== "undo_replayed") throw new Error("The original undo is not confirmed");
        entry.result = response;
        return { componentId, payload: response.component, fingerprint: response.fingerprint };
      } catch (error) {
        if ((error as { status?: number })?.status === 409) throw new WorkspaceSaveConflictError(componentId);
        throw error;
      }
    };
    operation = queue.applyEffect(componentId, before, entry.execute, (row, beforeApplication) => reconcile(entry, row, beforeApplication), { recovering: acknowledgementOnly });
  }
  const entry = record;
  const pending = trackWorkspacePersistence(operation.then(() => {
    if (!current()) throw new WorkspaceOwnerChangedError();
    if (!entry.reconciled || !entry.result) throw new Error("Task delivery reconciliation is incomplete");
    if (sequence === workspaceSaveSequence) useAppStore.setState({ workspaceSaveIssue: null });
    return entry.result;
  }).catch(error => {
    if (ownsQueue() && sequence === workspaceSaveSequence) useAppStore.setState({ workspaceSaveIssue: error instanceof WorkspaceSaveConflictError ? "conflict" : "unavailable" });
    throw error;
  }).finally(() => {
    entry.promise = null;
    if (entry.reconciled && pendingCompletionApplications.get(key) === entry) pendingCompletionApplications.delete(key);
  }));
  entry.promise = pending;
  return pending;
}

/** Restore fresh owned rows and their tokens without replacing already-open content. */
export async function restoreWorkspaceComponents(
  ownerId: string,
  componentIds: readonly string[],
  windowId: string,
  prepare: (rows: WorkspaceSnapshot["components"]) => componentsMapType[],
  shouldApply: () => boolean,
): Promise<void> {
  const identity = captureWorkspacePersistenceIdentity();
  const queue = workspaceSaveQueue;
  const current = () => Boolean(identity && identity.expectedOwnerId === ownerId && identity.isCurrent()
    && queue && workspaceSaveQueue === queue && shouldApply() && useAppStore.getState().componentsByWindow[windowId]);
  if (!current() || !queue) throw new WorkspaceOwnerChangedError();
  const ids = Array.from(new Set(componentIds));
  await trackWorkspacePersistence(queue.restore(ids, rows => {
    if (!current()) throw new WorkspaceOwnerChangedError();
    const state = useAppStore.getState();
    const present = new Set(state.getAllComponents().map(component => component.id));
    const missing = rows.filter(row => !present.has(row.componentId));
    if (missing.some(row => !row.payload)) throw new Error("A saved workspace component is no longer available");
    const prepared = prepare(missing);
    if (prepared.length !== missing.length || new Set(prepared.map(value => value.id)).size !== prepared.length
      || prepared.some(value => !missing.some(row => row.componentId === value.id))) throw new Error("Saved workspace components could not be prepared");
    if (!current()) throw new WorkspaceOwnerChangedError();
    if (prepared.length) state.addComponentToGrid(prepared, undefined, { targetWindowId: windowId });
    const applied = new Set((useAppStore.getState().componentsByWindow[windowId] || []).map(value => value.id));
    return prepared.filter(value => applied.has(value.id)).map(value => value.id);
  }));
  if (!current()) throw new WorkspaceOwnerChangedError();
  const available = new Set(useAppStore.getState().getAllComponents().map(value => value.id));
  if (ids.some(id => !available.has(id))) throw new Error("A saved workspace component could not be opened");
}

/** A window's components as they may be saved: folded windows at their unfolded height. */
export function savedWindowComponents(windowId: string): componentsMapType[] {
  const state = useAppStore.getState();
  return savedComponents(state.componentsByWindow[windowId] || [], state.heldComponentHeights);
}

/** Confirm normal persistence saved this snapshot, without writing stale content again. */
export async function verifyWorkspaceComponents(ownerId: string, components: readonly componentsMapType[]): Promise<void> {
  const current = () => {
    const state = useAppStore.getState();
    return Boolean(ownerId) && state.userSession.userId === ownerId && state.userSession.isAuthorized === true
      && ownerId !== "demo" && getSession() !== "demo" && hasBrowserSession()
      && !state.isSigningOut && !isWorkspacePersistenceSuspended();
  };
  if (!current()) throw new Error("Workspace owner changed");
  const snapshot = savedComponents(normalizeComponentList(components), useAppStore.getState().heldComponentHeights);
  if (snapshot.length !== components.length || snapshot.length > 100
    || snapshot.some(component => typeof component.id !== "string" || !component.id.trim() || component.id.trim() !== component.id || component.id.length > 256)
    || new Set(snapshot.map(component => component.id)).size !== snapshot.length) throw new Error("Invalid workspace snapshot");
  const body = JSON.stringify({ index: "verify-user-components", payload: { expectedOwnerId: ownerId, components: snapshot } });
  if (new TextEncoder().encode(body).byteLength > MAX_RECORDS_BODY_BYTES) throw new Error("Invalid workspace snapshot");
  if (snapshot.length === 0) return;
  const sessionId = getSession();
  const response = await trackWorkspacePersistence(fetchWorkspacePersistence(apiUrl("/api/records"), {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...(sessionId ? { "x-session-id": sessionId } : {}), ...getUserLogSessionHeaders() },
    credentials: isElectron ? "include" : "same-origin",
    body,
  }));
  const receipt = response.ok ? await response.json().catch(() => null) : null;
  const saved = receipt?.status === "success" && receipt.ownerId === ownerId && receipt.verified === snapshot.length;
  if (!current()) throw new Error("Workspace owner changed");
  if (!saved) throw new Error("Workspace components could not be saved");
}

async function updatePreferences(
  preferences: any,
  options: { allowComponentRemoval?: boolean; dismissNotificationWindow?: boolean; closedComponents?: any[] } = {},
) {
  const identity = captureWorkspacePersistenceIdentity();
  if (!identity || preferences?.componentsMap?.some((comp: any) => comp.name === 'SubscriptionRequired')) return;
  const queue = workspaceSaveQueue;
  if (!queue) {
    useAppStore.setState({ workspaceSaveIssue: "unavailable" });
    return;
  }
  const sequence = ++workspaceSaveSequence;
  // Folded windows save at their unfolded height (see heldHeights.ts).
  const held = useAppStore.getState().heldComponentHeights;
  const fullComponents = savedComponents(normalizeComponentList(preferences?.componentsMap), held);
  const draft = {
    components: Array.from(new Map([...savedComponents(normalizeComponentList(options.closedComponents), held), ...fullComponents].map(value => [value.id, value])).values()),
    layout: {
      componentsMap: fullComponents.map(toComponentReference),
      ...(options.dismissNotificationWindow ? { notificationWindowDismissed: true }
        : typeof preferences?.notificationWindowDismissed === "boolean" ? { notificationWindowDismissed: preferences.notificationWindowDismissed } : {}),
      ...(typeof preferences?.lastLayoutMode === "string" ? { lastLayoutMode: preferences.lastLayoutMode } : {}),
    },
  };
  const current = () => identity.isCurrent() && workspaceSaveQueue === queue;
  await trackWorkspacePersistence((async () => {
    try {
      try {
        await queue.save(draft, { preserveMissingLayoutReferences: options.allowComponentRemoval !== true });
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (!current() || !queue.hasPendingWrite() || error instanceof WorkspaceSaveConflictError || error instanceof WorkspaceOwnerChangedError
          || status !== undefined && ![502, 503, 504].includes(status)) throw error;
        await new Promise(resolve => setTimeout(resolve, 150));
        if (!current()) throw new WorkspaceOwnerChangedError();
        await queue.retryPending();
      }
      if (current() && sequence === workspaceSaveSequence) useAppStore.setState({ workspaceSaveIssue: null });
    } catch (error) {
      if (current() && sequence === workspaceSaveSequence) useAppStore.setState({ workspaceSaveIssue: error instanceof WorkspaceSaveConflictError ? "conflict" : "unavailable" });
      windowStoreDebug("Workspace save did not finish", error);
    }
  })());
}

const getGroupBounds = (components: any[]) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const component of components) {
    const { position, size } = component;
    minX = Math.min(minX, position.x);
    minY = Math.min(minY, position.y);
    maxX = Math.max(maxX, position.x + size.width);
    maxY = Math.max(maxY, position.y + size.height);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // ============================================
      // GLOBAL STATE (shared across all windows)
      // ============================================
      userSession: emptyUserInfo,
      chatSession: emptyChatSession,
      isSigningOut: false,
      workspaceSaveIssue: null,
      fileList: [],
      micState: 'Checking Microphone...' as MicState,
      wakeWordPrompt: null as string | null,
      isWakeWordListening: false,
      isAIPlaying: false,
      liveAudioData: undefined as Float32Array | undefined,
      notifications: [],
      notificationFeedStatus: NOTIFICATION_FEED_PENDING,
      readNotificationKeys: [],
      pendingDoneNotificationKeys: {},
      pendingReopenNotificationKeys: {},
      // Track which events have been notified at which time thresholds
      // Format: { [eventId]: { notified30min: timestamp, notified10min: timestamp } }
      notifiedEventThresholds: {} as Record<string, { notified30min?: number; notified10min?: number }>,
      activeCall: null as ActiveCall,
      joinedNativeCall: null as JoinedNativeCall | null,
      pendingChatMessage: null as PendingChatMessage,
      sendingChatMessage: null as PendingChatMessage,
      aiActivityByComponent: {} as Record<string, AiActivityState>,
      heldComponentHeights: {} as AppState["heldComponentHeights"],
      gestureArmedComponentId: null as string | null,
      showQuadTreeOverlay: false,
      maxEditHistory: 50,

      // Mobile layout mode - when true, components use mobilePosition/mobileSize
      // Automatically set by device detection, persisted to maintain consistency
      isMobileLayout: false,

      documentViewerByWindow: {},
      documentViewer: {
        files: [],
        currentDocumentIndex: 0
      },

      mediaGallery: {
        mediaGroups: [],
        currentMediaIndex: 0
      },

      // ============================================
      // ARTIFACT STORE
      // ============================================
      // Artifacts live here exactly once, keyed by artifactId. Components
      // reference IDs; provenance/lineage on each artifact powers the
      // transparency panel. Capped to keep localStorage persistence bounded.
      artifactsById: {} as Record<string, ChainArtifact>,

      upsertArtifacts: (artifacts: ChainArtifact[]) => {
        if (!Array.isArray(artifacts) || artifacts.length === 0) return;
        set((state) => {
          const next: Record<string, ChainArtifact> = { ...state.artifactsById };
          for (const artifact of artifacts) {
            if (!artifact?.artifactId) continue;
            const existing = next[artifact.artifactId];
            // Never downgrade a newer version already in the store
            if (existing && existing.version > artifact.version) continue;
            next[artifact.artifactId] = artifact;
          }
          // Prune oldest artifacts beyond the cap (by retrievedAt)
          const MAX_ARTIFACTS = 300;
          const ids = Object.keys(next);
          if (ids.length > MAX_ARTIFACTS) {
            ids
              .sort((a, b) => (next[a]?.provenance?.retrievedAt || 0) - (next[b]?.provenance?.retrievedAt || 0))
              .slice(0, ids.length - MAX_ARTIFACTS)
              .forEach((staleId) => delete next[staleId]);
          }
          return { artifactsById: next };
        });
      },

      updateArtifactPayload: (artifactId: string, payload: unknown, revision: { transform: string; reason: string }) => {
        set((state) => {
          const existing = state.artifactsById[artifactId];
          if (!existing) return {};
          return {
            artifactsById: {
              ...state.artifactsById,
              [artifactId]: reviseArtifact(existing, payload, revision.transform, { reason: revision.reason }),
            },
          };
        });
      },

      removeArtifacts: (artifactIds: string[]) => {
        if (!Array.isArray(artifactIds) || artifactIds.length === 0) return;
        set((state) => {
          const next = { ...state.artifactsById };
          artifactIds.forEach((artifactId) => delete next[artifactId]);
          return { artifactsById: next };
        });
      },

      // ============================================
      // PER-WINDOW STATE
      // ============================================
      // Current window's ID (set at runtime, NOT persisted)
      currentWindowId: null as string | null,
      workspaceScenesByWindow: {},
      setWorkspaceScene: (windowId: string, sceneId: string | null, ownerId: string, options: { preserveDocuments?: boolean } = {}) => {
        const state = get();
        if (!state.userSession.isAuthorized || state.isSigningOut || ownerId === "demo"
          || state.userSession.userId !== ownerId || !state.componentsByWindow[windowId]
          || (sceneId !== null && (!sceneId || sceneId.trim() !== sceneId || sceneId.length > 256))) return false;
        const changed = !options.preserveDocuments && state.workspaceScenesByWindow[windowId]?.sceneId !== sceneId;
        const emptyViewer = { files: [], currentDocumentIndex: 0 };
        set({ workspaceScenesByWindow: { ...state.workspaceScenesByWindow, [windowId]: { ownerId, sceneId } },
          ...(changed ? { documentViewerByWindow: { ...state.documentViewerByWindow, [windowId]: emptyViewer },
            ...(windowId === getCurrentWindowIdOrDefault(state) ? { documentViewer: emptyViewer } : {}) } : {}),
        });
        return true;
      },

      // Components organized by window ID - PRIMARY source of truth
      componentsByWindow: {} as Record<string, componentsMapType[]>,

      // Screen dimensions per window
      screenDimensionsByWindow: {} as Record<string, WindowDimensions>,

      // QuadTree per window (NOT persisted - rebuilt at runtime)
      quadtreesByWindow: {} as Record<string, QuadTree>,

      // Deleted components per window (for restore functionality)
      deletedComponentsByWindow: {} as Record<string, componentsMapType[]>,

      // Edit history per window (for undo/redo)
      editHistoryByWindow: {} as Record<string, WindowEditHistoryState>,

      // ============================================
      // WINDOW INITIALIZATION & STATE ACCESS
      // ============================================

      /**
       * Initialize a window with its ID and dimensions.
       * MUST be called when a window loads.
       * For web mode, use DEFAULT_WINDOW_ID.
       */
      initializeWindow: (windowId: string, width: number, height: number) => {
        const existingState = get();

        windowStoreDebug(`[Window] Initializing window ${windowId} with dimensions ${width}x${height}:`, {
          existingWindowIds: Object.keys(existingState.componentsByWindow),
          existingComponentCounts: Object.fromEntries(
            Object.entries(existingState.componentsByWindow).map(([wId, comps]) => [wId, comps.length])
          ),
          currentWindowId: existingState.currentWindowId
        });

        // Set current window ID
        if (existingState.currentWindowId !== windowId) {
          get().setCurrentWindowId(windowId);
        }

        // Initialize window state if not exists
        const { componentsByWindow, screenDimensionsByWindow, deletedComponentsByWindow, editHistoryByWindow } = get();

        const updates: any = {};

        // Initialize components if not exists
        if (!componentsByWindow[windowId]) {
          windowStoreDebug(`[Window] Creating new componentsByWindow entry for ${windowId}`);
          updates.componentsByWindow = {
            ...componentsByWindow,
            [windowId]: []
          };
        } else {
          windowStoreDebug(`[Window] componentsByWindow entry already exists for ${windowId} with ${componentsByWindow[windowId].length} components`);
        }

        // Initialize deleted components if not exists
        if (!deletedComponentsByWindow[windowId]) {
          updates.deletedComponentsByWindow = {
            ...deletedComponentsByWindow,
            [windowId]: []
          };
        }

        // Initialize edit history if not exists
        if (!editHistoryByWindow[windowId]) {
          updates.editHistoryByWindow = {
            ...editHistoryByWindow,
            [windowId]: { history: [], currentIndex: -1 }
          };
        }

        if (Object.keys(updates).length > 0) {
          windowStoreDebug(`[Window] Applying updates for ${windowId}:`, Object.keys(updates));
          set(updates);
        }

        // Set dimensions and build quadtree
        get().setDimensions(width, height);

        // Log final state
        const finalState = get();
        windowStoreDebug(`[Window] Window ${windowId} initialized. Final state:`, {
          allWindowIds: Object.keys(finalState.componentsByWindow),
          componentCounts: Object.fromEntries(
            Object.entries(finalState.componentsByWindow).map(([wId, comps]) => [wId, comps.length])
          )
        });
      },

      /**
       * Set the current window ID.
       * Called when switching between windows or on window focus.
       */
      setCurrentWindowId: (windowId: string) => {
        if (get().currentWindowId === windowId) {
          return;
        }
        windowStoreDebug(`[Window] Setting current window ID: ${windowId}`);
        set((state) => ({ currentWindowId: windowId,
          documentViewerByWindow: { ...state.documentViewerByWindow, [getCurrentWindowIdOrDefault(state)]: state.documentViewer },
          documentViewer: state.documentViewerByWindow[windowId] ?? { files: [], currentDocumentIndex: 0 },
        }));
      },

      /**
       * Get components for the CURRENT window.
       */
      getComponents: (): componentsMapType[] => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        return getWindowComponents(state, windowId);
      },

      /**
       * Get screen dimensions for the CURRENT window.
       */
      getScreenDimensions: (): WindowDimensions | null => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        return getWindowDimensions(state, windowId);
      },

      /**
       * Get quadtree for the CURRENT window.
       */
      getQuadtree: (): QuadTree | null => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        return getWindowQuadtree(state, windowId);
      },

      /**
       * Get quadtree for a SPECIFIC window by ID.
       */
      getQuadtreeForWindow: (windowId: string): QuadTree | null => {
        const state = get();
        return getWindowQuadtree(state, windowId);
      },

      /**
       * Get screen dimensions for a SPECIFIC window by ID.
       */
      getScreenDimensionsForWindow: (windowId: string): WindowDimensions | null => {
        const state = get();
        return getWindowDimensions(state, windowId);
      },

      /**
       * Get components for a SPECIFIC window by ID.
       */
      getComponentsForWindow: (windowId: string): componentsMapType[] => {
        const state = get();
        return getWindowComponents(state, windowId);
      },

      /**
       * Find the best window for placing a new component based on available space.
       * Returns the window ID with the most available space, or the current window if all have space.
       *
       * @param width - Component width
       * @param height - Component height
       * @param preferredPosition - Optional preferred position to check
       * @returns Object with windowId and whether space was found
       */
      findBestWindowForComponent: (
        width: number,
        height: number,
        preferredPosition?: { x: number; y: number }
      ): { windowId: string; hasSpace: boolean; position: { x: number; y: number } | null } => {
        const state = get();
        const { quadtreesByWindow, screenDimensionsByWindow, componentsByWindow } = state;
        const currentWindowId = getCurrentWindowIdOrDefault(state);

        // Get all initialized window IDs
        const windowIds = Object.keys(quadtreesByWindow).filter(
          wId => screenDimensionsByWindow[wId] && quadtreesByWindow[wId]
        );

        // If no windows are initialized, fall back to current window
        if (windowIds.length === 0) {
          return { windowId: currentWindowId, hasSpace: false, position: null };
        }

        // Calculate available space per window
        type WindowSpaceInfo = {
          windowId: string;
          hasSpace: boolean;
          position: { x: number; y: number } | null;
          freeAreaPercent: number;
        };

        const windowSpaceInfos: WindowSpaceInfo[] = [];
        const margin = OCCUPANCY_MARGIN;

        for (const windowId of windowIds) {
          const quadtree = quadtreesByWindow[windowId];
          const dimensions = screenDimensionsByWindow[windowId];
          const components = componentsByWindow[windowId] || [];

          if (!quadtree || !dimensions) continue;

          const { width: screenWidth, height: screenHeight } = dimensions;

          // Calculate occupied area from components
          let occupiedArea = 0;
          for (const comp of components) {
            if (blocksPlacement(comp)) {
              occupiedArea += (comp.size.width + margin * 2) * (comp.size.height + margin * 2);
            }
          }

          const totalArea = screenWidth * screenHeight;
          const freeAreaPercent = Math.max(0, (totalArea - occupiedArea) / totalArea);

          // Try to find a position for the component in this window
          let foundPosition: { x: number; y: number } | null = null;
          const componentRect = toOccupancyRect(0, 0, width, height);

          // Check preferred position first
          if (preferredPosition) {
            const testRect = toOccupancyRect(preferredPosition.x, preferredPosition.y, width, height);
            if (preferredPosition.x >= margin &&
                preferredPosition.y >= margin &&
                preferredPosition.x + width + margin <= screenWidth &&
                preferredPosition.y + height + margin <= screenHeight &&
                !quadtree.overlapsAny(testRect)) {
              foundPosition = preferredPosition;
            }
          }

          // If no preferred position or it's occupied, try grid-based search
          if (!foundPosition) {
            const step = 50; // Grid step size
            outer: for (let y = margin; y + height + margin <= screenHeight; y += step) {
              for (let x = margin; x + width + margin <= screenWidth; x += step) {
                const testRect = toOccupancyRect(x, y, width, height);
                if (!quadtree.overlapsAny(testRect)) {
                  foundPosition = { x, y };
                  break outer;
                }
              }
            }
          }

          windowSpaceInfos.push({
            windowId,
            hasSpace: foundPosition !== null,
            position: foundPosition,
            freeAreaPercent
          });
        }

        // Sort by: 1) has space (true first), 2) free area percent (descending)
        windowSpaceInfos.sort((a, b) => {
          if (a.hasSpace !== b.hasSpace) {
            return a.hasSpace ? -1 : 1;
          }
          return b.freeAreaPercent - a.freeAreaPercent;
        });

        // Prefer current window if it has space
        const currentWindowInfo = windowSpaceInfos.find(w => w.windowId === currentWindowId);
        if (currentWindowInfo?.hasSpace) {
          return {
            windowId: currentWindowId,
            hasSpace: true,
            position: currentWindowInfo.position
          };
        }

        // Otherwise, return the window with the most space
        const bestWindow = windowSpaceInfos[0];
        if (bestWindow) {
          return {
            windowId: bestWindow.windowId,
            hasSpace: bestWindow.hasSpace,
            position: bestWindow.position
          };
        }

        // Fallback to current window
        return { windowId: currentWindowId, hasSpace: false, position: null };
      },

      /**
       * Get deleted components for the CURRENT window.
       */
      getDeletedComponents: (): componentsMapType[] => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        return getWindowDeletedComponents(state, windowId);
      },

      /**
       * Set dimensions for the CURRENT window and rebuild its quadtree.
       */
      setDimensions: (width: number, height: number) => {
        // Reject invalid dimensions to prevent RangeError in analyzeFreeSpace
        if (width <= 0 || height <= 0) {
          console.warn(`[Window] Rejected invalid dimensions: ${width}x${height}`);
          return;
        }
        const windowId = getCurrentWindowIdOrDefault(get());
        const state = get();
        const existingDimensions = state.screenDimensionsByWindow[windowId];
        if (
          existingDimensions?.width === width &&
          existingDimensions?.height === height &&
          state.quadtreesByWindow[windowId]
        ) {
          return;
        }
        // Update dimensions. Windows restored from storage before the
        // viewport was known, or left behind by a larger one, are fitted into
        // the new workspace before they are indexed.
        set((state) => {
          const screenDimensionsByWindow = {
            ...state.screenDimensionsByWindow,
            [windowId]: { width, height }
          };
          const before = getWindowComponents(state, windowId);
          const resized = {
            isMobileLayout: state.isMobileLayout,
            screenDimensionsByWindow,
            heldComponentHeights: state.heldComponentHeights,
          };
          const inside = fitComponentsToWorkspace(resized, windowId, before);
          // A window the fit pushed into a conversation's area, or one a
          // conversation's area now reaches because it turned from beside
          // the sphere to under it, moves clear. A saved layout loaded
          // before the first size is the person's own and stays.
          const fitted = existingDimensions
            ? keepConversationsClear(resized, windowId, before, existingDimensions, inside)
            : inside;
          return fitted === before
            ? { screenDimensionsByWindow }
            : {
                screenDimensionsByWindow,
                componentsByWindow: { ...state.componentsByWindow, [windowId]: fitted },
              };
        });

        // Build/rebuild quadtree for this window
        const wholeScreen = new Rectangle(0, 0, width, height);
        const quadtree = new QuadTree(wholeScreen, 0);

        // Insert existing components into quadtree (skip minimized components - they don't occupy space)
        const components = getWindowComponents(get(), windowId);
        for (const component of components) {
          // Skip minimized components - they don't occupy space in the quadtree
          if (component.isMinimized) {
            continue;
          }
          const rect = toOccupancyRect(
            component.position.x,
            component.position.y,
            component.size.width,
            component.size.height
          );
          quadtree.insert(rect);
        }

        set((state) => ({
          quadtreesByWindow: {
            ...state.quadtreesByWindow,
            [windowId]: quadtree
          }
        }));

      },

      /**
       * Get ALL components across ALL windows (aggregated view).
       */
      getAllComponents: (): componentsMapType[] => {
        const { componentsByWindow } = get();
        const allComponents: componentsMapType[] = [];
        const windowCounts: Record<string, number> = {};

        for (const windowId of Object.keys(componentsByWindow)) {
          const windowComponents = componentsByWindow[windowId];
          windowCounts[windowId] = windowComponents.length;
          allComponents.push(...windowComponents);
        }

        windowStoreDebug(`[getAllComponents] Aggregating components from ${Object.keys(componentsByWindow).length} window(s):`, {
          windowCounts,
          totalComponents: allComponents.length,
          componentNames: allComponents.map(c => c.name)
        });

        return normalizeComponentList(allComponents);
      },

      /**
       * Check if a rectangle overlaps with any component across ALL windows.
       */
      getAggregatedQuadtreeOverlap: (rect: Rectangle, excludeWindowId?: string): boolean => {
        const { quadtreesByWindow } = get();

        for (const [windowId, qt] of Object.entries(quadtreesByWindow)) {
          if (excludeWindowId && windowId === excludeWindowId) continue;
          if (qt.overlapsAny(rect)) {
            return true;
          }
        }

        return false;
      },

      /**
       * Rebuild quadtree for the requested window, defaulting to the current one.
       * Uses effective positions/sizes based on current layout mode (mobile vs desktop).
       */
      rebuildQuadtree: (options: { targetWindowId?: string } = {}) => {
        const state = get();
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
        const dimensions = getWindowDimensions(state, windowId);
        const { isMobileLayout } = state;

        if (!dimensions) {
          console.warn(`[Window] Cannot rebuild quadtree for ${windowId}: no dimensions`);
          return;
        }

        const wholeScreen = new Rectangle(0, 0, dimensions.width, dimensions.height);
        const quadtree = new QuadTree(wholeScreen, 0);

        // A rebuild follows a wholesale change to the window list (a restore,
        // a sync from another window), so fit that list before indexing it.
        const stored = getWindowComponents(state, windowId);
        const components = fitComponentsToWorkspace(state, windowId, stored);
        if (components !== stored) {
          set((current) => ({
            componentsByWindow: { ...current.componentsByWindow, [windowId]: components },
          }));
        }
        let insertedCount = 0;
        for (const component of components) {
          // Skip minimized components - they don't occupy space in the quadtree
          if (component.isMinimized) {
            continue;
          }
          // Use effective position/size based on layout mode
          const effectivePosition = isMobileLayout && component.mobilePosition
            ? component.mobilePosition
            : component.position;
          const effectiveSize = isMobileLayout && component.mobileSize
            ? component.mobileSize
            : component.size;

          const rect = toOccupancyRect(
            effectivePosition.x,
            effectivePosition.y,
            effectiveSize.width,
            effectiveSize.height
          );
          quadtree.insert(rect);
          insertedCount++;
        }

        set((state) => ({
          quadtreesByWindow: {
            ...state.quadtreesByWindow,
            [windowId]: quadtree
          }
        }));

        windowStoreDebug(`[Window] Rebuilt quadtree for ${windowId} with ${insertedCount}/${components.length} components (${components.length - insertedCount} minimized), layout: ${isMobileLayout ? 'mobile' : 'desktop'}`);
      },

      /**
       * Rebuild quadtrees for ALL windows.
       * Uses effective positions/sizes based on current layout mode (mobile vs desktop).
       */
      rebuildAllQuadtrees: () => {
        const state = get();
        const { screenDimensionsByWindow, isMobileLayout } = state;
        const newQuadtrees: Record<string, QuadTree> = {};
        // Same fit as rebuildQuadtree, for every window this renderer has measured.
        let componentsByWindow = state.componentsByWindow;

        for (const windowId of Object.keys(state.componentsByWindow)) {
          const dimensions = screenDimensionsByWindow[windowId];
          if (!dimensions) continue;

          const wholeScreen = new Rectangle(0, 0, dimensions.width, dimensions.height);
          const quadtree = new QuadTree(wholeScreen, 0);

          const stored = state.componentsByWindow[windowId];
          const components = fitComponentsToWorkspace(state, windowId, stored);
          if (components !== stored) {
            componentsByWindow = { ...componentsByWindow, [windowId]: components };
          }
          let insertedCount = 0;
          for (const component of components) {
            // Skip minimized components - they don't occupy space in the quadtree
            if (component.isMinimized) {
              continue;
            }
            // Use effective position/size based on layout mode
            const effectivePosition = isMobileLayout && component.mobilePosition
              ? component.mobilePosition
              : component.position;
            const effectiveSize = isMobileLayout && component.mobileSize
              ? component.mobileSize
              : component.size;

            const rect = toOccupancyRect(
              effectivePosition.x,
              effectivePosition.y,
              effectiveSize.width,
              effectiveSize.height
            );
            quadtree.insert(rect);
            insertedCount++;
          }

          newQuadtrees[windowId] = quadtree;
          windowStoreDebug(`[Window] Rebuilt quadtree for ${windowId} with ${insertedCount}/${components.length} components, layout: ${isMobileLayout ? 'mobile' : 'desktop'}`);
        }

        set(componentsByWindow === state.componentsByWindow
          ? { quadtreesByWindow: newQuadtrees }
          : { quadtreesByWindow: newQuadtrees, componentsByWindow });
        windowStoreDebug(`[Window] Rebuilt all ${Object.keys(newQuadtrees).length} quadtrees, layout: ${isMobileLayout ? 'mobile' : 'desktop'}`);
      },

      /**
       * Move a component from the CURRENT window to another window.
       */
      moveComponentToWindow: (
        componentId: string,
        targetWindowId: string,
        newPosition?: { x: number; y: number }
      ): boolean => {
        const state = get();
        const currentWindowId = getCurrentWindowIdOrDefault(state);
        const sourceWindowId = getWindowComponents(state, currentWindowId).some((component) => component.id === componentId)
          ? currentWindowId
          : Object.keys(state.componentsByWindow).find((windowId) => getWindowComponents(state, windowId).some((component) => component.id === componentId));
        if (!sourceWindowId) return false;

        if (sourceWindowId === targetWindowId) {
          console.warn(`[Window] Component already in target window ${targetWindowId}`);
          return false;
        }

        const sourceComponents = getWindowComponents(state, sourceWindowId);
        const componentIndex = sourceComponents.findIndex(c => c.id === componentId);

        if (componentIndex === -1) {
          console.error(`[Window] Component ${componentId} not found in ${sourceWindowId}`);
          return false;
        }

        const component = sourceComponents[componentIndex];
        const members = getDockGroupMembers(sourceComponents, component.id);
        const movedIds = new Set(members.map((member) => member.id));
        const movedSourceComponents = newPosition
          ? moveDockGroup(
              sourceComponents,
              component.id,
              newPosition,
              state.isMobileLayout,
            )
          : sourceComponents;
        // A folded window leaves at its unfolded height and without its hold:
        // the hold belongs to this renderer's source window, and the target
        // renderer folds it again if it still needs one line.
        const movedMembers = savedComponents(
          movedSourceComponents.filter((candidate) => movedIds.has(candidate.id)),
          state.heldComponentHeights,
        );

        // Remove from source, add to target
        const newSourceComponents = sourceComponents.filter(
          (candidate) => !movedIds.has(candidate.id),
        );
        const targetComponents = getWindowComponents(state, targetWindowId);
        const newTargetComponents = [...targetComponents, ...movedMembers];

        // Update state
        set((state) => ({
          componentsByWindow: {
            ...state.componentsByWindow,
            [sourceWindowId]: newSourceComponents,
            [targetWindowId]: newTargetComponents
          },
          heldComponentHeights: withoutHolds(state.heldComponentHeights, movedIds),
        }));

        // Update quadtrees
        const sourceQt = getWindowQuadtree(get(), sourceWindowId);
        const targetQt = getWindowQuadtree(get(), targetWindowId);
        for (const oldMember of members) {
          if (sourceQt && !oldMember.isMinimized) {
            const geometry = getLayoutGeometry(
              oldMember,
              state.isMobileLayout,
            );
            sourceQt.remove(
              toOccupancyRect(
                geometry.position.x,
                geometry.position.y,
                geometry.size.width,
                geometry.size.height,
              ),
            );
          }
        }
        for (const movedMember of movedMembers) {
          if (targetQt && !movedMember.isMinimized) {
            const geometry = getLayoutGeometry(
              movedMember,
              state.isMobileLayout,
            );
            targetQt.insert(
              toOccupancyRect(
                geometry.position.x,
                geometry.position.y,
                geometry.size.width,
                geometry.size.height,
              ),
            );
          }
        }

        // Persist to backend
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        broadcastComponentsByWindowSync(get().componentsByWindow);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents
        });

        windowStoreDebug(
          `[Window] Moved ${movedMembers.length} dock member(s) from ${sourceWindowId} to ${targetWindowId}`,
        );
        return true;
      },

      setIsSigningOut: (value: boolean) => {
        if (value) invalidateWorkspaceSaveQueue();
        set((state) => ({ isSigningOut: value }));
      },

      findNextAvailablePosition: (width: number, height: number, priority: string, group = null, score = 5, options: { targetWindowId?: string } = {}) => {
        const state = get();
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
        const dimensions = getWindowDimensions(state, windowId);
        const quadtree = getPlacementOccupancy(state, windowId);
        const components = getWindowComponents(state, windowId);

        if (!dimensions || !quadtree) return null;
        const { width: screenWidth, height: screenHeight } = dimensions;

        windowStoreDebug(`[Window ${windowId}] findNextAvailablePosition with new space-aware logic`)
        const margin = OCCUPANCY_MARGIN;

        try {
          const priorityPositions: any = {
            high: { x: screenWidth / 2, y: screenHeight / 2 },
            mid: { x: screenWidth / 4, y: screenHeight / 4 },
            low: { x: screenWidth * 0.75, y: screenHeight * 0.75 },
          };

          const desiredPosition = priorityPositions[(priority || 'mid').toLowerCase()] ?? priorityPositions.mid;

          // Handle group placement - place near existing group members
          const groupComponents = components.filter((component) => component.group === group);
          let targetPosition = desiredPosition;

          if (groupComponents.length > 0) {
            const groupBounds = getGroupBounds(groupComponents);
            targetPosition = {
              x: groupBounds.x + groupBounds.width + margin + width / 2,
              y: groupBounds.y + height / 2,
            };
          }

          // Top-left anchor for the distance-ordered fallback searches below.
          // Derived from targetPosition so group placement carries through.
          const targetTopLeft = clampPositionToScreen(
            targetPosition.x - width / 2,
            targetPosition.y - height / 2,
            width,
            height,
            screenWidth,
            screenHeight,
            margin
          );

          // Step 1: Try the new space-aware placement strategy
          windowStoreDebug("Attempting space-aware placement for", { width, height, priority, score });
          const placementStrategy = get().findMinimalMovementSet(width, height, targetPosition, score, { targetWindowId: windowId });

          // If we found a good position without movement
          if (!placementStrategy.needsMovement && placementStrategy.position) {
            // Validate position is within screen bounds
            const pos = placementStrategy.position;
            const clamped = clampPositionToScreen(pos.x, pos.y, width, height, screenWidth, screenHeight, margin);

            // Check if position was out of bounds
            if (clamped.x !== Math.floor(pos.x) || clamped.y !== Math.floor(pos.y)) {
              console.warn(`⚠️ Position clamped from (${pos.x}, ${pos.y}) to (${clamped.x}, ${clamped.y}) to fit in screen`);
            }

            windowStoreDebug("Found free space at:", clamped);
            return clamped;
          }

          // Step 2: One deterministic distance-ordered scan of the whole screen,
          // anchored on the plan's target box (or the group-aware target).
          // Replaces the old target-box / expanding-radius / row-major scans and
          // the randomized localized search.
          // A placement plan may nominate blockers, but ordinary opening must
          // never move existing windows behind the user's back. The explicit
          // tidyWorkspace action is the only path that executes those moves.
          const scanQuadtree = quadtree;
          if (!scanQuadtree) {
            console.error("❌ Quadtree not available for placement scan");
            return null;
          }

          const scanPreferred = placementStrategy.targetBoundingBox
            ? clampPositionToScreen(
                placementStrategy.targetBoundingBox.x,
                placementStrategy.targetBoundingBox.y,
                width,
                height,
                screenWidth,
                screenHeight,
                margin
              )
            : targetTopLeft;

          const scannedPosition = findFirstAvailablePositionInQuadtree(
            width,
            height,
            screenWidth,
            screenHeight,
            scanQuadtree,
            scanPreferred
          );
          if (scannedPosition) {
            windowStoreDebug("✅ Found position via distance-ordered scan:", scannedPosition);
            return scannedPosition;
          }

          // Opening a window never minimizes or moves windows the user already
          // has open. When no collision-free rect of the requested size exists,
          // the caller's saturated-workspace fallback fits the new window into
          // the largest free region or stacks it on top.
          windowStoreDebug("[window-placement] Collision-free placement exhausted; using saturated-workspace fallback");
          return null;

        } catch (error) {
          console.trace("Error finding next available position", error);
          return null;
        }
      },

      /**
       * Calculate a position within a radius around a center point.
       * Used for positioning dependency components around their parent.
       * @param centerX - X coordinate of the center (parent component center)
       * @param centerY - Y coordinate of the center (parent component center)
       * @param radius - The radius distance from center
       * @param radiusPercentMin - Minimum percentage of radius (0.9 = 90%)
       * @param radiusPercentMax - Maximum percentage of radius (1.0 = 100%)
       * @param componentWidth - Width of the component being placed
       * @param componentHeight - Height of the component being placed
       * @returns Position within the radius, clamped to screen bounds with margin
       */
      calculateDependencyPosition: (
        centerX: number,
        centerY: number,
        radius: number,
        radiusPercentMin: number,
        radiusPercentMax: number,
        componentWidth: number,
        componentHeight: number,
        options: { targetWindowId?: string } = {}
      ): { x: number; y: number } => {
        const state = get();
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
        const dimensions = getWindowDimensions(state, windowId);
        if (!dimensions) {
          return { x: centerX, y: centerY };
        }
        const { width: screenWidth, height: screenHeight } = dimensions;
        const margin = OCCUPANCY_MARGIN;

        const quadtree = getPlacementOccupancy(state, windowId);

        const candidateAt = (angle: number, distance: number): { x: number; y: number } => {
          const rawX = centerX + Math.cos(angle) * distance - componentWidth / 2;
          const rawY = centerY + Math.sin(angle) * distance - componentHeight / 2;
          return clampPositionToScreen(rawX, rawY, componentWidth, componentHeight, screenWidth, screenHeight, margin);
        };

        // Random start angle keeps the organic scatter, but sweep evenly spaced
        // angles (at a few distances) and take the first collision-free spot so
        // sibling dependencies don't stack on each other.
        const startAngle = Math.random() * 2 * Math.PI;
        const angleCount = 16;
        const midPercent = (radiusPercentMin + radiusPercentMax) / 2;
        const distances = [
          radius * radiusPercentMax,
          radius * midPercent,
          radius * radiusPercentMin,
        ];

        let fallback: { x: number; y: number } | null = null;
        for (let i = 0; i < angleCount; i++) {
          const angle = startAngle + (i / angleCount) * 2 * Math.PI;
          for (const distance of distances) {
            const candidate = candidateAt(angle, distance);
            if (!fallback) fallback = candidate;
            if (!quadtree) continue;
            if (
              isPositionAvailableInQuadtree(
                candidate,
                componentWidth,
                componentHeight,
                screenWidth,
                screenHeight,
                quadtree
              )
            ) {
              windowStoreDebug(`[DependencyPosition] Found free position: (${candidate.x}, ${candidate.y}) at angle ${(angle * 180 / Math.PI).toFixed(0)}° and distance ${distance.toFixed(0)}px`);
              return candidate;
            }
          }
        }

        // No collision-free spot around the parent - fall back to the first
        // candidate (previous behavior: place at the random start angle).
        const fallbackPosition = fallback ?? candidateAt(startAngle, radius * radiusPercentMax);
        windowStoreDebug(`[DependencyPosition] No free spot in radius, using fallback: (${fallbackPosition.x}, ${fallbackPosition.y})`);
        return fallbackPosition;
      },

      addComponentToGrid: (
        newComponents: any[],
        parentComponentId?: string,
        options: AddComponentToGridOptions = {},
      ) => {
        const addStartedAt = performance.now();
        const state = get();
        const windowId = options.targetWindowId || getCurrentWindowIdOrDefault(state);
        let quadtree = getWindowQuadtree(state, windowId);

        windowStoreDebug(`[Window ${windowId}] addComponentToGrid called:`, {
          newComponentsCount: newComponents.length,
          newComponentNames: newComponents.map(c => c.name),
          newComponentIds: newComponents.map(c => c.id),
          parentComponentId,
          hasQuadtree: !!quadtree,
          componentsByWindowKeys: Object.keys(state.componentsByWindow),
          currentWindowComponents: state.componentsByWindow[windowId]?.length || 0
        });

        if (newComponents.length === 0) {
          return;
        }

        // If quadtree doesn't exist, the window hasn't been initialized yet
        // This can happen when receiving components via cross-window transfer before Grid mounts
        // Initialize the window state now to prevent losing the component
        if (!quadtree) {
          windowStoreDebug(`[addComponentToGrid] Window ${windowId} not initialized, initializing now`);
          const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
          const height = typeof window !== 'undefined' ? window.innerHeight : 1080;

          // Also ensure componentsByWindow entry exists
          const { componentsByWindow, deletedComponentsByWindow, editHistoryByWindow } = get();
          const updates: any = {};

          if (!componentsByWindow[windowId]) {
            updates.componentsByWindow = {
              ...componentsByWindow,
              [windowId]: []
            };
          }
          if (!deletedComponentsByWindow[windowId]) {
            updates.deletedComponentsByWindow = {
              ...deletedComponentsByWindow,
              [windowId]: []
            };
          }
          if (!editHistoryByWindow[windowId]) {
            updates.editHistoryByWindow = {
              ...editHistoryByWindow,
              [windowId]: { history: [], currentIndex: -1 }
            };
          }

          if (Object.keys(updates).length > 0) {
            set(updates);
          }

          get().setDimensions(width, height);
          quadtree = getWindowQuadtree(get(), windowId);
          if (!quadtree) {
            console.error(`[addComponentToGrid] Failed to initialize window ${windowId}`);
            return;
          }
        }

        // Prevent any new UI windows from being added while signing out
        if (get().isSigningOut) {
          windowStoreDebug('Skipping addComponentToGrid because session is ending');
          return;
        }

        // Singleton windows (Settings and the like) show the open copy instead
        // of stacking another one, whichever opener minted the new id. An
        // explicit cross-window transfer moves a window and is left alone.
        const singletonPlan = options.allowCrossWindowRelocation
          ? { toAdd: newComponents, reuse: [] }
          : planSingletonWindowAdds(getWindowComponents(get(), windowId), newComponents);
        for (const { existingId, props } of singletonPlan.reuse) {
          const existing = getWindowComponents(get(), windowId).find((candidate) => candidate.id === existingId);
          if (!existing) continue;
          if (propsWouldChange(existing.props, props)) {
            get().updateComponentProps(existingId, props, { targetWindowId: windowId });
          }
          get().restoreComponent(existingId, { targetWindowId: windowId });
          get().bringComponentToFront(existingId, { targetWindowId: windowId });
        }
        newComponents = singletonPlan.toAdd;
        if (newComponents.length === 0) {
          return;
        }
        try {

          let currentComponentsList: any[] = [...getWindowComponents(get(), windowId)];
          let didReconcileJobStatus = false;
          let reconciledJobStatus: any = null;
          const initialJobStatusReconciliation = reconcileJobStatusComponents(currentComponentsList);
          if (initialJobStatusReconciliation.changed) {
            currentComponentsList = initialJobStatusReconciliation.components;
            didReconcileJobStatus = true;
            reconciledJobStatus = currentComponentsList.find(
              (component) => component.name === "JobStatus",
            );
            for (const duplicate of initialJobStatusReconciliation.removed) {
              quadtree.remove(toOccupancyRect(
                duplicate.position.x,
                duplicate.position.y,
                duplicate.size.width,
                duplicate.size.height,
              ));
            }
          }
          windowStoreDebug(`[Window ${windowId}] addComponentToGrid`, currentComponentsList.length, newComponents.length, "parentComponentId:", parentComponentId)

          // === DEPENDENCY HANDLING ===
          // If a parentComponentId is provided, find the parent component for dependency positioning
          let parentComponent: any = null;
          let parentCenter: { x: number; y: number } | null = null;
          let dependencyRadius: number = 0;
          let parentZIndex: number = 10; // Default z-index

          if (parentComponentId) {
            parentComponent = currentComponentsList.find(c => c.id === parentComponentId);
            if (parentComponent) {
              // Calculate parent's center position
              parentCenter = {
                x: parentComponent.position.x + parentComponent.size.width / 2,
                y: parentComponent.position.y + parentComponent.size.height / 2
              };
              // Radius is 100% of the parent's width
              dependencyRadius = parentComponent.size.width * 0.5;
              // Get parent's z-index (default to 10 if not set)
              parentZIndex = parentComponent.zIndex ?? 10;

              windowStoreDebug(`[Dependency] Parent component found: ${parentComponent.name} (${parentComponentId})`);
              windowStoreDebug(`[Dependency] Parent center: (${parentCenter.x}, ${parentCenter.y}), radius: ${dependencyRadius}, zIndex: ${parentZIndex}`);
            } else {
              console.warn(`[Dependency] Parent component not found: ${parentComponentId}`);
            }
          }

          // Check which "forced to close" types are coming in the new components
          const incomingForcedTypes = new Set<string>();

          // Remove existing components that match the incoming forced types
          if (incomingForcedTypes.size > 0) {
            const existingComponentsToRemove = currentComponentsList.filter(existingComp => {
              // 🛡️ Never remove protected components
              if (PROTECTED_COMPONENTS.has(existingComp.name)) {
                return false;
              }
              return Array.from(incomingForcedTypes).some(forcedType =>
                existingComp.name.includes(forcedType)
              );
            });

            if (existingComponentsToRemove.length > 0) {
              windowStoreDebug('Removing existing components:', existingComponentsToRemove.map(c => c.name));
              get().removeComponent(existingComponentsToRemove);
              currentComponentsList = [...getWindowComponents(get(), windowId)];
            }
          }

          // Track new dependency IDs to add to parent
          const newDependencyIds: string[] = [];

          for (const incomingComponent of growingRestoresFirst(newComponents, Boolean(get().isMobileLayout))) {
            if (!incomingComponent) {
              console.warn('[addComponentToGrid] Skipping null/undefined component');
              continue;
            }
            const component = reopenRetiredSystemTuck(incomingComponent);
            windowStoreDebug('addComponentToGrid component', component)
            const { priority, size: requestedSize, name, group, id, reason, score, createdDate } = component;
            if (!id || !name || !hasValidComponentSize(requestedSize)) {
              console.warn('[addComponentToGrid] Skipping incomplete component config', {
                id,
                name,
                hasSize: !!requestedSize,
                size: requestedSize,
              });
              continue;
            }
            const size = component.isMinimized
              ? requestedSize
              : fitSizeToWorkspace(get(), requestedSize, getWindowDimensions(get(), windowId));

            if (name === "JobStatus") {
              const jobStatusReconciliation = reconcileJobStatusComponents(
                currentComponentsList,
                component,
              );
              if (jobStatusReconciliation.reusedExisting) {
                currentComponentsList = jobStatusReconciliation.components;
                didReconcileJobStatus = didReconcileJobStatus || jobStatusReconciliation.changed;
                reconciledJobStatus = currentComponentsList.find(
                  (candidate) => candidate.name === "JobStatus",
                );
                if (options.allowCrossWindowRelocation) {
                  const sourceCopies = Object.entries(get().componentsByWindow)
                    .flatMap(([candidateWindowId, windowComponents]) => (
                      candidateWindowId === windowId
                        ? []
                        : (windowComponents || [])
                          .filter((candidate: any) => candidate.id === id)
                          .map((candidate: any) => ({
                            windowId: candidateWindowId,
                            component: candidate,
                          }))
                    ));
                  if (sourceCopies.length > 0) {
                    set((state) => {
                      const nextComponentsByWindow = { ...state.componentsByWindow };
                      for (const sourceCopy of sourceCopies) {
                        nextComponentsByWindow[sourceCopy.windowId] = (
                          nextComponentsByWindow[sourceCopy.windowId] || []
                        ).filter((candidate: any) => candidate.id !== id);
                      }
                      return { componentsByWindow: nextComponentsByWindow };
                    });
                    for (const sourceCopy of sourceCopies) {
                      const sourceQuadtree = getWindowQuadtree(get(), sourceCopy.windowId);
                      if (sourceQuadtree && !sourceCopy.component.isMinimized) {
                        sourceQuadtree.remove(toOccupancyRect(
                          sourceCopy.component.position.x,
                          sourceCopy.component.position.y,
                          sourceCopy.component.size.width,
                          sourceCopy.component.size.height,
                        ));
                      }
                    }
                  }
                }
                for (const duplicate of jobStatusReconciliation.removed) {
                  quadtree.remove(toOccupancyRect(
                    duplicate.position.x,
                    duplicate.position.y,
                    duplicate.size.width,
                    duplicate.size.height,
                  ));
                }
                windowStoreDebug(
                  `[addComponentToGrid] Reusing the existing JobStatus component for ${id}`,
                );
                continue;
              }
            }

            // Skip if component already exists in the current window (prevent duplicates in window state)
            if (currentComponentsList.some((comp) => comp.id === id)) {
              windowStoreDebug(`[addComponentToGrid] Skipping component ${id} (${name}) - already exists in window`);
              continue;
            }

            // Protected shell components are singletons by product contract.
            // Saved layouts can contain legacy copies with different ids, so
            // id-only deduplication is not sufficient during hydration.
            if (
              PROTECTED_COMPONENTS.has(name) &&
              currentComponentsList.some((comp) => comp.name === name)
            ) {
              windowStoreDebug(`[addComponentToGrid] Skipping duplicate protected component ${name} (${id})`);
              continue;
            }

            const protectedCopyInAnotherWindow = PROTECTED_COMPONENTS.has(name)
              ? Object.entries(get().componentsByWindow).find(([candidateWindowId, windowComponents]) =>
                  candidateWindowId !== windowId
                  && (windowComponents || []).some((comp: any) => comp.name === name)
                )
              : undefined;
            if (protectedCopyInAnotherWindow) {
              const transferableCopy = options.allowCrossWindowRelocation
                && protectedCopyInAnotherWindow[1].some((comp: any) => comp.id === id);
              if (!transferableCopy) {
                windowStoreDebug(
                  `[addComponentToGrid] Skipping duplicate protected component ${name} (${id}) in another window`,
                );
                continue;
              }
            }

            // Skip if component already exists in ANY window unless this add is an
            // explicit cross-window transfer. During transfers, newly opened
            // renderers can still have a persisted copy under the source window;
            // relocate that copy locally before adding to the target window.
            const duplicateEntries = Object.entries(get().componentsByWindow)
              .flatMap(([duplicateWindowId, windowComponents]: [string, any]) =>
                (windowComponents || [])
                  .filter((comp: any) => comp.id === id)
                  .map((comp: any) => ({ windowId: duplicateWindowId, component: comp }))
              )
              .filter((entry) => entry.windowId !== windowId);

            if (duplicateEntries.length > 0) {
              if (!options.allowCrossWindowRelocation) {
                windowStoreDebug(`[addComponentToGrid] Skipping component ${id} (${name}) - already exists in another window`);
                continue;
              }

              const sourceWindowIds = duplicateEntries.map((entry) => entry.windowId);
              windowStoreDebug(`[addComponentToGrid] Relocating transferred component ${id} (${name}) from ${sourceWindowIds.join(', ')} to ${windowId}`);

              set((state) => {
                const nextComponentsByWindow = { ...state.componentsByWindow };
                for (const sourceWindowId of sourceWindowIds) {
                  nextComponentsByWindow[sourceWindowId] = (nextComponentsByWindow[sourceWindowId] || [])
                    .filter((comp: any) => comp.id !== id);
                }
                return { componentsByWindow: nextComponentsByWindow };
              });

              for (const duplicate of duplicateEntries) {
                const sourceQuadtree = getWindowQuadtree(get(), duplicate.windowId);
                if (sourceQuadtree && !duplicate.component.isMinimized) {
                  sourceQuadtree.remove(toOccupancyRect(
                    duplicate.component.position.x,
                    duplicate.component.position.y,
                    duplicate.component.size.width,
                    duplicate.component.size.height,
                  ));
                }
              }

              currentComponentsList = [...getWindowComponents(get(), windowId)];
            }

            // Skip if a dependency component with the same name and parent already exists
            // re-creates its children on mount while persisted copies already exist from session restore
            if (parentComponentId) {
              const duplicateDependency = currentComponentsList.find((comp: any) =>
                comp.name === name && comp.parentComponentId === parentComponentId
              );
              if (duplicateDependency) {
                windowStoreDebug(`[addComponentToGrid] Skipping dependency ${name} (${id}) - duplicate already exists as ${duplicateDependency.id} under parent ${parentComponentId}`);
                continue;
              }
            }

            let position: { x: number; y: number } | null = null;
            let componentZIndex: number | undefined = undefined;

            // === DEPENDENCY POSITIONING ===
            // If this is a dependency (has a parent), calculate position within the radius
            if (parentComponent && parentCenter) {
              const dimensions = getWindowDimensions(get(), windowId);
              const currentQuadtree = getPlacementOccupancy(get(), windowId, id, currentComponentsList);
              if (dimensions && component.dependencyPlacement) {
                const gap = 28;
                const alignedY = component.dependencyPlacement === "right-bottom"
                  ? parentComponent.position.y + parentComponent.size.height - size.height
                  : parentComponent.position.y;
                const preferredPositions = [
                  { x: parentComponent.position.x + parentComponent.size.width + gap, y: alignedY },
                  { x: parentComponent.position.x - size.width - gap, y: alignedY },
                  { x: parentComponent.position.x, y: parentComponent.position.y + parentComponent.size.height + gap },
                ].map((candidate) => clampPositionToScreen(
                  candidate.x,
                  candidate.y,
                  size.width,
                  size.height,
                  dimensions.width,
                  dimensions.height,
                ));

                // The occupancy holds every conversation's area (growth
                // reservations), so a free slot is off the conversation. With
                // every slot taken, the first one is only where the search
                // starts: the footprint check after the position search moves
                // it to the nearest free spot, or into the saturated fallback
                // that reserves the conversation.
                position = preferredPositions.find((candidate) => (
                  !currentQuadtree || isPositionAvailableInQuadtree(
                    candidate,
                    size.width,
                    size.height,
                    dimensions.width,
                    dimensions.height,
                    currentQuadtree,
                  )
                )) || preferredPositions[0];
              }

              position ||= get().calculateDependencyPosition(
                parentCenter.x,
                parentCenter.y,
                dependencyRadius,
                0.9, // 90% of radius minimum
                1.0, // 100% of radius maximum
                size.width,
                size.height,
                { targetWindowId: windowId },
              );
              // Set z-index to one less than parent
              componentZIndex = parentZIndex - 1;
              // Track this as a dependency
              newDependencyIds.push(id);

              windowStoreDebug(`[Dependency] Positioning ${name} at (${position.x}, ${position.y}) with zIndex: ${componentZIndex}`);
            } else {
              // Restored/specified components already have coordinates. Trust them
              // instead of running the expensive free-space scanner for every item.
              if (hasValidPosition(component.position)) {
                const dimensions = getWindowDimensions(get(), windowId);
                const currentQuadtree = getPlacementOccupancy(get(), windowId, id, currentComponentsList);
                const isMobilePlacement = Boolean(get().isMobileLayout);
                // A window keeps its saved spot when its whole footprint fits
                // there. Otherwise it takes the nearest spot that fits: an
                // edge-saved conversation moves only as far as its open
                // transcript needs, the way the window is repaired once it grows.
                if (dimensions && currentQuadtree && !component.isMinimized) {
                  position = findFirstFootprintPosition(
                    { name, size },
                    dimensions,
                    currentQuadtree,
                    isMobilePlacement,
                    component.position,
                  );
                  if (!position || position.x !== Math.floor(component.position.x) || position.y !== Math.floor(component.position.y)) {
                    windowStoreDebug(`[addComponentToGrid] Provided position for ${name} does not fit, moving to the nearest free position`, {
                      requested: component.position,
                      placed: position,
                    });
                  }
                } else {
                  position = dimensions
                    ? clampFootprintIntoWorkspace({ name, size }, component.position, dimensions, isMobilePlacement, OCCUPANCY_MARGIN)
                    : { x: Math.floor(component.position.x), y: Math.floor(component.position.y) };
                }
              } else {
                // Standard positioning (no parent). A window that grows on its
                // own is placed with room for its full footprint.
                const dimensions = getWindowDimensions(get(), windowId);
                const footprint = dimensions
                  ? placementFootprint({ name, size }, dimensions, Boolean(get().isMobileLayout))
                  : size;
                position = get().findNextAvailablePosition(footprint.width, footprint.height, priority, group, score, { targetWindowId: windowId });
                windowStoreDebug("🎯 Position from placement strategy:", position)
              }
            }

            // Refresh the component list after the position search so state
            // written meanwhile (JobStatus reconciliation, transfers) is seen,
            // while preserving components already added in this batch (not yet
            // committed to the store).
            const currentWindowComponents = getWindowComponents(get(), windowId);
            const previouslyAddedInBatch = currentComponentsList.filter(c =>
              !currentWindowComponents.some(existing => existing.id === c.id)
            );
            currentComponentsList = [...currentWindowComponents, ...previouslyAddedInBatch];
            if (reconciledJobStatus) {
              const refreshedJobStatusReconciliation = reconcileJobStatusComponents(
                currentComponentsList,
                reconciledJobStatus,
              );
              currentComponentsList = refreshedJobStatusReconciliation.components;
              reconciledJobStatus = currentComponentsList.find(
                (candidate) => candidate.name === "JobStatus",
              );
              for (const duplicate of refreshedJobStatusReconciliation.removed) {
                quadtree.remove(toOccupancyRect(
                  duplicate.position.x,
                  duplicate.position.y,
                  duplicate.size.width,
                  duplicate.size.height,
                ));
              }
            }
            windowStoreDebug("Refreshed currentComponentsList after position search, count:", currentComponentsList.length, "preserved:", previouslyAddedInBatch.length)

            const dimensions = getWindowDimensions(get(), windowId);
            const currentQuadtree = getPlacementOccupancy(get(), windowId, id, currentComponentsList);
            const placementIsMobile = Boolean(get().isMobileLayout);
            if (
              position &&
              dimensions &&
              currentQuadtree &&
              !component.isMinimized &&
              !footprintFitsAt({ name, size }, position, dimensions, currentQuadtree, placementIsMobile)
            ) {
              windowStoreDebug(`[addComponentToGrid] Calculated position for ${name} is occupied, scanning live quadtree`, position);
              position = findFirstFootprintPosition(
                { name, size },
                dimensions,
                currentQuadtree,
                placementIsMobile,
                position,
              );
            }

            let placedSize = size;
            if (!position) {
              if (dimensions) {
                const preferredPosition = hasValidPosition(component.position)
                  ? component.position
                  : {
                      x: (dimensions.width - size.width) / 2,
                      y: (dimensions.height - size.height) / 2,
                    };
                const collisionFreePosition = currentQuadtree
                  ? findFirstFootprintPosition(
                    { name, size },
                    dimensions,
                    currentQuadtree,
                    placementIsMobile,
                    preferredPosition,
                  )
                  : null;
                if (collisionFreePosition) {
                  position = collisionFreePosition;
                } else {
                  // Saturated workspace: existing windows keep their position,
                  // size, and visibility. Only the new window adapts. A fresh
                  // workspace window may open slightly smaller to fit a free
                  // region; prompts, restored geometry, and dependency windows
                  // keep their size and stack above what they cover.
                  const { isMobileLayout: isMobilePlacement } = get();
                  const visibleWindows = savedComponents(currentComponentsList.filter(
                    (candidate) => blocksPlacement(candidate) && candidate.id !== id,
                  ), growthHolds(get().heldComponentHeights));
                  const saturated = placeInSaturatedWorkspace({
                    requestedSize: size,
                    screen: dimensions,
                    obstacles: visibleWindows.map((candidate) => {
                      const geometry = getLayoutGeometry(candidate, isMobilePlacement);
                      const footprint = placementFootprint(
                        { name: candidate.name, size: geometry.size, position: geometry.position },
                        dimensions,
                        isMobilePlacement,
                      );
                      return {
                        x: geometry.position.x,
                        y: geometry.position.y,
                        width: footprint.width,
                        height: footprint.height,
                        overlapWeight: isAttentionPrompt(candidate)
                          ? ATTENTION_PROMPT_OVERLAP_WEIGHT
                          : 1,
                      };
                    }),
                    // The conversation's transcript and composer are
                    // reserved like chrome: the new window covers other
                    // windows, or opens smaller, before it covers them.
                    reserved: [
                      ...getChromeZones(get(), dimensions),
                      ...conversationKeepOuts(get(), dimensions, currentComponentsList, new Set([id])),
                    ],
                    fitAroundReserved: !isMobilePlacement && !component.isMinimized && !growsOnItsOwn({ name }, isMobilePlacement),
                    preferred: preferredPosition,
                    margin: OCCUPANCY_MARGIN,
                    allowShrink:
                      !isMobilePlacement &&
                      !component.isMinimized &&
                      !hasValidPosition(component.position) &&
                      !parentComponentId &&
                      !component.parentComponentId &&
                      !EPHEMERAL_COMPONENTS.has(name),
                  });
                  position = saturated.position;
                  placedSize = saturated.size;
                  if (saturated.overlapsExisting && componentZIndex === undefined) {
                    // The newest window is the one the person asked for, so it
                    // opens above every window it covers. Visible attention
                    // prompts are lifted back above it when the grid commits.
                    componentZIndex = visibleWindows.reduce(
                      (highest, candidate) =>
                        Math.max(highest, getEffectiveComponentZIndex(candidate)),
                      getEffectiveComponentZIndex(component),
                    ) + 1;
                  }
                  windowStoreDebug(
                    `[addComponentToGrid] Viewport saturated; placing ${name}`,
                    saturated,
                  );
                }
              } else {
                position = { x: 10, y: 10 };
              }
            }

          // Determine effective position based on the validated saved position or
          // calculated fallback above.
          const effectivePosition = position;

          // Check if we're in mobile layout mode to set appropriate position/size fields
          const { isMobileLayout } = get();

          const newComponent = {
            id,
            // Desktop position/size - always set (either from component or calculated)
            position: isMobileLayout
              ? (component.position || effectivePosition) // Use provided desktop position or fallback to calculated
              : effectivePosition,
            size: { ...placedSize },
            priority,
            name,
            group,
            props: { ...component.props },
            score,
            reason,
            createdDate,
            deletedDate: "",
            // Dependency tracking fields
            parentComponentId: parentComponentId || component.parentComponentId || undefined,
            dependencyIds: component.dependencyIds,
            zIndex: componentZIndex ?? component.zIndex,
            relationVisual: component.relationVisual,
            dependencyPlacement: component.dependencyPlacement,
            family: component.family,
            dockGroup: component.dockGroup,
            isDependenciesSpread: component.isDependenciesSpread,
            isSpreadDependency: component.isSpreadDependency,
            originalDependencyPosition: component.originalDependencyPosition,
            // Preserve minimized state from saved preferences
            isMinimized: component.isMinimized || false,
            minimizedScale: component.minimizedScale,
            isSystemTuckedAway: component.isMinimized
              ? component.isSystemTuckedAway
              : undefined,
            // Mobile position/size - set when in mobile mode or preserved from component
            mobilePosition: isMobileLayout
              ? effectivePosition
              : (component.mobilePosition || undefined),
            mobileSize: isMobileLayout
              ? { ...placedSize }
              : (component.mobileSize || undefined),
          };

          // Only add to quadtree if component is not minimized (minimized components don't occupy space)
          if (!newComponent.isMinimized) {
            // Use effective position/size for quadtree based on layout mode
            const qtPosition = isMobileLayout && newComponent.mobilePosition
              ? newComponent.mobilePosition
              : newComponent.position;
            const qtSize = isMobileLayout && newComponent.mobileSize
              ? newComponent.mobileSize
              : newComponent.size;

            const rect = toOccupancyRect(
              qtPosition.x,
              qtPosition.y,
              qtSize.width,
              qtSize.height
            );

            // Insert into current window's quadtree
            const currentQuadtree = getWindowQuadtree(get(), windowId);
            if (currentQuadtree) {
              currentQuadtree.insert(rect);
            }
          }

          currentComponentsList.push(newComponent);
            windowStoreDebug("currentComponentsList", currentComponentsList)
          }

          // === UPDATE PARENT'S DEPENDENCY IDS ===
          // If we added dependencies, update the parent component's dependencyIds array
          if (parentComponentId && newDependencyIds.length > 0) {
            currentComponentsList = currentComponentsList.map(comp => {
              if (comp.id === parentComponentId) {
                const existingDependencies = comp.dependencyIds || [];
                const updatedDependencies = [...existingDependencies, ...newDependencyIds];
                windowStoreDebug(`[Dependency] Updating parent ${comp.name} dependencyIds:`, updatedDependencies);
                return {
                  ...comp,
                  dependencyIds: updatedDependencies
                };
              }
              return comp;
            });
          }

          // Add new components to state (per-window only)
          let didMutateGrid = false;
          set((state) => {
            const existingComponents = getWindowComponents(state, windowId);
            const existingIds = new Set(existingComponents.map(c => c.id));
            const brandNewComponents = currentComponentsList.filter(c => !existingIds.has(c.id));

            // Also include updated parent component if it was modified
            let updatedExistingComponents = existingComponents;
            if (didReconcileJobStatus) {
              const canonicalJobStatus = currentComponentsList.find(
                (component) => component.name === "JobStatus",
              );
              updatedExistingComponents = existingComponents
                .filter((component) => (
                  component.name !== "JobStatus" || component.id === canonicalJobStatus?.id
                ))
                .map((component) => (
                  component.name === "JobStatus" && component.id === canonicalJobStatus?.id
                    ? canonicalJobStatus
                    : component
                ));
            }
            if (parentComponentId && newDependencyIds.length > 0) {
              updatedExistingComponents = updatedExistingComponents.map(comp => {
                if (comp.id === parentComponentId) {
                  const existingDependencies = comp.dependencyIds || [];
                  return {
                    ...comp,
                    dependencyIds: [...existingDependencies, ...newDependencyIds]
                  };
                }
                return comp;
              });
            }

            if (
              brandNewComponents.length === 0
              && newDependencyIds.length === 0
              && !didReconcileJobStatus
            ) {
              return state; // No new components to add and no parent to update
            }

            didMutateGrid = true;

            windowStoreDebug(`[Window ${windowId}] Adding ${brandNewComponents.length} new component(s):`, brandNewComponents.map(c => c.name));
            if (newDependencyIds.length > 0) {
              windowStoreDebug(`[Window ${windowId}] New dependencies added to parent ${parentComponentId}:`, newDependencyIds);
            }

            // An opened window never stacks above a visible attention prompt.
            const newComponents = keepAttentionPromptsOnTop([
              ...updatedExistingComponents,
              ...brandNewComponents,
            ]);
            const brandNewIds = new Set(brandNewComponents.map((component) => component.id));

            return {
              componentsByWindow: {
                ...state.componentsByWindow,
                [windowId]: newComponents
              },
              deletedComponentsByWindow: brandNewIds.size > 0
                ? Object.fromEntries(
                    Object.entries(state.deletedComponentsByWindow).map(
                      ([deletedWindowId, deletedComponents]) => [
                        deletedWindowId,
                        deletedComponents.filter(
                          (component) => !brandNewIds.has(component.id),
                        ),
                      ],
                    ),
                  )
                : state.deletedComponentsByWindow,
            };
          });

          if (!didMutateGrid) {
            return;
          }

          // Persist to backend (use all components across all windows)
          const allComponents = get().getAllComponents();
          const newUserBeforeUpdate = { ...get().userSession };
          newUserBeforeUpdate.preferences.componentsMap = allComponents;
          get().setUserSession(newUserBeforeUpdate);
          broadcastComponentsByWindowSync(get().componentsByWindow);
          const isAddingOnlyTransientStatus = newComponents.every((comp) =>
            comp.name === 'InactivityWarning' || EPHEMERAL_COMPONENTS.has(comp.name)
          );
          if (!isAddingOnlyTransientStatus) {
            updatePreferences({
              ...newUserBeforeUpdate.preferences,
              componentsMap: newUserBeforeUpdate.preferences.componentsMap
            });
          }
        } catch (error) {
          console.error("Error adding components to grid", error);
        }
      },

      removeComponent: (
        componentsArg: any,
        softDelete: boolean = false,
        options: RemoveComponentOptions = {},
      ) => {
        const state = get();
        const currentWindowId = getCurrentWindowIdOrDefault(state);
        const effectiveOptions: RemoveComponentOptions = isLandingDemoStoreIsolationActive()
          ? {
              ...options,
              skipPersistence: true,
              skipSync: true,
              trackDeleted: false,
            }
          : options;
        const shouldTrackDeleted = effectiveOptions.trackDeleted !== false;

        if (componentsArg.length === 0) {
          return;
        }

        // A rendered component can legitimately exist before the placement
        // quadtree is initialized (notably in the public landing workspace).
        // Removal is a canonical state operation, so a missing occupancy index
        // must not make the window controls inert.

        // Filter out empty strings and invalid values
        const validComponentsArg = componentsArg.filter((c: any) => {
          if (typeof c === 'string' && c.trim() === '') {
            console.warn('⚠️ removeComponent called with empty string, skipping');
            return false;
          }
          if (!c && c !== 0) {
            console.warn('⚠️ removeComponent called with null/undefined value, skipping');
            return false;
          }
          return true;
        });

        if (validComponentsArg.length === 0) {
          console.warn('⚠️ No valid components to remove after filtering');
          return;
        }

        // Restored and detached components may be rendered from a window other
        // than the caller's current renderer. Resolve the owning window before
        // removing so in-component controls never become inert after a restore
        // or Electron detach operation.
        const candidateWindowIds = effectiveOptions.targetWindowId
          ? [effectiveOptions.targetWindowId]
          : [
              currentWindowId,
              ...Object.keys(state.componentsByWindow).filter((id) => id !== currentWindowId),
            ];
        const matchingWindowIds = candidateWindowIds.filter((candidateWindowId) =>
          getWindowComponents(state, candidateWindowId).some((component) =>
            validComponentsArg.some((target: any) =>
              component.id === target ||
              component.name === target ||
              (
                target &&
                typeof target === 'object' &&
                (component.id === target.id || component.name === target.name)
              )
            )
          )
        );

        const shouldDismissNotifications = (removed: readonly componentsMapType[]) => {
          const session = get().userSession;
          return !softDelete && shouldTrackDeleted && !effectiveOptions.skipPersistence
            && !get().isSigningOut && session.isAuthorized === true && Boolean(session.userId) && session.userId !== "demo"
            && removed.some((component) => component.name === "Notifications");
        };

        if (!effectiveOptions.targetWindowId && matchingWindowIds.length > 1) {
          const before = matchingWindowIds.flatMap((windowId) => getWindowComponents(state, windowId).map((component) => ({ windowId, component })));
          for (const targetWindowId of matchingWindowIds) {
            get().removeComponent(validComponentsArg, softDelete, {
              ...effectiveOptions,
              targetWindowId,
              skipPersistence: true,
              skipSync: true,
            });
          }

          const closedAt = new Date();
          const removed = Array.from(new Map(before
            .filter(({ windowId, component }) => !getWindowComponents(get(), windowId).some((current) => current.id === component.id))
            .map(({ component }) => [component.id, { ...component, deletedDate: closedAt }])).values());
          const dismissNotificationWindow = shouldDismissNotifications(removed);
          const allComponents = get().getAllComponents();
          const session = get().userSession;
          const nextUser = { ...session, preferences: { ...session.preferences, componentsMap: allComponents,
            ...(dismissNotificationWindow ? { notificationWindowDismissed: true } : {}),
          } };
          get().setUserSession(nextUser);
          if (!effectiveOptions.skipSync) {
            broadcastComponentsByWindowSync(get().componentsByWindow);
          }
          if (!effectiveOptions.skipPersistence) {
            updatePreferences({
              ...nextUser.preferences,
              componentsMap: allComponents,
            }, { allowComponentRemoval: true, dismissNotificationWindow, closedComponents: removed });
          }
          return;
        }

        const windowId = matchingWindowIds[0] || currentWindowId;
        const quadtree = getWindowQuadtree(state, windowId);

        const currentComponentsList = [...getWindowComponents(state, windowId)];
        const directlyMatchedComponents = currentComponentsList.filter((currComp) =>
          validComponentsArg.some((c: any) =>
            currComp.id === c ||
            currComp.name === c ||
            (c && typeof c === 'object' && (currComp.id === c.id || currComp.name === c.name))
          )
        );

        if (directlyMatchedComponents.length === 0) {
          return;
        }

        const compToDelete: any = [];

        if (softDelete) {
          // 🛡️ Prevent minimization of protected components
          const protectedComponentsToMinimize = validComponentsArg.filter((c: any) => {
            const component = currentComponentsList.find((comp) =>
              comp.id === c || comp.name === c || (c && typeof c === 'object' && comp.id === c.id)
            );
            if (component && PROTECTED_COMPONENTS.has(component.name)) {
              console.warn(`🛡️ Cannot minimize protected component: ${component.name}`);
              return false;
            }
            return true;
          });

          if (protectedComponentsToMinimize.length === 0) {
            console.warn('⚠️ All components to minimize are protected. No action taken.');
            return;
          }

          const dockMemberIdsToMinimize = new Set<string>();
          protectedComponentsToMinimize.forEach((target: any) => {
            const component = currentComponentsList.find((candidate) =>
              candidate.id === target ||
              candidate.name === target ||
              (target && typeof target === "object" && candidate.id === target.id)
            );
            if (!component) return;
            getDockGroupMembers(currentComponentsList, component.id)
              .filter((member) => !PROTECTED_COMPONENTS.has(member.name))
              .forEach((member) => dockMemberIdsToMinimize.add(member.id));
          });

          // 🔗 Collect dependency IDs from parent components being minimized
          const dependencyIdsToMinimize = new Set<string>();
          currentComponentsList
            .filter((component) => dockMemberIdsToMinimize.has(component.id))
            .forEach((component) => {
            if (component?.dependencyIds && component.dependencyIds.length > 0) {
              windowStoreDebug(`🔗 Parent ${component.name} has ${component.dependencyIds.length} dependencies to minimize`);
              component.dependencyIds.forEach((depId: string) => dependencyIdsToMinimize.add(depId));
            }
          });

          // Soft delete: keep in components but mark as minimized
          let minimizedCount = 0;
          windowStoreDebug('[softDelete] Starting minimize pass. protectedComponentsToMinimize:',
            protectedComponentsToMinimize.map((c: any) => typeof c === 'object' ? { id: c.id, name: c.name } : c));
          windowStoreDebug('[softDelete] currentComponentsList:',
            currentComponentsList.map(c => ({ name: c.name, id: c.id, isMinimized: c.isMinimized })));
          windowStoreDebug('[softDelete] dependencyIdsToMinimize:', Array.from(dependencyIdsToMinimize));

          const updatedComponentsList = currentComponentsList.map((currComp) => {
            // Check if this component should be minimized directly
            const shouldMinimize =
              dockMemberIdsToMinimize.has(currComp.id) ||
              protectedComponentsToMinimize.some((c: any) => {
              const matchesId = currComp.id === c;
              const matchesName = currComp.name === c;
              const matchesObjId = c && typeof c === 'object' && currComp.id === c.id;
              if (!matchesId && !matchesName && !matchesObjId) {
                windowStoreDebug(`[softDelete] No match for "${currComp.name}" (id: ${currComp.id}) against arg:`,
                  typeof c === 'object' ? { id: c.id, name: c.name } : c,
                  { matchesId, matchesName, matchesObjId, argType: typeof c });
              }
              return matchesId || matchesName || matchesObjId;
              });

            // 🔗 Also check if this is a dependency of a parent being minimized
            const isDependencyOfMinimizedParent = dependencyIdsToMinimize.has(currComp.id);

            if (!shouldMinimize && !isDependencyOfMinimizedParent) {
              windowStoreDebug(`[softDelete] ❌ NOT minimizing "${currComp.name}" (id: ${currComp.id}) - no match found, isMinimized: ${currComp.isMinimized}`);
            }

            if (shouldMinimize || isDependencyOfMinimizedParent) {
              // Don't minimize protected components even if they're dependencies
              if (PROTECTED_COMPONENTS.has(currComp.name)) {
                console.warn(`🛡️ Skipping protected dependency: ${currComp.name}`);
                return currComp;
              }

              minimizedCount++;
              const reason = isDependencyOfMinimizedParent && !shouldMinimize
                ? '(dependency of minimized parent)'
                : '';
              windowStoreDebug(`✓ Minimizing component: ${currComp.name} id: ${currComp.id} ${reason}`)
              // Remove from quadtree but keep in grid
              const { position, size } = currComp;
              const rectToRemove = toOccupancyRect(position.x, position.y, size.width, size.height);
              quadtree?.remove(rectToRemove);
              // Keep tuck-away deterministic so the dock is learnable and the
              // same component can always be restored by voice or touch.
              const minimizedScale = 0.32;
              const minimizedComponent = {
                ...currComp,
                isMinimized: true,
                minimizedScale,
                // A person's minimize replaces any legacy system tuck, so the
                // window stays minimized when the workspace loads again.
                isSystemTuckedAway: undefined,
                deletedDate: new Date(),
              };
              compToDelete.push(minimizedComponent);
              return {
                ...minimizedComponent,
              };
            }
            return currComp;
          });

          if (minimizedCount === 0) {
            console.warn('⚠️ No components were minimized. Looking for:', protectedComponentsToMinimize.map((c: any) => typeof c === 'object' ? { id: c.id, name: c.name } : c));
            console.warn('   Available components:', currentComponentsList.map(c => ({ name: c.name, id: c.id, isMinimized: c.isMinimized })));
          } else {
            windowStoreDebug(`[softDelete] ✅ Successfully minimized ${minimizedCount} component(s)`);
          }

          windowStoreDebug(`[Window ${windowId}] Updated components with minimized flags`, updatedComponentsList)

          // Update per-window state
          set((state) => ({
            componentsByWindow: {
              ...state.componentsByWindow,
              [windowId]: updatedComponentsList
            },
            deletedComponentsByWindow: {
              ...state.deletedComponentsByWindow,
              [windowId]: shouldTrackDeleted
                ? [...getWindowDeletedComponents(state, windowId), ...compToDelete]
                : getWindowDeletedComponents(state, windowId)
            }
          }));

          // Rebuild from canonical component state so minimized components cannot
          // leave stale occupancy rectangles behind in the active placement layer.
          get().rebuildQuadtree();

          // Persist to backend
          const allComponents = get().getAllComponents();
          const newUser = { ...get().userSession };
          newUser.preferences.componentsMap = allComponents;
          get().setUserSession(newUser);
          if (!effectiveOptions.skipSync) {
            broadcastComponentsByWindowSync(get().componentsByWindow);
          }
          if (!effectiveOptions.skipPersistence) {
            updatePreferences({
              ...newUser.preferences,
              componentsMap: allComponents
            }, { allowComponentRemoval: true });
          }
        } else {
          // Hard delete: completely remove from components
          const protectedComponentsToKeep = effectiveOptions.allowProtectedRemoval
            ? []
            : directlyMatchedComponents.filter((component) =>
                PROTECTED_COMPONENTS.has(component.name)
              );
          if (protectedComponentsToKeep.length > 0) {
            console.warn(
              "[removeComponent] Refusing to delete protected component(s):",
              protectedComponentsToKeep.map((component) => component.name)
            );
          }

          const matchesRemoveTarget = (component: componentsMapType, target: any) =>
            component.id === target ||
            component.name === target ||
            (
              target &&
              typeof target === 'object' &&
              (component.id === target.id || component.name === target.name)
            );

          const componentsToDeleteArg = validComponentsArg.filter((c: any) => {
            const component = currentComponentsList.find((comp) =>
              matchesRemoveTarget(comp, c)
            );
            return !component || effectiveOptions.allowProtectedRemoval || !PROTECTED_COMPONENTS.has(component.name);
          });

          if (componentsToDeleteArg.length === 0) {
            console.warn('⚠️ All components to delete are protected. No action taken.');
            return;
          }

          const targetIds = directlyMatchedComponents
            .filter(component => componentsToDeleteArg.some((target: any) => matchesRemoveTarget(component, target)))
            .map(component => component.id);
          const { remaining: newComponentsList, closed } = planWorkspaceClose(currentComponentsList, targetIds, new Date(), {
            allowProtectedRemoval: effectiveOptions.allowProtectedRemoval,
          });
          compToDelete.push(...closed);

          for (const component of compToDelete) {
            windowStoreDebug("component", component)
            const { position, size } = component;
            const rectToRemove = toOccupancyRect(position.x, position.y, size.width, size.height);
            quadtree?.remove(rectToRemove);
          }
          windowStoreDebug(`[Window ${windowId}] newComponentsList`, newComponentsList)

          // Update per-window state
          set((state) => ({
            componentsByWindow: {
              ...state.componentsByWindow,
              [windowId]: newComponentsList
            },
            deletedComponentsByWindow: {
              ...state.deletedComponentsByWindow,
              [windowId]: shouldTrackDeleted
                ? [...getWindowDeletedComponents(state, windowId), ...compToDelete]
                : getWindowDeletedComponents(state, windowId)
            },
            // An untracked removal (a window that moved to another desktop
            // window) leaves nothing here to grow back: its hold goes with it,
            // and the target saves the unfolded height it was sent with.
            ...(shouldTrackDeleted ? {} : {
              heldComponentHeights: withoutHolds(state.heldComponentHeights, compToDelete.map((component: componentsMapType) => component.id)),
            }),
          }));

          // Persist to backend
          const allComponents = get().getAllComponents();
          const session = get().userSession;
          const dismissNotificationWindow = shouldDismissNotifications(compToDelete);
          const newUser = { ...session, preferences: { ...session.preferences, componentsMap: allComponents,
            ...(dismissNotificationWindow ? { notificationWindowDismissed: true } : {}),
          } };
          get().setUserSession(newUser);
          if (!effectiveOptions.skipSync) {
            broadcastComponentsByWindowSync(get().componentsByWindow);
          }
          if (!effectiveOptions.skipPersistence) {
            // Save the close history and smaller active layout together.
            updatePreferences({
              ...newUser.preferences,
              componentsMap: allComponents
            }, { allowComponentRemoval: true, dismissNotificationWindow, closedComponents: compToDelete });
          }
        }
      },

      bringComponentToFront: (id: string, options: { targetWindowId?: string } = {}) => {
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(get());

        set((state) => {
          const currentComponents = getWindowComponents(state, windowId);
          const componentToPromote = currentComponents.find(
            (component) =>
              component.id === id || component.name === id,
          );

          if (!componentToPromote) {
            return state;
          }
          const promotedComponents = bringComponentGroupToFront(
            currentComponents,
            componentToPromote.id,
          );
          if (promotedComponents === currentComponents) {
            return state;
          }

          return {
            componentsByWindow: {
              ...state.componentsByWindow,
              [windowId]: promotedComponents,
            },
          };
        });
      },

      dockComponents: (
        contextComponentId: string,
        authoritativeComponentId: string,
        options: { id?: string; gap?: number; align?: "start" | "center" } = {},
      ) => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const currentComponents = getWindowComponents(state, windowId);
        const context = currentComponents.find(
          (component) => component.id === contextComponentId,
        );
        const authoritative = currentComponents.find(
          (component) => component.id === authoritativeComponentId,
        );
        if (!context || !authoritative || context.id === authoritative.id) {
          return false;
        }
        if (
          context.dockGroup?.peerId === authoritative.id &&
          authoritative.dockGroup?.peerId === context.id &&
          (options.gap === undefined ||
            context.dockGroup.gap === Math.max(6, Math.floor(options.gap))) &&
          dockGroupIsAdjacent(
            currentComponents,
            context.id,
            state.isMobileLayout,
          )
        ) {
          get().bringComponentToFront(context.id);
          return true;
        }

        const docked = dockComponentPair(
          currentComponents,
          context.id,
          authoritative.id,
          {
            ...options,
            dimensions: getWindowDimensions(state, windowId),
            isMobileLayout: state.isMobileLayout,
          },
        );
        if (!docked) return false;
        const dockedOnFrontLayer = bringComponentGroupToFront(
          docked,
          context.id,
        );

        set((latest) => ({
          componentsByWindow: {
            ...latest.componentsByWindow,
            [windowId]: dockedOnFrontLayer,
          },
        }));
        get().rebuildQuadtree();
        broadcastComponentsByWindowSync(get().componentsByWindow);

        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents,
        });
        return true;
      },

      undockComponent: (componentId: string) => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const currentComponents = getWindowComponents(state, windowId);
        const undocked = undockComponentGroup(
          currentComponents,
          componentId,
        );
        if (!undocked) return false;
        set((latest) => ({
          componentsByWindow: {
            ...latest.componentsByWindow,
            [windowId]: undocked,
          },
        }));
        broadcastComponentsByWindowSync(get().componentsByWindow);

        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents,
        });
        return true;
      },

      updateComponentPosition: (
        id: string,
        newPosition: { x: number; y: number },
        updateUserSession: boolean = true,
        options: GeometryChangeOptions = {},
      ) => {
        const windowId = getCurrentWindowIdOrDefault(get());
        const initialState = get();
        const currentComponents = getWindowComponents(initialState, windowId);
        const compToUpdate = currentComponents.find(
          (component) => component.id === id || component.name === id,
        );

        if (!compToUpdate) return;

        const { isMobileLayout } = initialState;
        const oldPosition = getLayoutGeometry(
          compToUpdate,
          isMobileLayout,
        ).position;
        const deltaX = newPosition.x - oldPosition.x;
        const deltaY = newPosition.y - oldPosition.y;
        const dockMembers = getDockGroupMembers(
          currentComponents,
          compToUpdate.id,
        );
        const dockMemberIds = new Set(
          dockMembers.map((component) => component.id),
        );
        const collapsedDependencyIds = new Set<string>();
        const spreadDependencyIds = new Set<string>();
        for (const owner of dockMembers) {
          if (!owner.dependencyIds?.length) continue;
          const descendants = get().getAllDescendantDependencyIds(owner.id);
          const target =
            owner.isDependenciesSpread === true
              ? spreadDependencyIds
              : collapsedDependencyIds;
          descendants.forEach((dependencyId) => target.add(dependencyId));
        }
        collapsedDependencyIds.forEach((dependencyId) =>
          spreadDependencyIds.delete(dependencyId),
        );

        set((state) => {
          const before = getWindowComponents(state, windowId);
          const quadtree = getWindowQuadtree(state, windowId);
          let updated = moveDockGroup(
            before,
            compToUpdate.id,
            newPosition,
            isMobileLayout,
          );
          updated = updated.map((component) => {
            if (
              collapsedDependencyIds.has(component.id) &&
              !dockMemberIds.has(component.id)
            ) {
              const position = getLayoutGeometry(
                component,
                isMobileLayout,
              ).position;
              const movedPosition = {
                x: Math.floor(position.x + deltaX),
                y: Math.floor(position.y + deltaY),
              };
              const dependencyPosition =
                component.originalDependencyPosition
                  ? {
                      x: Math.floor(
                        component.originalDependencyPosition.x + deltaX,
                      ),
                      y: Math.floor(
                        component.originalDependencyPosition.y + deltaY,
                      ),
                    }
                  : undefined;
              return isMobileLayout
                ? {
                    ...component,
                    mobilePosition: movedPosition,
                    originalDependencyPosition: dependencyPosition,
                  }
                : {
                    ...component,
                    position: movedPosition,
                    originalDependencyPosition: dependencyPosition,
                  };
            }
            if (spreadDependencyIds.has(component.id)) {
              return {
                ...component,
                originalDependencyPosition:
                  component.originalDependencyPosition
                    ? {
                        x: Math.floor(
                          component.originalDependencyPosition.x + deltaX,
                        ),
                        y: Math.floor(
                          component.originalDependencyPosition.y + deltaY,
                        ),
                      }
                    : undefined,
              };
            }
            return component;
          });
          const changedIds = new Set([
            ...Array.from(dockMemberIds),
            ...Array.from(collapsedDependencyIds),
          ]);
          // A drop under the drag rail or past the screen edge lands just
          // inside the workspace instead.
          updated = fitComponentsToWorkspace(state, windowId, updated, changedIds);
          if (!options.byPerson) updated = keepConversationsClearOf(state, windowId, before, updated, changedIds);
          reindexComponentChanges(
            quadtree,
            before,
            updated,
            changedIds,
            isMobileLayout,
          );

          return {
            componentsByWindow: {
              ...state.componentsByWindow,
              [windowId]: updated
            }
          };
        });

        // Persist to backend if requested. Live gesture movement updates the
        // local layout frequently, then commits once when the gesture ends.
        if (updateUserSession) {
          const allComponents = get().getAllComponents();
          const newUser = { ...get().userSession };
          newUser.preferences.componentsMap = allComponents;
          get().setUserSession(newUser);
          updatePreferences({
            ...newUser.preferences,
            componentsMap: allComponents
          });
        }
      },

      updateComponentSize: (
        id: string,
        newSize: { width: number; height: number },
        updateUserSession: boolean = true,
        options: GeometryChangeOptions = {},
      ) => {
        const windowId = getCurrentWindowIdOrDefault(get());
        const { isMobileLayout } = get();

        set((state) => {
          windowStoreDebug("updateComponentSize", id, newSize, updateUserSession, `isMobile: ${isMobileLayout}`)
          const before = getWindowComponents(state, windowId);
          const compToUpdate = before.find((comp) => comp.id === id || comp.name === id);
          windowStoreDebug("compToUpdate", compToUpdate)
          if (!compToUpdate) {
            console.error("Component not found");
            return state;
          }

          const quadtree = getWindowQuadtree(state, windowId);
          const changedIds = new Set(
            getDockGroupMembers(before, compToUpdate.id).map(
              (component) => component.id,
            ),
          );
          // A window that grows with its content stops at the workspace edge
          // and never pushes its title bar under the drag rail.
          const fitted = fitComponentsToWorkspace(
            state,
            windowId,
            resizeDockGroup(before, compToUpdate.id, newSize, isMobileLayout),
            changedIds,
          );
          // Nor does it grow over a conversation's transcript and composer.
          const updated = options.byPerson ? fitted : keepConversationsClearOf(state, windowId, before, fitted, changedIds);
          reindexComponentChanges(
            quadtree,
            before,
            updated,
            changedIds,
            isMobileLayout,
          );

          return {
            componentsByWindow: {
              ...state.componentsByWindow,
              [windowId]: updated
            }
          };
        });

        // Persist to backend if requested
        if (updateUserSession) {
          const allComponents = get().getAllComponents();
          const newUser = { ...get().userSession };
          newUser.preferences.componentsMap = allComponents;
          get().setUserSession(newUser);
          updatePreferences({
            ...newUser.preferences,
            componentsMap: allComponents
          });
        }
      },

      holdComponentHeight: (id: string, options: { reserveGrowth?: boolean } = {}) => {
        const state = get();
        if (state.heldComponentHeights[id]) return;
        const windowId = getCurrentWindowIdOrDefault(state);
        const components = getWindowComponents(state, windowId);
        const component = components.find((entry) => entry.id === id);
        if (!component) return;
        set({
          heldComponentHeights: {
            ...state.heldComponentHeights,
            [id]: {
              windowId,
              height: component.size.height,
              mobileHeight: component.mobileSize?.height ?? null,
              at: positionKey(component),
              growth: options.reserveGrowth ? { overlapping: overlappingAtFold(components, id) } : null,
            },
          },
        });
      },

      releaseComponentHeight: (id: string) => {
        let moved = false;
        set((state) => {
          const hold = state.heldComponentHeights[id];
          if (!hold) return state;
          const { [id]: _released, ...heldComponentHeights } = state.heldComponentHeights;
          const live = getWindowComponents(state, hold.windowId);
          const folded = live.find((component) => component.id === id);
          if (folded) {
            const isMobileLayout = Boolean(state.isMobileLayout);
            const changedIds = new Set(getDockGroupMembers(live, id).map((component) => component.id));
            // Growing back stops at the workspace edge, like any resize.
            const grown = fitComponentsToWorkspace(
              state,
              hold.windowId,
              live.map((component) => component.id === id ? withHeldHeight(component, hold) : component),
              changedIds,
            );
            // A short fold also moves clear of a window placed where the line was not.
            const clear = hold.growth
              ? moveGrownWindowClear(state, hold.windowId, grown, folded, hold.at, hold.growth, heldComponentHeights)
              : grown;
            // Growing back in the spot it was held restores the person's layout, an overlap with a
            // conversation included. Growing back anywhere else is the app's change, so it never
            // covers a conversation's transcript and composer.
            const before = positionKey(folded) === hold.at ? grown : live;
            const updated = keepConversationsClearOf({ ...state, heldComponentHeights }, hold.windowId, before, clear, changedIds);
            moved = updated !== grown;
            reindexComponentChanges(getWindowQuadtree(state, hold.windowId), live, updated, changedIds, isMobileLayout);
            return { heldComponentHeights, componentsByWindow: { ...state.componentsByWindow, [hold.windowId]: updated } };
          }
          // Closed while folded: the copy kept for restore gets its height back.
          const deleted = state.deletedComponentsByWindow[hold.windowId];
          if (!deleted?.some((component) => component.id === id)) return { heldComponentHeights };
          return {
            heldComponentHeights,
            deletedComponentsByWindow: {
              ...state.deletedComponentsByWindow,
              [hold.windowId]: deleted.map((component) => component.id === id ? withHeldHeight(component, hold) : component),
            },
          };
        });
        if (!moved) return;
        // The new spot is a layout change like a restore: other renderers and
        // the saved workspace take it, so a reload keeps the window clear.
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        broadcastComponentsByWindowSync(get().componentsByWindow);
        updatePreferences({ ...newUser.preferences, componentsMap: allComponents });
      },

      keepHeldComponentHeight: (id: string) => {
        if (!get().heldComponentHeights[id]) return;
        set((state) => {
          const hold = state.heldComponentHeights[id];
          if (!hold) return state;
          const heldComponentHeights = withoutHolds(state.heldComponentHeights, [id]);
          const live = getWindowComponents(state, hold.windowId);
          if (!live.some((component) => component.id === id)) return { heldComponentHeights };
          const isMobileLayout = Boolean(state.isMobileLayout);
          const updated = live.map((component) => component.id === id ? withLiveHeightIn(component, hold, isMobileLayout) : component);
          reindexComponentChanges(getWindowQuadtree(state, hold.windowId), live, updated, new Set([id]), isMobileLayout);
          return { heldComponentHeights, componentsByWindow: { ...state.componentsByWindow, [hold.windowId]: updated } };
        });
        // The live height is the one to keep now: saved, so a reload and the other renderers have it too.
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        broadcastComponentsByWindowSync(get().componentsByWindow);
        updatePreferences({ ...newUser.preferences, componentsMap: allComponents });
      },

      markComponentUserSized: (id: string) => {
        const windowId = getCurrentWindowIdOrDefault(get());
        const target = getWindowComponents(get(), windowId).find((component) => component.id === id);
        if (!target || target.userSized) return;
        set((state) => ({
          componentsByWindow: {
            ...state.componentsByWindow,
            [windowId]: getWindowComponents(state, windowId).map((component) =>
              component.id === id ? { ...component, userSized: true } : component),
          },
        }));
        // Saved with the workspace, so a reload keeps the user's height too.
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({ ...newUser.preferences, componentsMap: allComponents });
      },

      updateComponentGeometry: (
        id: string,
        geometry: {
          position: { x: number; y: number };
          size: { width: number; height: number };
        },
        updateUserSession: boolean = true,
        options: GeometryChangeOptions = {},
      ) => {
        const windowId = getCurrentWindowIdOrDefault(get());
        const { isMobileLayout } = get();

        set((state) => {
          const before = getWindowComponents(state, windowId);
          const compToUpdate = before.find(
            (component) => component.id === id || component.name === id,
          );
          if (!compToUpdate) return state;
          const quadtree = getWindowQuadtree(state, windowId);
          const changedIds = new Set(
            getDockGroupMembers(before, compToUpdate.id).map(
              (component) => component.id,
            ),
          );
          const fitted = fitComponentsToWorkspace(
            state,
            windowId,
            updateDockGroupGeometry(before, compToUpdate.id, geometry, isMobileLayout),
            changedIds,
          );
          const updated = options.byPerson ? fitted : keepConversationsClearOf(state, windowId, before, fitted, changedIds);
          reindexComponentChanges(
            quadtree,
            before,
            updated,
            changedIds,
            isMobileLayout,
          );
          return {
            componentsByWindow: {
              ...state.componentsByWindow,
              [windowId]: updated,
            },
          };
        });

        if (updateUserSession) {
          const allComponents = get().getAllComponents();
          const newUser = { ...get().userSession };
          newUser.preferences.componentsMap = allComponents;
          get().setUserSession(newUser);
          updatePreferences({
            ...newUser.preferences,
            componentsMap: allComponents,
          });
        }
      },

      /**
       * Update a component's props with new data.
      * This is used to persist component-specific data (like workflow results) to user preferences.
      */
      updateComponentProps: (id: string, propsUpdate: Record<string, any>, options: { targetWindowId?: string } = {}) => {
        if (options.targetWindowId && !getWindowComponents(get(), options.targetWindowId).some((component) => component.id === id || component.name === id)) return;
        set((state) => {
          const preferredWindowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
          const targetEntry = [
            [preferredWindowId, getWindowComponents(state, preferredWindowId)] as const,
            ...(options.targetWindowId ? [] : Object.entries(state.componentsByWindow).filter(([windowId]) => windowId !== preferredWindowId)),
          ].find(([, components]) =>
            (components || []).some((component) => component.id === id || component.name === id)
          );

          const targetWindowId = targetEntry?.[0];
          const currentComponents = targetEntry?.[1] || [];
          const compToUpdate = currentComponents.find((comp) => comp.id === id || comp.name === id);

          if (!targetWindowId || !compToUpdate) {
            console.error("[updateComponentProps] Component not found:", id);
            return state;
          }

          const updatedComponent = {
            ...compToUpdate,
            props: {
              ...compToUpdate.props,
              ...propsUpdate
            }
          };

          const newComponents = currentComponents.map((comp) =>
            comp.id === compToUpdate.id ? updatedComponent : comp
          );

          return {
            componentsByWindow: {
              ...state.componentsByWindow,
              [targetWindowId]: newComponents
            }
          };
        });

        // Electron renderers keep separate Zustand instances. Prop updates are
        // part of the durable workspace just like adds/removals, so publish the
        // complete window map before another renderer can persist a stale copy.
        broadcastComponentsByWindowSync(get().componentsByWindow);

        // Persist to backend
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents
        });

        windowStoreDebug("[updateComponentProps] Updated props for component:", id);
      },

      setFileList: (file: any) => {
        const fileList = [...get().fileList];
        fileList.unshift(file);
        set((state) => ({ fileList }));
      },

      setUserSession: (userSession: PassageUserType, options: { restoredWorkspaceIdentity?: { ownerId: string; companyId: string }; workspaceSnapshots?: WorkspaceSnapshot[] } = {}) => {
        const previous = get().userSession;
        const ownerChanged = previous.userId !== userSession.userId || (previous.companyId || null) !== (userSession.companyId || null);
        const restored = options.restoredWorkspaceIdentity;
        // Only the authenticated hydration path supplies this identity after
        // loading that account's preferences. Ordinary session edits may carry
        // an inherited componentsMap and cannot restore it after a switch.
        const restoredForNewOwner = userSession.isAuthorized === true && userSession.userId !== "demo"
          && restored?.ownerId && restored.ownerId === userSession.userId
          && restored.companyId && restored.companyId === userSession.companyId;
        const bootstrapping = Boolean(restoredForNewOwner && options.workspaceSnapshots);
        if (ownerChanged || userSession.isAuthorized !== true || bootstrapping) invalidateWorkspaceSaveQueue();
        if (bootstrapping) {
          if (!userSession.userId || !userSession.companyId) throw new Error("Workspace bootstrap requires an owner and company");
          const owner = { expectedOwnerId: userSession.userId, expectedCompanyId: userSession.companyId };
          let nextQueue: WorkspaceSaveQueue;
          const current = () => workspaceSaveQueue === nextQueue && captureWorkspacePersistenceIdentity()?.isCurrent() === true
            && useAppStore.getState().userSession.userId === owner.expectedOwnerId
            && useAppStore.getState().userSession.companyId === owner.expectedCompanyId;
          nextQueue = new WorkspaceSaveQueue(owner, options.workspaceSnapshots!, async input => {
            const { requestWorkspacePersistence } = await import("@/src/features/workspace/client/workspacePersistence");
            return requestWorkspacePersistence(input, { isCurrent: current });
          }, current);
          workspaceSaveQueue = nextQueue;
        }
        const nextSession = ownerChanged && !restoredForNewOwner
          ? { ...userSession, preferences: { ...userSession.preferences, componentsMap: [] } } : userSession;
        set({ userSession: nextSession, ...(ownerChanged || bootstrapping ? {
          workspaceSaveIssue: null,
          ...(get().micState === "Processing..." ? { micState: "Checking Microphone..." as MicState } : {}),
          // A later broadcast must never label the previous owner's views as
          // belonging to the newly authenticated account or company.
          componentsByWindow: {}, deletedComponentsByWindow: {}, editHistoryByWindow: {}, quadtreesByWindow: {},
          workspaceScenesByWindow: {}, documentViewerByWindow: {}, documentViewer: { files: [], currentDocumentIndex: 0 },
        } : {}) });
        if (ownerChanged || bootstrapping) get().rebuildAllQuadtrees();
        const queue = workspaceSaveQueue;
        const cleanup = bootstrapping && queue ? collapsedSingletonCleanup(options.workspaceSnapshots!, new Date().toISOString()) : null;
        // Close the duplicate singleton windows the restore dropped, so no
        // later restore can bring them back.
        if (queue && cleanup) void trackWorkspacePersistence(queue.save(cleanup, { preserveMissingLayoutReferences: false })
          .catch(error => windowStoreDebug("Duplicate window cleanup did not save", error)));
      },
      setChatSession: (chatSession: ChatSession) => {
        set((state) => ({ chatSession }));
      },
      setIsAIPlaying: (isAIPlaying: boolean) => {
        set((state) => ({ isAIPlaying }));
      },

      setMicState: (micState: MicState) => {
        set((state) => ({ micState }));
      },
      setWakeWordPrompt: (wakeWordPrompt: string | null) => {
        set(() => ({ wakeWordPrompt }));
      },
      setIsWakeWordListening: (isWakeWordListening: boolean) => {
        set(() => ({ isWakeWordListening }));
      },
      setActiveCall: (activeCall: ActiveCall) => {
        set((state) => ({ activeCall }));
      },
      setJoinedNativeCall: (joinedNativeCall: JoinedNativeCall | null) => {
        set(() => ({ joinedNativeCall }));
      },
      clearJoinedNativeCall: (joinedNativeCall: JoinedNativeCall) => {
        set((state) => {
          if (
            !state.joinedNativeCall
            || state.joinedNativeCall.callId !== joinedNativeCall.callId
            || state.joinedNativeCall.callType !== joinedNativeCall.callType
          ) {
            return {};
          }
          return { joinedNativeCall: null };
        });
      },

      setLiveAudioData: (liveAudioData: Float32Array | undefined) => {
        set((state) => ({ liveAudioData }));
      },
      setNotifications: (notifications: any[]) => {
        set((state) => ({ notifications }));
      },

      setAiActivity: (componentId: string, activity: AiActivityState | null) => {
        if (!componentId) return;
        set((state) => {
          if (!activity && !(componentId in state.aiActivityByComponent)) return {};
          const aiActivityByComponent = { ...state.aiActivityByComponent };
          if (activity) {
            aiActivityByComponent[componentId] = activity;
          } else {
            delete aiActivityByComponent[componentId];
          }
          return { aiActivityByComponent };
        });
      },

      setGestureArmedComponentId: (componentId: string | null) => {
        set((state) =>
          state.gestureArmedComponentId === componentId ? {} : { gestureArmedComponentId: componentId },
        );
      },

      markNotificationsRead: (keys: string[]) => {
        set((state) => {
          const current = new Set(state.readNotificationKeys || []);
          keys.forEach((k) => current.add(k));
          return { readNotificationKeys: Array.from(current) };
        });
      },

      unmarkNotificationsRead: (keys: string[]) => {
        set((state) => {
          const current = state.readNotificationKeys || [];
          const drop = new Set(keys);
          const next = current.filter((key) => !drop.has(key));
          return next.length === current.length ? {} : { readNotificationKeys: next };
        });
      },

      // Done: the rows leave at once and stay hidden while the feed may still
      // return them (memory only; the server's feed state is the durable copy).
      dismissNotifications: (keys: string[]) => {
        let removed: DismissedNotificationEntry[] = [];
        set((state) => {
          const keySet = new Set(keys);
          const pending = { ...(state.pendingDoneNotificationKeys || {}) };
          keys.forEach((k) => { pending[k] = 0; });
          const taken = takeDoneEntries(state.notifications || [], keySet);
          removed = taken.removed;
          const remainingRead = (state.readNotificationKeys || []).filter((k) => !keySet.has(k));
          return {
            notifications: taken.remaining,
            readNotificationKeys: remainingRead,
            pendingDoneNotificationKeys: pending,
          };
        });
        return removed;
      },

      // The server recorded the done; the next feed read started after this drops the keys.
      confirmDoneNotifications: (keys: string[], confirmedAt: number) => {
        set((state) => {
          const current = state.pendingDoneNotificationKeys || {};
          if (!keys.some((key) => Object.prototype.hasOwnProperty.call(current, key))) return {};
          const pending = { ...current };
          keys.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(pending, key)) pending[key] = confirmedAt;
          });
          return { pendingDoneNotificationKeys: pending };
        });
      },

      // Takes back a dismissal the server did not accept: the rows come back
      // at once, in their old places, without waiting for a feed read.
      undismissNotifications: (keys: string[], restore: readonly DismissedNotificationEntry[] = []) => {
        set((state) => {
          const current = state.pendingDoneNotificationKeys || {};
          const pending = { ...current };
          keys.forEach((key) => { delete pending[key]; });
          const changedPending = keys.some((key) => Object.prototype.hasOwnProperty.call(current, key));
          if (!changedPending && restore.length === 0) return {};
          return {
            pendingDoneNotificationKeys: changedPending ? pending : current,
            ...(restore.length > 0 ? { notifications: restoreDoneEntries(state.notifications || [], restore) } : {}),
          };
        });
      },

      // Undo: the rows it brought back stay while a feed read may still
      // leave them out as done (memory only, like the pending done keys).
      holdReopenedNotifications: (keys: string[]) => {
        set((state) => {
          const pending = { ...(state.pendingReopenNotificationKeys || {}) };
          keys.forEach((key) => { pending[key] = 0; });
          return { pendingReopenNotificationKeys: pending };
        });
      },

      confirmReopenedNotifications: (keys: string[], confirmedAt: number) => {
        set((state) => {
          const current = state.pendingReopenNotificationKeys || {};
          if (!keys.some((key) => Object.prototype.hasOwnProperty.call(current, key))) return {};
          const pending = { ...current };
          keys.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(pending, key)) pending[key] = confirmedAt;
          });
          return { pendingReopenNotificationKeys: pending };
        });
      },

      releaseReopenedNotifications: (keys: string[]) => {
        set((state) => {
          const current = state.pendingReopenNotificationKeys || {};
          if (!keys.some((key) => Object.prototype.hasOwnProperty.call(current, key))) return {};
          const pending = { ...current };
          keys.forEach((key) => { delete pending[key]; });
          return { pendingReopenNotificationKeys: pending };
        });
      },

      // Track that an event has been notified at a specific threshold (30min or 10min)
      markEventNotified: (eventId: string, threshold: '30min' | '10min') => {
        set((state) => {
          const current = state.notifiedEventThresholds || {};
          const eventEntry = current[eventId] || {};
          return {
            notifiedEventThresholds: {
              ...current,
              [eventId]: {
                ...eventEntry,
                [threshold === '30min' ? 'notified30min' : 'notified10min']: Date.now()
              }
            }
          };
        });
      },

      // Mark multiple events as notified at once (batch operation)
      markEventsNotified: (events: Array<{ eventId: string; threshold: '30min' | '10min' }>) => {
        set((state) => {
          const current = { ...(state.notifiedEventThresholds || {}) };
          const now = Date.now();
          events.forEach(({ eventId, threshold }) => {
            const eventEntry = current[eventId] || {};
            current[eventId] = {
              ...eventEntry,
              [threshold === '30min' ? 'notified30min' : 'notified10min']: now
            };
          });
          return { notifiedEventThresholds: current };
        });
      },

      // Clear old notification thresholds (cleanup for events that have passed)
      cleanupOldNotificationThresholds: () => {
        set((state) => {
          const current = state.notifiedEventThresholds || {};
          const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
          const cleaned: Record<string, { notified30min?: number; notified10min?: number }> = {};

          Object.entries(current).forEach(([eventId, thresholds]) => {
            const latest = Math.max(thresholds.notified30min || 0, thresholds.notified10min || 0);
            // Keep entries that were notified within the last 24 hours
            if (latest > oneDayAgo) {
              cleaned[eventId] = thresholds;
            }
          });

          return { notifiedEventThresholds: cleaned };
        });
      },

      getNotifiedEventThresholds: () => get().notifiedEventThresholds || {},

      // One slot; a waiting question is never replaced (chatSlotActions).
      ...chatSlotActions(get, (patch) => set(patch)),

      updateComponentScores: (componentScores: Record<string, number>) => {
        const windowId = getCurrentWindowIdOrDefault(get());

        set((state) => {
          const currentComponents = getWindowComponents(state, windowId);

          // Get components with updated scores and reasons
          const updatedComponents = currentComponents.map((component) => {
            const updatedComponent = { ...component };

            // Update score if available
            if (componentScores[component.id] !== undefined) {
              updatedComponent.score = componentScores[component.id];
            }

            return updatedComponent;
          });

          // Update the user session preferences
          const newUser = { ...state.userSession };

          // Update components in user preferences to match updated components
          if (newUser.preferences?.componentsMap) {
            newUser.preferences.componentsMap = newUser.preferences.componentsMap.map(comp => {
              const matchingComp = updatedComponents.find(c => c.id === comp.id);
              if (matchingComp) {
                return {
                  ...comp,
                  score: matchingComp.score
                };
              }
              return comp;
            });
          }

          // Return the updated state
          return {
            componentsByWindow: {
              ...state.componentsByWindow,
              [windowId]: updatedComponents
            },
            userSession: newUser
          };
        });
      },

      // Space-aware placement methods

      // Step 1: Find ALL free spaces (not just exact fits) and prioritize by area
      analyzeFreeSpace: (
        componentWidth: number,
        componentHeight: number,
        desiredPosition?: { x: number; y: number },
        options: { targetWindowId?: string } = {}
      ): {
        totalFreeArea: number;
        hasContinuousSpace: boolean;
        freeRegions: FreeSpaceRegion[];
        needsMerging: boolean;
        largestSpace: FreeSpaceRegion | null;
      } => {
        const state = get();
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
        const dimensions = getWindowDimensions(state, windowId);
        const quadtree = getWindowQuadtree(state, windowId);
        const components = getWindowComponents(state, windowId);

        if (!dimensions || !quadtree) {
          return { totalFreeArea: 0, hasContinuousSpace: false, freeRegions: [], needsMerging: false, largestSpace: null };
        }
        const { width: screenWidth, height: screenHeight } = dimensions;

        // Guard against invalid dimensions (negative or zero) that would cause RangeError
        if (screenWidth <= 0 || screenHeight <= 0) {
          console.warn(`[SpaceAnalysis] Invalid screen dimensions: ${screenWidth}x${screenHeight}, returning empty`);
          return { totalFreeArea: 0, hasContinuousSpace: false, freeRegions: [], needsMerging: false, largestSpace: null };
        }

        const requiredArea = componentWidth * componentHeight;

        // Only minimized (docked) windows free their area. Visible prompts and
        // dependency windows still cover the workspace.
        const minimizedComponents = components.filter(c => c.isMinimized);
        if (minimizedComponents.length > 0) {
          const minimizedArea = minimizedComponents.reduce((sum, c) => sum + (c.size.width * c.size.height), 0);
          windowStoreDebug(`[SpaceAnalysis] Found ${minimizedComponents.length} minimized component(s) (${minimizedComponents.map(c => c.name).join(', ')}) - treating ${minimizedArea}px² as free space`);
        }

        windowStoreDebug(`[SpaceAnalysis] Analyzing space for ${componentWidth}x${componentHeight} (required area: ${requiredArea})`);

        // Rasterize visible components into a coarse occupancy grid and
        // extract maximal free rectangles (histogram sweep). Replaces the old
        // per-cell × per-component scan that allocated a Rectangle per cell.
        const gridStepSize = 5; // Fine-grained grid
        const occupiedRects = components
          .filter(blocksPlacement)
          .map((comp) => ({
            x: comp.position.x,
            y: comp.position.y,
            width: comp.size.width,
            height: comp.size.height,
          }));

        const { regions, totalFreeArea } = computeFreeRegions(
          occupiedRects,
          { width: screenWidth, height: screenHeight },
          gridStepSize
        );

        const freeRegions: FreeSpaceRegion[] = regions.map((region) => ({
          x: region.x,
          y: region.y,
          width: region.width,
          height: region.height,
          area: region.width * region.height,
          distanceFromDesired: desiredPosition
            ? Math.hypot(
                (region.x + region.width / 2) - desiredPosition.x,
                (region.y + region.height / 2) - desiredPosition.y
              )
            : undefined,
        }));

        // Sort by area (LARGEST first), then by distance from desired
        freeRegions.sort((a, b) => {
          const areaDiff = b.area - a.area; // Descending by area
          if (Math.abs(areaDiff) > 1000) return areaDiff;

          if (desiredPosition && a.distanceFromDesired !== undefined && b.distanceFromDesired !== undefined) {
            return a.distanceFromDesired - b.distanceFromDesired;
          }
          return 0;
        });

        const largestSpace = freeRegions.length > 0 ? freeRegions[0] : null;

        // Check if largest space can fit the component
        const hasContinuousSpace = largestSpace !== null &&
          largestSpace.width >= componentWidth &&
          largestSpace.height >= componentHeight;

        windowStoreDebug(`[SpaceAnalysis] Found ${freeRegions.length} free regions. Largest: ${largestSpace ? `${largestSpace.width}x${largestSpace.height} (${largestSpace.area} sq px)` : 'none'}`);
        windowStoreDebug(`[SpaceAnalysis] Required: ${componentWidth}x${componentHeight} (${requiredArea} sq px). Has continuous space: ${hasContinuousSpace}`);

        return {
          totalFreeArea,
          hasContinuousSpace,
          freeRegions,
          needsMerging: !hasContinuousSpace && largestSpace !== null && largestSpace.area < requiredArea * 2,
          largestSpace
        };
      },

      calculateFreeSpaceRegions: (
        componentWidth: number,
        componentHeight: number,
        desiredPosition?: { x: number; y: number }
      ): FreeSpaceRegion[] => {
        const state = get() as AppState;
        const analysis = state.analyzeFreeSpace(componentWidth, componentHeight, desiredPosition);
        return analysis.freeRegions;
      },

      calculateMovementCost: (
        component: componentsMapType,
        toPosition: { x: number; y: number }
      ): number => movementCost(component, toPosition),

      findMinimalMovementSet: (
        newComponentWidth: number,
        newComponentHeight: number,
        desiredPosition: { x: number; y: number },
        score: number = 5,
        options: { targetWindowId?: string } = {}
      ): PlacementStrategy => {
        const state = get();
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
        const dimensions = getWindowDimensions(state, windowId);
        const components = getWindowComponents(state, windowId);

        if (!dimensions) {
          windowStoreDebug('[Movement] No screen dimensions available');
          return {
            needsMovement: false,
            position: null,
            componentsToMove: [],
            allBlockersResolved: false,
          };
        }

        const strategy = planComponentPlacement({
          components,
          footprintAt: footprintAtFor(dimensions, Boolean(state.isMobileLayout)),
          reservedRects: getChromeZones(state, dimensions),
          screen: dimensions,
          newComponentSize: { width: newComponentWidth, height: newComponentHeight },
          desiredCenter: desiredPosition,
          margin: OCCUPANCY_MARGIN,
          maxMovements: 5,
          gridStep: 24,
          maxTargetCandidates: 320,
          fixedComponentNames: EPHEMERAL_COMPONENTS,
        });

        windowStoreDebug("[Movement] Deterministic placement plan", {
          needsMovement: strategy.needsMovement,
          position: strategy.position,
          moveCount: strategy.componentsToMove.length,
          targetBoundingBox: strategy.targetBoundingBox,
          allBlockersResolved: strategy.allBlockersResolved,
        });

        return strategy;
      },

      executeComponentMovement: (strategy: PlacementStrategy) => {
        if (!strategy.needsMovement || strategy.componentsToMove.length === 0) {
          windowStoreDebug('[Movement] No movement needed or no components to move');
          return;
        }

        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);

        windowStoreDebug(`✨ [Movement] Executing movement for ${strategy.componentsToMove.length} component(s)`);

        const successfulMoves = strategy.componentsToMove.filter((move) => {
          const currentComponent = getWindowComponents(get(), windowId).find((component) => component.id === move.component.id);
          if (!currentComponent) {
            console.warn(`[Movement] Component ${move.component.name} no longer exists, skipping movement`);
            return false;
          }
          windowStoreDebug(`  📦 Moving ${move.component.name} from (${move.fromPosition.x.toFixed(0)}, ${move.fromPosition.y.toFixed(0)}) to (${move.toPosition.x.toFixed(0)}, ${move.toPosition.y.toFixed(0)})`);
          return true;
        });

        if (successfulMoves.length === 0) {
          console.warn("[Movement] No planned movements could be applied");
          return;
        }

        // Commit component positions first, then rebuild the quadtree from state.
        // This avoids drift from exact rectangle remove/insert mismatches.
        set((state) => {
          const currentComponents = getWindowComponents(state, windowId);
          const updatedComponents = currentComponents.map((comp) => {
            const moveCandidate = successfulMoves.find(m => m.component.id === comp.id);
            if (moveCandidate) {
              return {
                ...comp,
                position: {
                  x: Math.floor(moveCandidate.toPosition.x),
                  y: Math.floor(moveCandidate.toPosition.y)
                },
                isAnimatingPosition: true,
                animationStartTime: Date.now()
              };
            }
            return comp;
          });

          windowStoreDebug(`[Movement] Updated ${successfulMoves.length} components in state`);

          return {
            componentsByWindow: {
              ...state.componentsByWindow,
              [windowId]: updatedComponents
            }
          };
        });

        get().rebuildQuadtree();

        const rebuiltQuadtree = getWindowQuadtree(get(), windowId);
        if (strategy.targetBoundingBox && rebuiltQuadtree) {
          const target = strategy.targetBoundingBox;
          const targetRect = toOccupancyRect(target.x, target.y, target.width, target.height);
          const isTargetClear = !rebuiltQuadtree.overlapsAny(targetRect);
          windowStoreDebug(`[Movement] Target area verification: ${isTargetClear ? 'CLEAR' : 'STILL BLOCKED'}`);

          if (!isTargetClear) {
            console.warn(`[Movement] Target area (${target.x}, ${target.y}) ${target.width}x${target.height} still blocked after movements`);
          }
        }

        // Clear animation flags after transition completes
        setTimeout(() => {
          set((state) => {
            const currentComponents = getWindowComponents(state, windowId);
            return {
              componentsByWindow: {
                ...state.componentsByWindow,
                [windowId]: currentComponents.map(comp => ({
                  ...comp,
                  isAnimatingPosition: false,
                  animationStartTime: undefined
                }))
              }
            };
          });
        }, 1700); // Slightly longer than 1.5s animation duration

        // Persist to backend
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents
        });

        windowStoreDebug(`[Movement] Movement execution completed: ${successfulMoves.length}/${strategy.componentsToMove.length} successful`);
      },

      tidyWorkspace: () => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const dimensions = getWindowDimensions(state, windowId);
        if (!dimensions) return false;

        // Tidy is an explicit user action. Preserve each existing window as an
        // anchor and let the deterministic planner move only the blockers that
        // prevent a tighter arrangement around that anchor.
        let moved = false;
        const anchors = getWindowComponents(state, windowId)
          .filter(isTidyAnchor)
          .sort((left, right) => (
            (right.score || 5) - (left.score || 5)
            || String(left.id).localeCompare(String(right.id))
          ));

        for (const anchor of anchors) {
          const currentComponents = getWindowComponents(get(), windowId);
          const currentAnchor = currentComponents.find((component) => component.id === anchor.id);
          if (!currentAnchor || currentAnchor.isMinimized) continue;
          const isMobileLayout = Boolean(get().isMobileLayout);
          const anchorFootprint = placementFootprint(currentAnchor, dimensions, isMobileLayout);
          const strategy = planComponentPlacement({
            components: currentComponents.filter((component) => component.id !== anchor.id),
            footprintAt: footprintAtFor(dimensions, isMobileLayout),
            reservedRects: getChromeZones(get(), dimensions),
            screen: dimensions,
            newComponentSize: anchorFootprint,
            desiredCenter: {
              x: currentAnchor.position.x + anchorFootprint.width / 2,
              y: currentAnchor.position.y + anchorFootprint.height / 2,
            },
            margin: OCCUPANCY_MARGIN,
            maxMovements: 3,
            gridStep: 24,
            fixedComponentNames: EPHEMERAL_COMPONENTS,
          });
          if (!strategy.needsMovement || !strategy.allBlockersResolved) continue;
          get().executeComponentMovement(strategy);
          moved = true;
        }

        return moved;
      },

      resetStore: () => {
        invalidateWorkspaceSaveQueue();
        set({
          userSession: emptyUserInfo,
          chatSession: emptyChatSession,
          showQuadTreeOverlay: false,
          isSigningOut: false,
          workspaceSaveIssue: null,
          fileList: [],
          micState: 'Checking Microphone...',
          wakeWordPrompt: null,
          isWakeWordListening: false,
          isAIPlaying: false,
          liveAudioData: undefined,
          notifications: [],
          notificationFeedStatus: NOTIFICATION_FEED_PENDING,
          pendingChatMessage: null,
          sendingChatMessage: null,
          readNotificationKeys: [],
          pendingDoneNotificationKeys: {},
          pendingReopenNotificationKeys: {},
          notifiedEventThresholds: {},
          activeCall: null,
          joinedNativeCall: null,
          isMobileLayout: false,
          workspaceScenesByWindow: {},
          documentViewerByWindow: {},
          // Per-window state reset - keep currentWindowId (set per-window at runtime)
          // IMPORTANT: This clears ALL window data when resetStore is called (e.g., on logout)
          componentsByWindow: {},
          screenDimensionsByWindow: {},
          quadtreesByWindow: {},
          deletedComponentsByWindow: {},
          editHistoryByWindow: {},
          heldComponentHeights: {},
          documentViewer: {
            files: [],
            currentDocumentIndex: 0
          }
        });
      },

      // Debug actions
      toggleQuadTreeOverlay: () => {
        set((state) => {
          const newValue = !state.showQuadTreeOverlay;
          // Broadcast to other windows
          if (broadcastChannel) {
            broadcastChannel.postMessage({
              type: 'QUADTREE_OVERLAY_TOGGLE',
              value: newValue
            });
          }
          return { showQuadTreeOverlay: newValue };
        });
      },

      // Set quadtree overlay directly (used for cross-window sync)
      setQuadTreeOverlay: (value: boolean) => set({ showQuadTreeOverlay: value }),

      // Note: rebuildQuadtree() is defined earlier in window management section

      // Document viewer actions
      setDocumentFiles: (files: any[], options: { targetWindowId?: string } = {}) => {
        set((state) => {
          const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
          if (state.isSigningOut || (options.targetWindowId && !state.componentsByWindow[windowId])) return {};
          const current = windowId === getCurrentWindowIdOrDefault(state);
          const viewer = { ...(current ? state.documentViewer : state.documentViewerByWindow[windowId] ?? { currentDocumentIndex: 0 }), files };
          return { documentViewerByWindow: { ...state.documentViewerByWindow, [windowId]: viewer }, ...(current ? { documentViewer: viewer } : {}) };
        });
      },

      setCurrentDocumentIndex: (index: number, options: { targetWindowId?: string } = {}) => {
        set((state) => {
          const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
          if (state.isSigningOut || (options.targetWindowId && !state.componentsByWindow[windowId])) return {};
          const current = windowId === getCurrentWindowIdOrDefault(state);
          const viewer = { ...(current ? state.documentViewer : state.documentViewerByWindow[windowId] ?? { files: [] }), currentDocumentIndex: index };
          return { documentViewerByWindow: { ...state.documentViewerByWindow, [windowId]: viewer }, ...(current ? { documentViewer: viewer } : {}) };
        });
      },

      setMediaGroups: (mediaGroups: any[]) => {
        set((state) => ({
          mediaGallery: {
            ...state.mediaGallery,
            mediaGroups
          }
        }));
      },

      setCurrentMediaIndex: (index: number) => {
        set((state) => ({
          mediaGallery: {
            ...state.mediaGallery,
            currentMediaIndex: index
          }
        }));
      },

      moveOutOfGrowthPath: (componentId: string, options: { targetWindowId?: string } = {}) => {
        const state = get();
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
        const components = getWindowComponents(state, windowId);
        // The conversation's footprint covers the open transcript whether it
        // is open now or not. The window moves to the nearest free spot, and
        // in a full workspace over other windows rather than over the
        // conversation (moveClearOfConversation).
        const moved = moveClearOfConversation(state, windowId, components, componentId);
        if (moved === components) return false;
        set((current) => ({
          componentsByWindow: {
            ...current.componentsByWindow,
            [windowId]: bringComponentGroupToFront(
              moveClearOfConversation(current, windowId, getWindowComponents(current, windowId), componentId),
              componentId,
            ),
          },
        }));
        get().rebuildQuadtree({ targetWindowId: windowId });
        return true;
      },

      restoreComponent: (componentId: string, options: { targetWindowId?: string; keepOpenPlacement?: boolean } = {}) => {
        const state = get();
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
        const components = getWindowComponents(state, windowId);

        windowStoreDebug(`[Window ${windowId}] restoreComponent`, componentId);
        const isMobileLayout = Boolean(state.isMobileLayout);

        const requested = components.find(c => c.id === componentId);
        if (requested && !requested.isMinimized) {
          // An open window being brought back (a reused singleton, a nav
          // tap) keeps its spot unless it sits where the conversation's
          // transcript opens; then the transcript would cover it. A caller
          // keeping the person's presentation keeps the spot as well.
          if (!options.keepOpenPlacement) {
            get().moveOutOfGrowthPath(requested.id, { targetWindowId: windowId });
          }
          return;
        }
        const componentToRestore = requested;
        if (!componentToRestore) return;
        const restoreMembers = getDockGroupMembers(
          components,
          componentToRestore.id,
        ).filter((component) => component.isMinimized);
        const restoreIds = new Set(
          restoreMembers.map((component) => component.id),
        );

        windowStoreDebug("Restoring component", componentToRestore.name);

        const dimensions = getWindowDimensions(state, windowId);
        const chromeZones = dimensions ? getChromeZones(state, dimensions) : [];
        const chromeRects = chromeZones.map(toChromeRectangle);
        // A restored window must fit between the desktop drag rail and the
        // screen edges, or its title bar lands under the rail wherever it goes.
        const restoredSizes = new Map(
          restoreMembers.map((member) => [
            member.id,
            fitSizeToWorkspace(state, member.size, dimensions),
          ]),
        );
        // moveDockGroup translates every member of the dock group, so the
        // whole group is placed as one footprint and every member is checked.
        const movingMembers = getDockGroupMembers(components, componentToRestore.id);
        const movingIds = new Set(movingMembers.map((member) => member.id));
        const memberRects = movingMembers.map((member) => ({
          x: member.position.x,
          y: member.position.y,
          ...(restoredSizes.get(member.id) ?? member.size),
        }));
        // Every visible window outside the group occupies space, prompts and
        // dependencies included. A window that grows on its own (the
        // conversation) occupies its whole footprint, so a window restored
        // into the transcript's path moves even while the transcript is closed.
        // A window folded to one line for a moment occupies its unfolded height.
        const obstacles = savedComponents(components.filter(
          (candidate) => !movingIds.has(candidate.id) && blocksPlacement(candidate),
        ), growthHolds(state.heldComponentHeights));
        const memberOccupancy = memberRects.map((rect, index) =>
          dimensions
            ? toFootprintOccupancyRect({ name: movingMembers[index].name, size: rect, position: rect }, dimensions, isMobileLayout)
            : toOccupancyRect(rect.x, rect.y, rect.width, rect.height),
        );
        const overlapsChrome = memberOccupancy.some((rect) =>
          chromeRects.some((zone) => rect.intersects(zone)),
        );
        // The conversation's transcript and composer area is reserved like
        // chrome: a restored window never keeps a spot there.
        const conversationZones = dimensions ? conversationAreas(state, dimensions, components, movingIds) : [];
        const coversConversation = memberRects.some((rect) =>
          conversationZones.some((zone) => rectsOverlap(rect, zone)),
        );
        const hasOverlap = overlapsChrome || memberOccupancy.some((rect) =>
          obstacles.some((other) =>
            rect.intersects(
              dimensions
                ? toFootprintOccupancyRect(other, dimensions, isMobileLayout)
                : toOccupancyRect(other.position.x, other.position.y, other.size.width, other.size.height),
            ),
          ),
        );

        const groupLeft = Math.min(...memberRects.map((rect) => rect.x));
        const groupTop = Math.min(...memberRects.map((rect) => rect.y));
        const groupSize = {
          width: Math.max(...memberRects.map((rect) => rect.x + rect.width)) - groupLeft,
          height: Math.max(...memberRects.map((rect) => rect.y + rect.height)) - groupTop,
        };
        // newPosition is for the restored window; the group footprint is
        // placed and the window keeps its offset inside it.
        const positionForGroupAt = (groupPosition: LayoutPoint) => ({
          x: groupPosition.x + componentToRestore.position.x - groupLeft,
          y: groupPosition.y + componentToRestore.position.y - groupTop,
        });

        let newPosition = componentToRestore.position;

        if (hasOverlap) {
          windowStoreDebug(`⚠️ Component ${componentToRestore.name} overlaps on restore, finding new position`);
          // The conversation needs room for its whole footprint, which
          // depends on where it lands; other windows place their group box.
          const growingAlone = movingMembers.length === 1 && growsOnItsOwn(componentToRestore, isMobileLayout);
          const growingOccupancy = growingAlone && dimensions
            ? getPlacementOccupancy(state, windowId, componentToRestore.id)
            : null;
          const foundPosition = growingOccupancy && dimensions
            ? findFirstFootprintPosition(
                { name: componentToRestore.name, size: groupSize },
                dimensions,
                growingOccupancy,
                isMobileLayout,
                { x: groupLeft, y: groupTop },
              )
            : get().findNextAvailablePosition(
                groupSize.width,
                groupSize.height,
                componentToRestore.priority,
                componentToRestore.group,
                componentToRestore.score || 5,
                { targetWindowId: windowId }
              );

          if (foundPosition) {
            newPosition = positionForGroupAt(foundPosition);
            windowStoreDebug(`✅ Found new position for restored component:`, newPosition);
          } else if ((overlapsChrome || coversConversation) && dimensions) {
            // No free spot, and staying put leaves the title bar under the
            // rail or the window over the conversation's composer. Take the
            // saturated-workspace fallback new windows use: never under app
            // chrome or over the conversation, least overlap with visible
            // windows, nearest the saved spot. A window alone may open
            // smaller to stay off the conversation. The group is raised above
            // what it covers.
            const saturated = placeInSaturatedWorkspace({
              requestedSize: groupSize,
              screen: dimensions,
              obstacles: obstacles.map((candidate) => {
                const footprint = placementFootprint(
                  { name: candidate.name, size: candidate.size, position: candidate.position },
                  dimensions,
                  isMobileLayout,
                );
                return {
                  x: candidate.position.x,
                  y: candidate.position.y,
                  width: footprint.width,
                  height: footprint.height,
                  overlapWeight: isAttentionPrompt(candidate)
                    ? ATTENTION_PROMPT_OVERLAP_WEIGHT
                    : 1,
                };
              }),
              reserved: [...chromeZones, ...conversationKeepOuts(state, dimensions, components, movingIds)],
              preferred: { x: groupLeft, y: groupTop },
              margin: OCCUPANCY_MARGIN,
              allowShrink: false,
              fitAroundReserved: movingMembers.length === 1,
            });
            newPosition = positionForGroupAt(saturated.position);
            if (movingMembers.length === 1 && restoreIds.has(componentToRestore.id)) {
              restoredSizes.set(componentToRestore.id, saturated.size);
            }
            windowStoreDebug(`Viewport saturated; restoring ${componentToRestore.name} clear of app chrome`, saturated);
          } else {
            // Nothing is free and the saved spot is clear of app chrome and
            // the conversation: keep the spot the person remembers, raised
            // above what it covers.
            windowStoreDebug(`Could not find new position for ${componentToRestore.name}, using original position`);
          }
        }

        // The public landing workspace sits beneath a fixed navigation bar.
        // Placement scans can otherwise restore a window with its controls
        // underneath that bar, making a visually present Close/Minimize button
        // physically unclickable. Keep restored demo controls below the shell.
        if (isLandingDemoStoreIsolationActive() && newPosition.y < 160) {
          newPosition = { ...newPosition, y: 160 };
        }

        const repositionedComponents = moveDockGroup(
          components,
          componentToRestore.id,
          newPosition,
          state.isMobileLayout,
        );
        // A restored window is the one the person just asked for. It opens
        // above its neighbours, which matters when a saturated workspace leaves
        // no free slot and it must overlap them.
        const updatedComponents = bringComponentGroupToFront(
          repositionedComponents.map((comp) => {
            if (restoreIds.has(comp.id)) {
              const { isMinimized, minimizedScale, isSystemTuckedAway, ...restoredComp } = comp;
              return {
                ...restoredComp,
                size: { ...(restoredSizes.get(comp.id) ?? comp.size) },
                isRestoringFromMinimized: true, // Flag for animation
                restorationStartTime: Date.now()
              };
            }
            return comp;
          }),
          componentToRestore.id,
        );

        set((state) => ({
          componentsByWindow: {
            ...state.componentsByWindow,
            [windowId]: updatedComponents
          }
        }));

        // Rebuild instead of relying only on the incremental insert above; this
        // clears any stale minimized occupancy before future placement scans run.
        get().rebuildQuadtree({ targetWindowId: windowId });

        // Clear restoration animation flag after animation completes
        const restorationOwnerId = state.userSession.userId;
        setTimeout(() => {
          set((state) => {
            if (state.userSession.userId !== restorationOwnerId || state.isSigningOut) return state;
            const currentComponents = getWindowComponents(state, windowId);
            return {
              componentsByWindow: {
                ...state.componentsByWindow,
                [windowId]: currentComponents.map(comp => {
                  if (restoreIds.has(comp.id)) {
                    const { isRestoringFromMinimized, restorationStartTime, isSystemTuckedAway, ...cleanComp } = comp;
                    return cleanComp;
                  }
                  return comp;
                })
              }
            };
          });
        }, 400); // Match the transition duration

        // Persist to backend
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents.map(c => {
            const { isRestoringFromMinimized, restorationStartTime, ...cleanComp } = c;
            return cleanComp;
          })
        });
      },

      // ============================================
      // DEPENDENCY SPREAD/COLLAPSE
      // ============================================

      /**
       * Recursively collect all descendant dependency IDs for a component.
       * Returns a flat array of all dependency IDs at any level of nesting.
       * This enables spreading/collapsing the entire dependency tree, not just direct children.
       */
      getAllDescendantDependencyIds: (parentId: string, visited: Set<string> = new Set()): string[] => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const components = getWindowComponents(state, windowId);

        if (visited.has(parentId)) return []; // Prevent infinite loops
        visited.add(parentId);

        const parent = components.find(c => c.id === parentId);
        if (!parent || !parent.dependencyIds || parent.dependencyIds.length === 0) {
          return [];
        }

        const directDeps = parent.dependencyIds as string[];
        const allDeps: string[] = [...directDeps];

        // Recursively get dependencies of each direct dependency
        for (const depId of directDeps) {
          const childDeps = get().getAllDescendantDependencyIds(depId, visited);
          allDeps.push(...childDeps);
        }

        return allDeps;
      },

      /**
       * Toggle spread/collapse of dependency components for a parent component.
       * When spread: dependencies move to distributed positions across the screen.
       * When collapsed: dependencies return to their original positions near the parent.
       * This affects ALL nested dependencies (children, grandchildren, etc.)
       */
      toggleDependencySpread: (parentComponentId: string) => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const components = getWindowComponents(state, windowId);
        const parentComponent = components.find(c => c.id === parentComponentId);

        if (!parentComponent) {
          windowStoreDebug('[DependencySpread] Parent component not found:', parentComponentId);
          return;
        }

        const isCurrentlySpread = parentComponent.isDependenciesSpread || false;

        windowStoreDebug(`[DependencySpread] Toggling dependencies for ${parentComponent.name}: ${isCurrentlySpread ? 'collapse' : 'spread'}`);

        if (isCurrentlySpread) {
          get().collapseDependencies(parentComponentId);
        } else {
          get().spreadDependencies(parentComponentId);
        }
      },

      /**
       * Spread dependency components across the screen in a distributed pattern.
       * This affects ALL nested dependencies (children, grandchildren, etc.)
       * Dependencies must NOT overlap each other or the parent, but CAN overlap other components.
       */
      spreadDependencies: (parentComponentId: string) => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const components = getWindowComponents(state, windowId);
        const dimensions = getWindowDimensions(state, windowId);
        const quadtree = getWindowQuadtree(state, windowId);

        if (!dimensions || !quadtree) {
          windowStoreDebug('[DependencySpread] Screen dimensions or quadtree not available');
          return;
        }
        const { width: screenWidth, height: screenHeight } = dimensions;

        const parentComponent = components.find(c => c.id === parentComponentId);
        if (!parentComponent) {
          windowStoreDebug('[DependencySpread] Parent component not found:', parentComponentId);
          return;
        }

        // Get ALL descendant dependency IDs recursively (not just direct children)
        const allDependencyIds = get().getAllDescendantDependencyIds(parentComponentId);
        if (allDependencyIds.length === 0) {
          windowStoreDebug('[DependencySpread] No dependencies to spread for:', parentComponent.name);
          return;
        }

        windowStoreDebug(`[DependencySpread] Spreading ${allDependencyIds.length} total dependencies (including nested) for ${parentComponent.name}`);

        // Minimal margin between components
        const margin = 10;
        const componentGap = 15; // Gap between dependencies to prevent overlap

        // Parent rectangle - dependencies cannot overlap this
        const parentRect = new Rectangle(
          parentComponent.position.x - componentGap,
          parentComponent.position.y - componentGap,
          parentComponent.size.width + componentGap * 2,
          parentComponent.size.height + componentGap * 2
        );

        // Get parent component center for reference
        const parentCenterX = parentComponent.position.x + parentComponent.size.width / 2;
        const parentCenterY = parentComponent.position.y + parentComponent.size.height / 2;

        // Get dependency components (all descendants)
        const depComponents = allDependencyIds
          .map((depId: string) => components.find(c => c.id === depId))
          .filter(Boolean);

        // Track assigned positions to avoid overlap between dependencies
        const assignedRects: Rectangle[] = [];

        /**
         * Check if a position overlaps with parent or any already-assigned dependency
         */
        const hasOverlap = (x: number, y: number, width: number, height: number): boolean => {
          const testRect = new Rectangle(x, y, width, height);

          // Check overlap with parent
          if (testRect.intersects(parentRect)) {
            return true;
          }

          // Check overlap with already-assigned dependencies
          for (const assignedRect of assignedRects) {
            if (testRect.intersects(assignedRect)) {
              return true;
            }
          }

          return false;
        };

        /**
         * Find a non-overlapping position for a dependency component.
         * Tries the target position first, then searches nearby.
         */
        const findNonOverlappingPosition = (
          targetX: number,
          targetY: number,
          width: number,
          height: number
        ): { x: number; y: number } => {
          // Clamp initial target to screen bounds
          let x = Math.max(margin, Math.min(screenWidth - width - margin, targetX));
          let y = Math.max(margin, Math.min(screenHeight - height - margin, targetY));

          // If target position has no overlap, use it
          if (!hasOverlap(x, y, width, height)) {
            return { x: Math.floor(x), y: Math.floor(y) };
          }

          // Search in expanding spiral around target
          const maxSearchRadius = Math.max(screenWidth, screenHeight);
          const step = 20;

          for (let radius = step; radius < maxSearchRadius; radius += step) {
            // Try positions around the target in a circular pattern
            for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 8) {
              const testX = targetX + Math.cos(angle) * radius;
              const testY = targetY + Math.sin(angle) * radius;

              // Clamp to screen bounds
              const clampedX = Math.max(margin, Math.min(screenWidth - width - margin, testX));
              const clampedY = Math.max(margin, Math.min(screenHeight - height - margin, testY));

              if (!hasOverlap(clampedX, clampedY, width, height)) {
                return { x: Math.floor(clampedX), y: Math.floor(clampedY) };
              }
            }
          }

          // Fallback: grid scan for any free position
          for (let scanY = margin; scanY + height <= screenHeight - margin; scanY += step) {
            for (let scanX = margin; scanX + width <= screenWidth - margin; scanX += step) {
              if (!hasOverlap(scanX, scanY, width, height)) {
                return { x: Math.floor(scanX), y: Math.floor(scanY) };
              }
            }
          }

          // Last resort: return clamped target (may overlap but better than nothing)
          console.warn(`[DependencySpread] Could not find non-overlapping position, using clamped target`);
          return { x: Math.floor(x), y: Math.floor(y) };
        };

        // Define strategic anchor positions across the screen
        const anchorZones = [
          // Corners first - maximum spread
          { x: 0.08, y: 0.08 },   // Top-left
          { x: 0.92, y: 0.08 },   // Top-right
          { x: 0.08, y: 0.85 },   // Bottom-left
          { x: 0.92, y: 0.85 },   // Bottom-right
          // Edge centers
          { x: 0.50, y: 0.08 },   // Top-center
          { x: 0.08, y: 0.50 },   // Left-center
          { x: 0.92, y: 0.50 },   // Right-center
          { x: 0.50, y: 0.85 },   // Bottom-center
          // Quadrant centers
          { x: 0.28, y: 0.28 },   // Top-left quadrant
          { x: 0.72, y: 0.28 },   // Top-right quadrant
          { x: 0.28, y: 0.72 },   // Bottom-left quadrant
          { x: 0.72, y: 0.72 },   // Bottom-right quadrant
          // Inner spread points
          { x: 0.38, y: 0.50 },
          { x: 0.62, y: 0.50 },
          { x: 0.50, y: 0.38 },
          { x: 0.50, y: 0.62 },
        ];

        // Sort anchor zones by distance from parent (farthest first for better spread)
        const sortedZones = [...anchorZones].sort((a, b) => {
          const distA = Math.hypot(a.x * screenWidth - parentCenterX, a.y * screenHeight - parentCenterY);
          const distB = Math.hypot(b.x * screenWidth - parentCenterX, b.y * screenHeight - parentCenterY);
          return distB - distA; // Farthest first
        });

        // Calculate positions for each dependency
        const spreadPositions: { id: string; position: { x: number; y: number } }[] = [];

        depComponents.forEach((depComponent: any, index: number) => {
          if (!depComponent) return;

          let targetX: number, targetY: number;

          if (index < sortedZones.length) {
            // Use strategic anchor positions
            const zone = sortedZones[index];
            targetX = zone.x * screenWidth - depComponent.size.width / 2;
            targetY = zone.y * screenHeight - depComponent.size.height / 2;
          } else {
            // Fallback to distributed grid for many components
            const overflowIndex = index - sortedZones.length;
            const gridCols = Math.ceil(Math.sqrt(depComponents.length - sortedZones.length + 1));
            const gridRows = Math.ceil((depComponents.length - sortedZones.length) / gridCols);
            const col = overflowIndex % gridCols;
            const row = Math.floor(overflowIndex / gridCols);

            const cellWidth = (screenWidth - margin * 2) / Math.max(1, gridCols);
            const cellHeight = (screenHeight - margin * 2) / Math.max(1, gridRows);

            targetX = margin + col * cellWidth + cellWidth / 2 - depComponent.size.width / 2;
            targetY = margin + row * cellHeight + cellHeight / 2 - depComponent.size.height / 2;
          }

          // Find a non-overlapping position starting from the target
          const finalPosition = findNonOverlappingPosition(
            targetX,
            targetY,
            depComponent.size.width,
            depComponent.size.height
          );

          // Record this position to avoid future overlaps
          assignedRects.push(new Rectangle(
            finalPosition.x - componentGap / 2,
            finalPosition.y - componentGap / 2,
            depComponent.size.width + componentGap,
            depComponent.size.height + componentGap
          ));

          spreadPositions.push({
            id: depComponent.id,
            position: finalPosition
          });

          windowStoreDebug(`[DependencySpread] ${depComponent.name} -> (${finalPosition.x}, ${finalPosition.y})`);
        });

        // Update components with spread state
        const updatedComponents = components.map(comp => {
          if (comp.id === parentComponentId) {
            return {
              ...comp,
              isDependenciesSpread: true
            };
          }

          const spreadPos = spreadPositions.find(p => p.id === comp.id);
          if (spreadPos) {
            // Store original position before spreading
            const originalPos = comp.originalDependencyPosition || { ...comp.position };

            // Remove old position from quadtree
            const oldRect = toOccupancyRect(comp.position.x, comp.position.y, comp.size.width, comp.size.height);
            quadtree.remove(oldRect);

            // Insert new position into quadtree (minimized deps occupy no space)
            if (!comp.isMinimized) {
              const newRect = toOccupancyRect(spreadPos.position.x, spreadPos.position.y, comp.size.width, comp.size.height);
              quadtree.insert(newRect);
            }

            windowStoreDebug(`[DependencySpread] Moving ${comp.name} from (${comp.position.x}, ${comp.position.y}) to (${spreadPos.position.x}, ${spreadPos.position.y})`);

            return {
              ...comp,
              originalDependencyPosition: originalPos,
              position: spreadPos.position,
              isSpreadDependency: true,
              isAnimatingPosition: true,
              animationStartTime: Date.now()
            };
          }

          return comp;
        });

        set((state) => ({
          componentsByWindow: {
            ...state.componentsByWindow,
            [windowId]: updatedComponents
          }
        }));

        // Clear animation flags after transition (match DraggableComponent's 1.5s animation + buffer)
        setTimeout(() => {
          set((state) => {
            const currentComponents = getWindowComponents(state, windowId);
            return {
              componentsByWindow: {
                ...state.componentsByWindow,
                [windowId]: currentComponents.map(comp => ({
                  ...comp,
                  isAnimatingPosition: false,
                  animationStartTime: undefined
                }))
              }
            };
          });
        }, 1700);

        // Persist to backend
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents.map(c => {
            const { isAnimatingPosition, animationStartTime, ...cleanComp } = c;
            return cleanComp;
          })
        });

        windowStoreDebug('[DependencySpread] Spread complete');
      },

      /**
       * Collapse dependency components back to their original positions near the parent.
       * This affects ALL nested dependencies (children, grandchildren, etc.)
       */
      collapseDependencies: (parentComponentId: string) => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const components = getWindowComponents(state, windowId);
        const quadtree = getWindowQuadtree(state, windowId);
        if (!quadtree) {
          windowStoreDebug('[DependencySpread] Quadtree not available');
          return;
        }

        const parentComponent = components.find(c => c.id === parentComponentId);
        if (!parentComponent) {
          windowStoreDebug('[DependencySpread] Parent component not found:', parentComponentId);
          return;
        }

        // Get ALL descendant dependency IDs recursively (not just direct children)
        const allDependencyIds = get().getAllDescendantDependencyIds(parentComponentId);
        if (allDependencyIds.length === 0) {
          windowStoreDebug('[DependencySpread] No dependencies to collapse for:', parentComponent.name);
          return;
        }

        windowStoreDebug(`[DependencySpread] Collapsing ${allDependencyIds.length} total dependencies (including nested) for ${parentComponent.name}`);

        // Update components with collapsed state
        const updatedComponents = components.map(comp => {
          if (comp.id === parentComponentId) {
            return {
              ...comp,
              isDependenciesSpread: false
            };
          }

          if (allDependencyIds.includes(comp.id) && comp.originalDependencyPosition) {
            const originalPos = comp.originalDependencyPosition;

            // Remove current position from quadtree
            const oldRect = toOccupancyRect(comp.position.x, comp.position.y, comp.size.width, comp.size.height);
            quadtree.remove(oldRect);

            // Insert original position back into quadtree (minimized deps occupy no space)
            if (!comp.isMinimized) {
              const newRect = toOccupancyRect(originalPos.x, originalPos.y, comp.size.width, comp.size.height);
              quadtree.insert(newRect);
            }

            windowStoreDebug(`[DependencySpread] Returning ${comp.name} from (${comp.position.x}, ${comp.position.y}) to original (${originalPos.x}, ${originalPos.y})`);

            return {
              ...comp,
              position: originalPos,
              isSpreadDependency: false,
              isAnimatingPosition: true,
              animationStartTime: Date.now(),
              // Keep originalDependencyPosition for next spread
            };
          }

          return comp;
        });

        set((state) => ({
          componentsByWindow: {
            ...state.componentsByWindow,
            [windowId]: updatedComponents
          }
        }));

        // Clear animation flags after transition (match DraggableComponent's 1.5s animation + buffer)
        setTimeout(() => {
          set((state) => {
            const currentComponents = getWindowComponents(state, windowId);
            return {
              componentsByWindow: {
                ...state.componentsByWindow,
                [windowId]: currentComponents.map(comp => ({
                  ...comp,
                  isAnimatingPosition: false,
                  animationStartTime: undefined
                }))
              }
            };
          });
        }, 1700);

        // Persist to backend
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents.map(c => {
            const { isAnimatingPosition, animationStartTime, ...cleanComp } = c;
            return cleanComp;
          })
        });

        windowStoreDebug('[DependencySpread] Collapse complete');
      },

      // ============================================
      // COMPONENT EDIT HISTORY (UNDO/REDO) - Per-window
      // ============================================

      /**
       * Push a new edit to the current window's history.
       * This truncates any "future" edits if we're not at the end of history.
       */
      pushComponentEdit: (edit: ComponentEditRecord, options: { targetWindowId?: string } = {}) => {
        const windowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(get());
        if (options.targetWindowId && !getWindowComponents(get(), windowId).some((component) => component.id === edit.componentId)) return;

        set((state) => {
          const currentHistory = getWindowEditHistory(state, windowId);

          // Truncate any "future" history if we've undone some edits
          const truncatedHistory = currentHistory.history.slice(0, currentHistory.currentIndex + 1);

          // Add the new edit
          const newHistory = [...truncatedHistory, edit];

          // Enforce maximum history length
          const maxHistory = state.maxEditHistory || 50;
          const finalHistory = newHistory.length > maxHistory
            ? newHistory.slice(newHistory.length - maxHistory)
            : newHistory;

          return {
            editHistoryByWindow: {
              ...state.editHistoryByWindow,
              [windowId]: {
                history: finalHistory,
                currentIndex: finalHistory.length - 1
              }
            }
          };
        });
      },

      /**
       * Undo the last edit in the current window - returns the previous state to apply.
       * Returns null if no more undos available.
       */
      undoComponentEdit: (): { componentId: string; previousState: any; editRequest: string } | null => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const historyState = getWindowEditHistory(state, windowId);

        if (historyState.currentIndex < 0) {
          windowStoreDebug('[EditHistory] No more undos available');
          return null;
        }

        const currentEdit = historyState.history[historyState.currentIndex];

        // Move the index back
        set((state) => ({
          editHistoryByWindow: {
            ...state.editHistoryByWindow,
            [windowId]: {
              ...getWindowEditHistory(state, windowId),
              currentIndex: historyState.currentIndex - 1
            }
          }
        }));

        windowStoreDebug(`[EditHistory] Undo: reverting ${currentEdit.componentName} to previous state`);

        return {
          componentId: currentEdit.componentId,
          previousState: currentEdit.previousState,
          editRequest: currentEdit.editRequest
        };
      },

      /**
       * Redo the next edit in the current window - returns the new state to apply.
       * Returns null if no more redos available.
       */
      redoComponentEdit: (): { componentId: string; newState: any; editRequest: string } | null => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const historyState = getWindowEditHistory(state, windowId);

        const nextIndex = historyState.currentIndex + 1;

        if (nextIndex >= historyState.history.length) {
          windowStoreDebug('[EditHistory] No more redos available');
          return null;
        }

        const nextEdit = historyState.history[nextIndex];

        // Move the index forward
        set((state) => ({
          editHistoryByWindow: {
            ...state.editHistoryByWindow,
            [windowId]: {
              ...getWindowEditHistory(state, windowId),
              currentIndex: nextIndex
            }
          }
        }));

        windowStoreDebug(`[EditHistory] Redo: applying edit to ${nextEdit.componentName}`);

        return {
          componentId: nextEdit.componentId,
          newState: nextEdit.newState,
          editRequest: nextEdit.editRequest
        };
      },

      /**
       * Clear current window's edit history
       */
      clearComponentEditHistory: () => {
        const windowId = getCurrentWindowIdOrDefault(get());
        set((state) => ({
          editHistoryByWindow: {
            ...state.editHistoryByWindow,
            [windowId]: { history: [], currentIndex: -1 }
          }
        }));
      },

      /**
       * Get edit history for a specific component (for BE editor context)
       */
      getComponentEditHistory: (componentId: string) => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const historyState = getWindowEditHistory(state, windowId);
        return historyState.history.filter(edit => edit.componentId === componentId);
      },

      /**
       * Get recent edit history for BE context (last N edits across all components)
       */
      getRecentEditHistory: (count: number = 10) => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const historyState = getWindowEditHistory(state, windowId);
        return historyState.history.slice(-count).map(edit => ({
          componentId: edit.componentId,
          componentName: edit.componentName,
          editRequest: edit.editRequest,
          summary: edit.changes.map(c => c.description).join('; '),
          timestamp: edit.timestamp
        }));
      },

      /**
       * Check if undo is available in current window
       */
      canUndo: () => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const historyState = getWindowEditHistory(state, windowId);
        return historyState.currentIndex >= 0;
      },

      /**
       * Check if redo is available in current window
       */
      canRedo: () => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const historyState = getWindowEditHistory(state, windowId);
        return historyState.currentIndex < historyState.history.length - 1;
      },

      /**
       * Apply a component edit (update the component props in state)
       */
      applyComponentEdit: (componentId: string, newProps: any, options: { targetWindowId?: string } = {}) => {
        if (options.targetWindowId && !getWindowComponents(get(), options.targetWindowId).some((component) => component.id === componentId || component.name === componentId)) return;
        const resolveTargetComponent = (state: AppState) => {
          const preferredWindowId = options.targetWindowId ?? getCurrentWindowIdOrDefault(state);
          const preferredComponents = getWindowComponents(state, preferredWindowId);
          const preferredMatch = preferredComponents.find(
            (component) => component.id === componentId || component.name === componentId
          );
          if (preferredMatch) {
            return { windowId: preferredWindowId, targetId: preferredMatch.id };
          }

          if (options.targetWindowId) return null;
          for (const [windowId, components] of Object.entries(state.componentsByWindow)) {
            const match = (components || []).find(
              (component) => component.id === componentId || component.name === componentId
            );
            if (match) return { windowId, targetId: match.id };
          }
          return null;
        };

        set((state) => {
          const target = resolveTargetComponent(state);
          if (!target) {
            console.error("[applyComponentEdit] Component not found:", componentId);
            return state;
          }

          const currentComponents = getWindowComponents(state, target.windowId);
          const updatedComponents = currentComponents.map(comp => {
            if (comp.id === target.targetId) {
              return {
                ...comp,
                props: {
                  ...comp.props,
                  // A new document edit patch keeps earlier edits by folding
                  // the previous latest patch into documentEditBasePatch.
                  ...foldDocumentEditPatchProps(comp.props, newProps)
                },
                updatedDate: Date.now()
              };
            }
            return comp;
          });

          return {
            componentsByWindow: {
              ...state.componentsByWindow,
              [target.windowId]: updatedComponents
            }
          };
        });

        // Component edits can target a surface owned by another Electron
        // renderer. Publish the full durable window map before persistence so
        // that renderer immediately receives the edited props (including an
        // empty-state transition after a note was deleted) and cannot later
        // persist its stale component payload over this edit.
        broadcastComponentsByWindowSync(get().componentsByWindow);

        // Persist to backend
        const allComponents = get().getAllComponents();
        const newUser = { ...get().userSession };
        newUser.preferences.componentsMap = allComponents;
        get().setUserSession(newUser);
        updatePreferences({
          ...newUser.preferences,
          componentsMap: allComponents
        });
      },

      // ============================================
      // MOBILE LAYOUT MANAGEMENT
      // ============================================

      /**
       * Set mobile layout mode.
       * When switching modes, the quadtree is rebuilt to use the appropriate positions.
       */
      setMobileLayout: (isMobile: boolean) => {
        const currentMode = get().isMobileLayout;
        if (currentMode === isMobile) return; // No change needed

        windowStoreDebug(`[MobileLayout] Switching layout mode: ${currentMode ? 'mobile' : 'desktop'} → ${isMobile ? 'mobile' : 'desktop'}`);

        set({ isMobileLayout: isMobile });

        // Rebuild quadtrees with new effective positions
        get().rebuildAllQuadtrees();

        // Persist the layout mode preference
        const newUser = { ...get().userSession };
        newUser.preferences.lastLayoutMode = isMobile ? 'mobile' : 'desktop';
        updatePreferences({
          ...newUser.preferences,
          lastLayoutMode: isMobile ? 'mobile' : 'desktop'
        });
      },

      /**
       * Get effective position for a component based on current layout mode.
       * Returns mobilePosition if in mobile mode and available, otherwise regular position.
       */
      getEffectivePosition: (component: componentsMapType): { x: number; y: number } => {
        const { isMobileLayout } = get();
        if (isMobileLayout && component.mobilePosition) {
          return { ...component.mobilePosition };
        }
        return { ...component.position };
      },

      /**
       * Get effective size for a component based on current layout mode.
       * Returns mobileSize if in mobile mode and available, otherwise regular size.
       */
      getEffectiveSize: (component: componentsMapType): { width: number; height: number } => {
        const { isMobileLayout } = get();
        if (isMobileLayout && component.mobileSize) {
          return { ...component.mobileSize };
        }
        return { ...component.size };
      },

      /**
       * Get components for current window with effective positions/sizes applied.
       * This returns components with position/size reflecting the current layout mode.
       * Useful for rendering where you want the "active" position/size.
       */
      getComponentsForCurrentLayout: (): componentsMapType[] => {
        const state = get();
        const windowId = getCurrentWindowIdOrDefault(state);
        const components = getWindowComponents(state, windowId);
        const { isMobileLayout } = state;

        if (!isMobileLayout) {
          // Desktop mode - use regular position/size (no transformation needed)
          return components;
        }

        // Mobile mode - transform to use mobile position/size when available
        return components.map(comp => ({
          ...comp,
          // Override position with mobile position if available
          position: comp.mobilePosition || comp.position,
          // Override size with mobile size if available
          size: comp.mobileSize || comp.size
        }));
      }
    }),
    {
      name: "nv-storage",
      version: 1,
      storage: createJSONStorage(() => isolatedBrowserStorage),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (typeof window !== "undefined") {
            purgeLegacyBrowserSecrets(window.localStorage);
            if (!(window as any).electronAPI?.isElectron) {
              window.localStorage.removeItem("workos_session_id");
            }
          }
        };
      },
      // storage: createJSONStorage(() => createIndexedDBStorage('nv-storage', 'app-store')),
      partialize: (state) => {
        // Exclude transient/runtime state from persistence:
        // - liveAudioData: transient audio data
        // - wakeWordPrompt/isWakeWordListening: transient voice UI state
        // - currentWindowId: set at runtime per-window
        // - quadtreesByWindow: rebuilt at runtime for each window
        // - screenDimensionsByWindow: rebuilt at runtime per window
        // - activeCall: transient runtime telephony state
        // - joinedNativeCall: transient native room locator
        const {
          liveAudioData,
          wakeWordPrompt,
          isWakeWordListening,
          currentWindowId,
          workspaceScenesByWindow,
          documentViewerByWindow,
          quadtreesByWindow,
          screenDimensionsByWindow,
          activeCall,
          joinedNativeCall,
          aiActivityByComponent,
          heldComponentHeights,
          gestureArmedComponentId,
          workspaceSaveIssue,
          ...persistedState
        } = state;
        return sanitizePersistedAppState(persistedState);
      },
      // Version 1 purges sensitive fields written by the legacy broad
      // persistence policy while retaining harmless device/UI preferences.
      migrate: (persistedState) => sanitizePersistedAppState(
        persistedState as Record<string, unknown>,
      ),
      merge: (persistedState, currentState) => {
        if (!persistedState || typeof persistedState !== "object") {
          return currentState;
        }
        const { demoState, ...persistedWithoutDemoState } = sanitizePersistedAppState(
          persistedState as Record<string, unknown>,
        );
        return {
          ...currentState,
          ...persistedWithoutDemoState,
        };
      },
    }
  )
);
// Set up cross-window synchronization listener
if (broadcastChannel) {
  broadcastChannel.onmessage = (event) => {
    // The public landing demo owns a synthetic, in-memory workspace. Ignore
    // inbound messages as well as outbound broadcasts while it is mounted;
    // otherwise another Smart Manager window can replace the demo components
    // between pointer-down and click, making controls such as Minimize appear
    // inert and risking signed-in workspace data leaking into the public demo.
    if (isLandingDemoStoreIsolationActive()) return;

    if (!event.data || typeof event.data !== "object") return;
    const { type, value } = event.data;

    switch (type) {
      case 'QUADTREE_OVERLAY_TOGGLE':
        // Update local state without broadcasting (to avoid infinite loop)
        useAppStore.setState({ showQuadTreeOverlay: value });
        break;
      case 'COMPONENTS_BY_WINDOW_SYNC': {
        // A detached surface is not the workspace: it neither sends nor takes
        // the workspace's window list.
        if (isDetachedWorkspaceSurfaceActive()) break;
        const componentsByWindow = event.data.componentsByWindow;
        if (!componentsByWindow || typeof componentsByWindow !== 'object' || Array.isArray(componentsByWindow)
          || !Object.values(componentsByWindow).every(components => Array.isArray(components) && Array.from(components).every(isWorkspaceSyncComponent))) break;

        let accepted = false;
        useAppStore.setState((state) => {
          const identity = workspaceSyncIdentity(state);
          if (!identity || event.data.schemaVersion !== 1 || event.data.ownerId !== identity.ownerId
            || event.data.companyId !== identity.companyId) return state;
          const merged = mergeSyncedComponentsByWindow(
            componentsByWindow,
            state.componentsByWindow,
            state.heldComponentHeights,
          );
          accepted = true;
          return {
            ...merged,
            ...updateSessionComponentsMap(state, flattenComponentsByWindow(merged.componentsByWindow)),
          };
        });
        if (accepted) useAppStore.getState().rebuildAllQuadtrees();
        break;
      }
      case VERIFIED_CHECKOUT_SYNC_MESSAGE_TYPE:
        // Never trust the renderer-to-renderer message as entitlement proof.
        // Each recipient asks the authenticated server to verify the exact
        // checkout session before changing its local access decision.
        void reverifyBroadcastCheckoutSession(event.data);
        break;
      case PROVIDER_CONNECTION_SYNC_MESSAGE_TYPE:
        // Another window connected a provider. The hint is not proof: refresh
        // this window's session from the server, and the awaited-action
        // watcher here sees the connection like a local one.
        void providerConnectionSync.handleMessage(event.data).catch((error) => {
          console.warn("[Integrations] Cross-window connection refresh failed", error);
        });
        break;
      // Add more sync types here as needed
    }
  };
  useAppStore.subscribe((state, previous) => {
    if (state.userSession === previous.userSession) return;
    providerConnectionSync.handleSessionChange(
      state.isSigningOut ? null : state.userSession,
      previous.isSigningOut ? null : previous.userSession,
    );
  });
}

// Debug subscriber to track componentsByWindow changes
if (typeof window !== 'undefined') {
  let prevWindowIds: string[] = [];

  useAppStore.subscribe((state) => {
    const currentWindowIds = Object.keys(state.componentsByWindow);

    // Check for added windows
    const addedWindows = currentWindowIds.filter(id => !prevWindowIds.includes(id));
    // Check for removed windows
    const removedWindows = prevWindowIds.filter(id => !currentWindowIds.includes(id));

    if (addedWindows.length > 0) {
      windowStoreDebug(`[Store] 🟢 Windows added to componentsByWindow:`, addedWindows);
    }
    if (removedWindows.length > 0) {
      windowStoreDebug(`[Store] 🔴 Windows removed from componentsByWindow:`, removedWindows);
      console.trace('[Store] Stack trace for window removal');
    }

    prevWindowIds = currentWindowIds;
  });
}

export {
  useAppStore,
  Rectangle,
};
